<template>
  <div class="invoice-mobile" :style="mobileShellStyle">
    <header class="invoice-mobile-header">
      <button
        type="button"
        class="invoice-mobile-header__icon-btn"
        :aria-label="$t('back')"
        @click="goBack"
      >
        <v-icon size="24" color="#37474F">mdi-chevron-left</v-icon>
      </button>

      <h1 class="invoice-mobile-header__title">
        {{ $t(invoice.type) }}
      </h1>

      <div class="invoice-mobile-header__actions">
        <button
          type="button"
          class="invoice-mobile-header__icon-btn"
          :class="{ 'invoice-mobile-header__icon-btn--active': cashierMode }"
          :aria-label="$t('cash')"
          @click="cashierMode = !cashierMode"
        >
          <v-icon size="22" :color="cashierMode ? '#1565C0' : '#37474F'">
            mdi-cash-register
          </v-icon>
        </button>

        <button
          type="button"
          class="invoice-mobile-header__icon-btn"
          :aria-label="$t('barcode')"
          @click="onBarcode"
        >
          <v-icon size="22" color="#37474F">mdi-barcode-scan</v-icon>
        </button>

        <button
          type="button"
          class="invoice-mobile-header__icon-btn"
          :aria-label="$t('refresh')"
          @click="onRefresh"
        >
          <v-icon size="22" color="#37474F">mdi-refresh</v-icon>
        </button>

        <button
          type="button"
          class="invoice-mobile-header__icon-btn"
          :aria-label="$t('more')"
          @click="moreSheet = true"
        >
          <v-icon size="22" color="#37474F">mdi-dots-vertical</v-icon>
        </button>
      </div>
    </header>

    <div
      class="invoice-mobile__body"
      :class="{ 'invoice-mobile__body--shop': cashierMode }"
    >
      <!-- keep defaults in sync (same as desktop set-init) -->
      <div class="invoice-mobile-init" aria-hidden="true">
        <autocomplete
          v-model="invoice.entrepot"
          title="entrepot"
          type="entrepot"
          :set-init="!invoice.id"
          :value="invoice.entrepot"
        />
        <autocomplete
          v-model="invoice.branch"
          :title="$t('branch')"
          type="branch"
          :set-init="!invoice.id"
          :value="invoice.branch"
        />
      </div>

      <div class="invoice-summary-row" dir="rtl">
        <div class="invoice-summary-card invoice-summary-card--customer">
          <button
            type="button"
            class="invoice-summary-card__main"
            @click="openEntityPicker('person', customerLabel)"
          >
            <span class="invoice-summary-card__label">{{ customerLabel }}</span>
            <span
              class="invoice-summary-card__value"
              :class="{ 'is-placeholder': !customerName }"
            >
              {{ customerName || $t("selectCustomer") }}
            </span>
          </button>
          <button
            type="button"
            class="invoice-summary-card__create"
            :aria-label="$t('newCustomer')"
            @click.stop="openNewCustomer"
          >
            <v-icon size="24" color="#78909C">mdi-account-plus-outline</v-icon>
          </button>
        </div>

        <button
          type="button"
          class="invoice-summary-card invoice-summary-card--meta"
          @click="metaSheet = true"
        >
          <div class="invoice-summary-card__top">
            <span class="invoice-summary-card__label">{{
              $t("invoice-mobile-invoice-meta-label")
            }}</span>
            <v-icon size="16" color="#90A4AE">mdi-pencil-outline</v-icon>
          </div>
          <div v-if="invoiceMetaText" class="invoice-summary-card__value">
            {{ invoiceMetaText }}
          </div>
        </button>
      </div>

      <template v-if="!cashierMode">
        <div class="invoice-product-add-row" dir="rtl">
          <button
            v-if="canCreateProduct"
            type="button"
            class="invoice-summary-add-btn"
            :aria-label="$t('product')"
            @click="openNewProductDialog"
          >
            <v-icon size="22" color="#1565C0">mdi-package-variant-plus</v-icon>
          </button>
          <button type="button" class="invoice-add-row-btn" @click="openProductPicker()">
            <v-icon size="18" color="#607D8B">mdi-plus</v-icon>
            <span class="invoice-add-row-btn__text">{{ $t("addRow") }}</span>
          </button>
        </div>

        <div class="invoice-product-rows" dir="rtl">
          <article
            v-for="(item, index) in filledInvoiceItems"
            :key="item._uid || item.product.id || index"
            class="invoice-product-card"
          >
            <button
              type="button"
              class="invoice-product-card__top"
              @click="openRowDetail(item)"
            >
              <div class="invoice-product-card__media">
                <img
                  v-if="productImageUrl(item)"
                  :src="productImageUrl(item)"
                  alt=""
                  class="invoice-product-card__img"
                />
                <v-icon v-else size="28" color="#B0BEC5">mdi-image-outline</v-icon>
              </div>
              <div class="invoice-product-card__info">
                <div class="invoice-product-card__title">
                  {{ item.product.name }}
                </div>
                <div
                  class="invoice-product-card__line invoice-product-card__line--accent"
                >
                  {{ $t("price_with_tax") }}:
                  <span>{{ formatMoney(rowFeeWithTax(item)) }}</span>
                </div>
                <div class="invoice-product-card__line">
                  {{ $t("discount") }}:
                  <span>{{ formatMoney(item.discount || 0) }}</span>
                </div>
                <div class="invoice-product-card__line">
                  {{ $t("invoice-mobile-row-total-label") }}
                  <span>{{ formatMoney(rowTotal(item)) }}</span>
                </div>
              </div>
            </button>
            <div class="invoice-product-card__footer">
              <div class="invoice-product-card__tools">
                <button
                  type="button"
                  class="invoice-product-card__tool"
                  :aria-label="$t('delete')"
                  @click="onDeleteRow(item)"
                >
                  <v-icon size="22" color="#78909C">mdi-delete-outline</v-icon>
                </button>
                <button
                  type="button"
                  class="invoice-product-card__tool"
                  :aria-label="$t('edit')"
                  @click="openRowDetail(item)"
                >
                  <v-icon size="22" color="#78909C">mdi-pencil-outline</v-icon>
                </button>
              </div>
              <div class="invoice-product-card__count">
                <button
                  type="button"
                  class="invoice-product-card__count-btn"
                  @click="bumpCount(item, 1)"
                >
                  <v-icon size="18" color="#607D8B">mdi-plus</v-icon>
                </button>
                <button
                  type="button"
                  class="invoice-product-card__count-value"
                  @click="openRowNumpad('count', item)"
                >
                  {{ item.count }}
                  <small>{{ rowUnitLabel(item) }}</small>
                </button>

                <button
                  type="button"
                  class="invoice-product-card__count-btn"
                  @click="bumpCount(item, -1)"
                >
                  <v-icon size="18" color="#607D8B">mdi-minus</v-icon>
                </button>
              </div>
            </div>
          </article>
        </div>
      </template>

      <div v-else class="invoice-shop" dir="rtl">
        <div class="invoice-shop__toolbar">
          <div class="invoice-shop__search">
            <v-icon size="20" color="#90A4AE" class="invoice-shop__search-icon">
              mdi-magnify
            </v-icon>
            <input
              v-model="shopSearch"
              type="search"
              class="invoice-shop__search-input"
              :placeholder="$t('searchProduct')"
              autocomplete="off"
            />
            <button
              v-if="shopSearch"
              type="button"
              class="invoice-shop__search-clear"
              :aria-label="$t('clear')"
              @click="shopSearch = ''"
            >
              <v-icon size="16" color="#90A4AE">mdi-close</v-icon>
            </button>
          </div>
          <button
            type="button"
            class="invoice-shop__sort-btn"
            :class="{ 'invoice-shop__sort-btn--active': shopSort !== 'default' }"
            :aria-label="shopSortLabel"
            @click="cycleShopSort"
          >
            <v-icon size="22" :color="shopSort !== 'default' ? '#1565C0' : '#546E7A'">
              {{
                shopSort === "priceAsc"
                  ? "mdi-sort-ascending"
                  : shopSort === "priceDesc"
                  ? "mdi-sort-descending"
                  : "mdi-swap-vertical"
              }}
            </v-icon>
          </button>
        </div>

        <div class="invoice-shop__cats">
          <button
            type="button"
            class="invoice-shop__cat"
            :class="{ 'invoice-shop__cat--active': !shopCategoryId }"
            @click="selectShopCategory(null)"
          >
            {{ $t("all") }}
          </button>
          <button
            v-for="cat in shopCategories"
            :key="'shop-cat-' + cat.id"
            type="button"
            class="invoice-shop__cat"
            :class="{ 'invoice-shop__cat--active': shopCategoryId === cat.id }"
            @click="selectShopCategory(cat.id)"
          >
            {{ cat.name }}
          </button>
        </div>

        <div ref="shopListBody" class="invoice-shop__list" @scroll.passive="onShopScroll">
          <div v-if="shopLoading && !shopProducts.length" class="invoice-shop__state">
            <v-progress-circular indeterminate color="primary" size="28" />
          </div>
          <div
            v-else-if="!shopLoading && !shopProductsDisplay.length"
            class="invoice-shop__state"
          >
            {{ $t("no-shortcut-product") }}
          </div>
          <article
            v-for="product in shopProductsDisplay"
            :key="'shop-p-' + (product.id || product.product_id)"
            class="invoice-product-card"
          >
            <button
              type="button"
              class="invoice-product-card__top"
              @click="openShopProductDetail(product)"
            >
              <div class="invoice-product-card__media">
                <img
                  v-if="shopProductImage(product)"
                  :src="shopProductImage(product)"
                  alt=""
                  class="invoice-product-card__img"
                />
                <v-icon v-else size="28" color="#B0BEC5">mdi-image-outline</v-icon>
              </div>
              <div class="invoice-product-card__info">
                <div class="invoice-product-card__title">
                  {{ product.name }}
                </div>
                <div
                  class="invoice-product-card__line invoice-product-card__line--accent"
                >
                  {{ $t("price_with_tax") }}:
                  <span>{{ formatMoney(shopProductFeeWithTax(product)) }}</span>
                </div>
                <div class="invoice-product-card__line">
                  {{ $t("discount") }}:
                  <span>{{ formatMoney(shopProductDiscount(product)) }}</span>
                </div>
                <div class="invoice-product-card__line">
                  {{ $t("invoice-mobile-row-total-label") }}
                  <span>{{ formatMoney(shopProductTotal(product)) }}</span>
                </div>
              </div>
            </button>
            <div class="invoice-product-card__footer">
              <div class="invoice-product-card__tools">
                <button
                  v-if="shopProductCount(product)"
                  type="button"
                  class="invoice-product-card__tool"
                  :aria-label="$t('delete')"
                  @click="removeShopProduct(product)"
                >
                  <v-icon size="22" color="#78909C">mdi-delete-outline</v-icon>
                </button>
                <button
                  type="button"
                  class="invoice-product-card__tool"
                  :aria-label="$t('edit')"
                  @click="openShopProductDetail(product)"
                >
                  <v-icon size="22" color="#78909C">mdi-pencil-outline</v-icon>
                </button>
              </div>
              <div class="invoice-product-card__count">
                <button
                  type="button"
                  class="invoice-product-card__count-btn"
                  @click="bumpShopProduct(product, 1)"
                >
                  <v-icon size="18" color="#607D8B">mdi-plus</v-icon>
                </button>
                <button
                  type="button"
                  class="invoice-product-card__count-value"
                  @click="openShopProductNumpad(product)"
                >
                  {{ shopProductCount(product) || 0 }}
                  <small>{{ shopProductUnitLabel(product) }}</small>
                </button>
                <button
                  type="button"
                  class="invoice-product-card__count-btn"
                  @click="bumpShopProduct(product, -1)"
                >
                  <v-icon size="18" color="#607D8B">mdi-minus</v-icon>
                </button>
              </div>
            </div>
          </article>
          <div v-if="shopLoading && shopProducts.length" class="invoice-shop__more">
            <v-progress-circular indeterminate color="primary" size="22" />
          </div>
        </div>
      </div>
    </div>

    <footer class="invoice-mobile-footer" dir="rtl">
      <div class="invoice-mobile-footer__summary">
        <span>{{ $t("invoice-mobile-footer-items", { count: footerItemCount }) }}</span>
        <span class="invoice-mobile-footer__sep">|</span>
        <span class="invoice-mobile-footer__amount">{{ footerTotalText }}</span>
      </div>

      <button
        type="button"
        class="invoice-mobile-footer__btn invoice-mobile-footer__btn--solid"
        @click="onReceivePayment"
      >
        {{ $t("receiveCash") }}
      </button>

      <button
        type="button"
        class="invoice-mobile-footer__btn invoice-mobile-footer__btn--outline"
        :disabled="invoice.isLoading"
        @click="openReviewSheet"
      >
        {{ $t("invoice-mobile-review-and-submit") }}
      </button>

      <button
        type="button"
        class="invoice-mobile-footer__more"
        :aria-label="$t('more')"
        @click="extrasSheet = true"
      >
        <v-icon size="22" color="#455A64">mdi-dots-vertical</v-icon>
      </button>
    </footer>

    <v-bottom-sheet v-model="reviewSheet">
      <v-card class="invoice-edit-sheet invoice-review-sheet">
        <div
          class="invoice-more-sheet__handle"
          role="button"
          :aria-label="$t('close')"
          @touchstart.passive="onSheetHandleTouchStart"
          @touchend="onSheetHandleTouchEnd"
        />
        <div class="invoice-edit-sheet__header" dir="rtl">
          <span class="invoice-edit-sheet__header-spacer" />
          <div class="invoice-edit-sheet__title invoice-edit-sheet__title--inline">
            {{ $t("invoice-mobile-review-and-submit") }}
          </div>
          <button
            type="button"
            class="invoice-mobile-header__icon-btn"
            :aria-label="$t('back')"
            @click="reviewSheet = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="invoice-edit-sheet__body invoice-review-sheet__body" dir="rtl">
          <div class="invoice-review-sheet__hint">
            {{ $t("invoice-mobile-review-hint") }}
          </div>

          <div class="invoice-review-sheet__card">
            <div
              v-for="(row, index) in reviewSummaryRows"
              :key="'review-row-' + index"
              class="invoice-review-sheet__row"
              :class="{ 'is-emphasis': row.emphasis, 'is-section': row.section }"
            >
              <span>{{ row.label }}</span>
              <strong>{{ row.value }}</strong>
            </div>
          </div>

          <button
            v-if="filledInvoiceItems.length"
            type="button"
            class="invoice-review-sheet__list-btn"
            @click="openReviewProductListSheet"
          >
            <v-icon size="20" color="#5A6B85">mdi-package-variant</v-icon>
            <span>{{ $t("invoiceItems") }} ({{ filledInvoiceItems.length }})</span>
            <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
          </button>

          <template v-if="reviewCompositeRows.length">
            <button
              type="button"
              class="invoice-review-sheet__list-btn"
              @click="openReviewCompositeListSheet"
            >
              <v-icon size="20" color="#5A6B85">mdi-format-list-bulleted</v-icon>
              <span>{{ reviewCompositeListTitle }}</span>
              <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>

            <div class="invoice-review-sheet__card invoice-review-sheet__card--soft">
              <div class="invoice-review-sheet__card-title">
                {{
                  invoice.compositeType === "pay"
                    ? $t("compositePayment")
                    : $t("compositeReceipt")
                }}
              </div>
              <div
                v-for="(row, index) in reviewCompositeRows"
                :key="'review-composite-' + index"
                class="invoice-review-sheet__row"
                :class="{ 'is-emphasis': row.emphasis }"
              >
                <span>{{ row.label }}</span>
                <strong>{{ row.value }}</strong>
              </div>
            </div>
          </template>
        </div>
        <div class="invoice-edit-sheet__footer">
          <button
            type="button"
            class="invoice-review-sheet__pay-btn"
            :disabled="invoice.isLoading"
            @click="confirmReviewSubmit"
          >
            <v-progress-circular
              v-if="invoice.isLoading"
              indeterminate
              size="18"
              width="2"
              color="#00664D"
              class="ml-2"
            />
            <span>{{ reviewSubmitLabel }}</span>
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="reviewCompositeListSheet">
      <v-card class="invoice-edit-sheet">
        <div
          class="invoice-more-sheet__handle"
          role="button"
          :aria-label="$t('close')"
          @touchstart.passive="onSheetHandleTouchStart"
          @touchend="onSheetHandleTouchEnd"
        />
        <div class="invoice-edit-sheet__header" dir="rtl">
          <span class="invoice-edit-sheet__header-spacer" />
          <div class="invoice-edit-sheet__title invoice-edit-sheet__title--inline">
            {{ reviewCompositeListTitle }}
          </div>
          <button
            type="button"
            class="invoice-mobile-header__icon-btn"
            :aria-label="$t('back')"
            @click="backToReviewSheet"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div class="invoice-edit-sheet__body invoice-review-list-sheet__body" dir="rtl">
          <div
            v-if="!reviewActiveCompositeItems.length"
            class="invoice-review-list-sheet__empty"
          >
            {{ $t("invoice-mobile-composite-list-empty") }}
          </div>
          <div
            v-for="(item, index) in reviewActiveCompositeItems"
            :key="'review-comp-item-' + index + '-' + (item.id || item.amount)"
            class="invoice-review-list-sheet__card"
          >
            <div class="invoice-review-list-sheet__head">
              <span class="invoice-review-list-sheet__index">{{ index + 1 }}</span>
              <span class="invoice-review-list-sheet__type">
                {{ $t(item.compositeType) }}
              </span>
            </div>
            <div class="invoice-review-sheet__row">
              <span>{{ $t("amount") }}</span>
              <strong>{{ formatReviewCompositeAmount(item) }}</strong>
            </div>
            <div class="invoice-review-sheet__row">
              <span>{{ $t("date") }}</span>
              <strong>{{ reviewCompositeItemDate(item) }}</strong>
            </div>
            <div
              v-for="(line, lineIndex) in reviewCompositeItemOtherLines(item)"
              :key="'review-comp-line-' + index + '-' + lineIndex"
              class="invoice-review-sheet__row"
            >
              <span>{{ line.label }}</span>
              <strong>{{ line.value }}</strong>
            </div>
          </div>
        </div>
        <div class="invoice-edit-sheet__footer">
          <v-btn
            color="primary"
            block
            depressed
            class="invoice-edit-sheet__submit"
            height="48"
            @click="backToReviewSheet"
          >
            {{ $t("back") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="reviewProductListSheet">
      <v-card class="invoice-edit-sheet">
        <div
          class="invoice-more-sheet__handle"
          role="button"
          :aria-label="$t('close')"
          @touchstart.passive="onSheetHandleTouchStart"
          @touchend="onSheetHandleTouchEnd"
        />
        <div class="invoice-edit-sheet__header" dir="rtl">
          <span class="invoice-edit-sheet__header-spacer" />
          <div class="invoice-edit-sheet__title invoice-edit-sheet__title--inline">
            {{ $t("invoiceItems") }}
          </div>
          <button
            type="button"
            class="invoice-mobile-header__icon-btn"
            :aria-label="$t('back')"
            @click="backToReviewSheet"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div class="invoice-edit-sheet__body invoice-review-list-sheet__body" dir="rtl">
          <div
            v-for="(item, index) in filledInvoiceItems"
            :key="'review-all-product-' + (item._uid || index)"
            class="invoice-review-product-list__row"
          >
            <div class="invoice-review-product-list__media">
              <img
                v-if="productImageUrl(item)"
                :src="productImageUrl(item)"
                alt=""
                class="invoice-review-product-list__img"
              />
              <v-icon v-else size="20" color="#B0BEC5">mdi-image-outline</v-icon>
            </div>
            <div class="invoice-review-product-list__info">
              <strong>{{ item.product && item.product.name }}</strong>
              <span>{{ item.count || 0 }} {{ rowUnitLabel(item) }}</span>
            </div>
            <div class="invoice-review-product-list__amount">
              {{ formatMoney(rowTotal(item)) }}
            </div>
          </div>
        </div>
        <div class="invoice-edit-sheet__footer">
          <v-btn
            color="primary"
            block
            depressed
            class="invoice-edit-sheet__submit"
            height="48"
            @click="backToReviewSheet"
          >
            {{ $t("back") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="reviewValidationSheet">
      <v-card class="invoice-edit-sheet invoice-review-validation-sheet">
        <div
          class="invoice-more-sheet__handle"
          role="button"
          :aria-label="$t('close')"
          @touchstart.passive="onSheetHandleTouchStart"
          @touchend="onSheetHandleTouchEnd"
        />
        <div class="invoice-edit-sheet__header" dir="rtl">
          <span class="invoice-edit-sheet__header-spacer" />
          <div class="invoice-edit-sheet__title invoice-edit-sheet__title--inline">
            {{ $t("invoice-mobile-incomplete-items-title") }}
          </div>
          <button
            type="button"
            class="invoice-mobile-header__icon-btn"
            :aria-label="$t('back')"
            @click="dismissReviewValidation"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div
          class="invoice-edit-sheet__body invoice-review-validation-sheet__body"
          dir="rtl"
        >
          <div class="invoice-review-validation-sheet__hint">
            {{ $t("invoice-mobile-incomplete-items-hint") }}
          </div>
          <button
            v-for="item in reviewValidationErrors"
            :key="item.key"
            type="button"
            class="invoice-review-validation-sheet__item"
            @click="fixReviewValidationItem(item)"
          >
            <v-icon
              class="invoice-review-validation-sheet__icon"
              size="22"
              color="#C62828"
            >
              mdi-alert-circle-outline
            </v-icon>
            <span class="invoice-review-validation-sheet__texts">
              <span class="invoice-review-validation-sheet__item-title">
                {{ item.title }}
              </span>
              <span class="invoice-review-validation-sheet__item-sub">
                {{ item.description }}
              </span>
            </span>
            <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
          </button>
        </div>
        <div class="invoice-edit-sheet__footer">
          <v-btn
            color="primary"
            block
            depressed
            class="invoice-edit-sheet__submit"
            height="48"
            @click="dismissReviewValidation"
          >
            {{ $t("invoice-mobile-got-it") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <div class="invoice-mobile-composite-host" aria-hidden="true">
      <composite
        ref="invoiceComposite"
        :compact="true"
        :total-tax="invoice.totalTax"
        :discount="invoice.discount"
        :total="invoice.total"
        :credit="invoice.credit"
        :account="invoice.account"
        :option-dialog-type="invoice.optionDialogType"
        :prop-type="invoice.compositeType"
        :prop-date="invoice.date"
        :is-invoice-created="invoice.isInvoiceCreated"
        :edit-composite-table-data="invoice.editCompositeTableData"
        :currency="invoice.currency"
        :factor="true"
        :pos="hasActivePosDevice"
        :return-pos-info="invoice.returnPosInfo"
        :is-pos-successfull="invoice.isPosSuccessfull"
        @update="onCompositeUpdate"
        @closeOptionsDialog="invoice.optionDialogType = null"
        @totalAmount="onCompositeTotalAmount"
        @invoiceRemain="onCompositeInvoiceRemain"
        @sendToPosProcess="onCompositeSendToPos"
        @successAdded="invoice.isPosSuccessfull = false"
      />
    </div>

    <v-bottom-sheet v-model="metaSheet">
      <v-card class="invoice-edit-sheet">
        <div
          class="invoice-more-sheet__handle"
          role="button"
          :aria-label="$t('close')"
          @touchstart.passive="onSheetHandleTouchStart"
          @touchend="onSheetHandleTouchEnd"
        />
        <div class="invoice-edit-sheet__title">
          {{ $t("invoice-mobile-invoice-meta-label") }}
        </div>
        <div class="invoice-edit-sheet__body invoice-meta-sheet__body">
          <div class="invoice-meta-sheet__nav-list" dir="rtl">
            <button
              type="button"
              class="invoice-more-sheet__item"
              @click="openMetaSubSheet('metaLocationSheet')"
            >
              <v-icon class="invoice-more-sheet__icon" size="22" color="#5A6B85">
                mdi-warehouse
              </v-icon>
              <span class="invoice-more-sheet__texts">
                <span class="invoice-more-sheet__item-title">{{
                  $t("invoice-mobile-entrepot-and-branch")
                }}</span>
                <span class="invoice-more-sheet__item-sub">
                  {{ metaLocationSubtitle }}
                </span>
              </span>
            </button>

            <button
              type="button"
              class="invoice-more-sheet__item"
              @click="openMetaSubSheet('metaDateSheet')"
            >
              <v-icon class="invoice-more-sheet__icon" size="22" color="#5A6B85">
                mdi-calendar
              </v-icon>
              <span class="invoice-more-sheet__texts">
                <span class="invoice-more-sheet__item-title">{{
                  $t("invoice-date")
                }}</span>
                <span class="invoice-more-sheet__item-sub">
                  {{ metaDateSubtitle }}
                </span>
              </span>
            </button>

            <button
              v-if="showOfficialNumber"
              type="button"
              class="invoice-more-sheet__item"
              @click="openMetaSubSheet('metaOfficialSheet')"
            >
              <v-icon class="invoice-more-sheet__icon" size="22" color="#5A6B85">
                mdi-numeric
              </v-icon>
              <span class="invoice-more-sheet__texts">
                <span class="invoice-more-sheet__item-title">
                  {{ $t("officialNumber") }}
                </span>
                <span class="invoice-more-sheet__item-sub">
                  {{ metaOfficialSubtitle }}
                </span>
              </span>
            </button>

            <button
              v-if="showTaxInvoiceFields"
              type="button"
              class="invoice-more-sheet__item"
              @click="openMetaSubSheet('metaTaxSheet')"
            >
              <v-icon class="invoice-more-sheet__icon" size="22" color="#5A6B85">
                mdi-file-document-outline
              </v-icon>
              <span class="invoice-more-sheet__texts">
                <span class="invoice-more-sheet__item-title">
                  {{ $t("invoice-mobile-tax-invoice-title") }}
                </span>
                <span class="invoice-more-sheet__item-sub">
                  {{ $t("invoice-mobile-tax-invoice-subtitle") }}
                </span>
              </span>
            </button>

            <button
              type="button"
              class="invoice-more-sheet__item"
              @click="openMetaDocumentsSheet"
            >
              <v-icon class="invoice-more-sheet__icon" size="22" color="#5A6B85">
                mdi-paperclip
              </v-icon>
              <span class="invoice-more-sheet__texts">
                <span class="invoice-more-sheet__item-title">
                  {{ $t("attachments") }} {{ $t("documents") }}
                </span>
                <span class="invoice-more-sheet__item-sub">
                  {{ documentsSubtitle }}
                </span>
              </span>
            </button>

            <button
              v-if="showCommissionEntry"
              type="button"
              class="invoice-more-sheet__item"
              @click="openMetaCommissionSheet"
            >
              <v-icon class="invoice-more-sheet__icon" size="22" color="#5A6B85">
                mdi-cash-multiple
              </v-icon>
              <span class="invoice-more-sheet__texts">
                <span class="invoice-more-sheet__item-title">
                  {{ $t("commission") }}
                </span>
                <span class="invoice-more-sheet__item-sub">
                  {{ commissionSubtitle }}
                </span>
              </span>
            </button>
          </div>
        </div>
        <div class="invoice-edit-sheet__footer">
          <v-btn
            color="primary"
            block
            depressed
            class="invoice-edit-sheet__submit"
            height="48"
            @click="metaSheet = false"
          >
            {{ $t("submit") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="metaLocationSheet">
      <v-card class="invoice-edit-sheet">
        <div
          class="invoice-more-sheet__handle"
          role="button"
          :aria-label="$t('close')"
          @touchstart.passive="onSheetHandleTouchStart"
          @touchend="onSheetHandleTouchEnd"
        />
        <div class="invoice-edit-sheet__header" dir="rtl">
          <span class="invoice-edit-sheet__header-spacer" />
          <div class="invoice-edit-sheet__title invoice-edit-sheet__title--inline">
            {{ $t("invoice-mobile-entrepot-and-branch") }}
          </div>
          <button
            type="button"
            class="invoice-mobile-header__icon-btn"
            :aria-label="$t('back')"
            @click="backToMetaSheet"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div class="invoice-edit-sheet__body" dir="rtl">
          <div class="invoice-edit-sheet__fields">
            <div class="invoice-edit-field-wrap">
              <div class="invoice-edit-field-wrap__label">{{ $t("entrepot") }}</div>
              <button
                type="button"
                class="invoice-edit-picker-field"
                @click="openEntityPicker('entrepot', $t('entrepot'))"
              >
                <span class="invoice-edit-picker-field__text">
                  {{ entityLabel(invoice.entrepot) || "—" }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
            </div>
            <div class="invoice-edit-field-wrap">
              <div class="invoice-edit-field-wrap__label">{{ $t("branch") }}</div>
              <button
                type="button"
                class="invoice-edit-picker-field"
                @click="openEntityPicker('branch', $t('branch'))"
              >
                <span class="invoice-edit-picker-field__text">
                  {{ entityLabel(invoice.branch) || "—" }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
            </div>
          </div>
        </div>
        <div class="invoice-edit-sheet__footer">
          <v-btn
            color="primary"
            block
            depressed
            class="invoice-edit-sheet__submit"
            height="48"
            @click="backToMetaSheet"
          >
            {{ $t("Save") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="metaDateSheet">
      <v-card class="invoice-edit-sheet">
        <div
          class="invoice-more-sheet__handle"
          role="button"
          :aria-label="$t('close')"
          @touchstart.passive="onSheetHandleTouchStart"
          @touchend="onSheetHandleTouchEnd"
        />
        <div class="invoice-edit-sheet__header" dir="rtl">
          <span class="invoice-edit-sheet__header-spacer" />
          <div class="invoice-edit-sheet__title invoice-edit-sheet__title--inline">
            {{ $t("invoice-date") }}
          </div>
          <button
            type="button"
            class="invoice-mobile-header__icon-btn"
            :aria-label="$t('back')"
            @click="backToMetaSheet"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div class="invoice-edit-sheet__body" dir="rtl">
          <div class="invoice-mobile-date">
            <div class="invoice-mobile-date__selected">
              <span class="invoice-mobile-date__label">{{ $t("date") }}</span>
              <strong class="invoice-mobile-date__value">
                {{ invoice.date || "—" }}
              </strong>
            </div>
            <date-picker
              v-model="invoice.date"
              inline
              simple
              auto-submit
              :locale="$store.state.date_locale"
              :format="$store.state.date_format"
              :min="$store.state.startDate"
              :max="$store.state.endDate"
              class="invoice-mobile-date__picker"
              color="#7B8BA3"
            />
          </div>
        </div>
        <div class="invoice-edit-sheet__footer">
          <v-btn
            color="primary"
            block
            depressed
            class="invoice-edit-sheet__submit"
            height="48"
            @click="backToMetaSheet"
          >
            {{ $t("Save") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="metaOfficialSheet">
      <v-card class="invoice-edit-sheet">
        <div
          class="invoice-more-sheet__handle"
          role="button"
          :aria-label="$t('close')"
          @touchstart.passive="onSheetHandleTouchStart"
          @touchend="onSheetHandleTouchEnd"
        />
        <div class="invoice-edit-sheet__header" dir="rtl">
          <span class="invoice-edit-sheet__header-spacer" />
          <div class="invoice-edit-sheet__title invoice-edit-sheet__title--inline">
            {{ $t("officialNumber") }}
          </div>
          <button
            type="button"
            class="invoice-mobile-header__icon-btn"
            :aria-label="$t('back')"
            @click="backToMetaSheet"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div class="invoice-edit-sheet__body" dir="rtl">
          <div class="invoice-edit-field-wrap">
            <div class="invoice-edit-field-wrap__label">
              {{ $t("officialNumber") }}
            </div>
            <v-text-field
              v-model="invoice.officialNumber"
              solo
              flat
              hide-details
              clearable
              dense
              height="40"
              class="invoice-edit-field"
              @blur="onOfficialNumberBlur"
            />
          </div>
        </div>
        <div class="invoice-edit-sheet__footer">
          <v-btn
            color="primary"
            block
            depressed
            class="invoice-edit-sheet__submit"
            height="48"
            @click="backToMetaSheet"
          >
            {{ $t("Save") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="metaTaxSheet">
      <v-card class="invoice-edit-sheet">
        <div
          class="invoice-more-sheet__handle"
          role="button"
          :aria-label="$t('close')"
          @touchstart.passive="onSheetHandleTouchStart"
          @touchend="onSheetHandleTouchEnd"
        />
        <div class="invoice-edit-sheet__header" dir="rtl">
          <span class="invoice-edit-sheet__header-spacer" />
          <div class="invoice-edit-sheet__title invoice-edit-sheet__title--inline">
            {{ $t("invoice-mobile-tax-invoice-title") }}
          </div>
          <button
            type="button"
            class="invoice-mobile-header__icon-btn"
            :aria-label="$t('back')"
            @click="backToMetaSheet"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div class="invoice-edit-sheet__body" dir="rtl">
          <div class="invoice-edit-sheet__fields">
            <div class="invoice-edit-field-wrap">
              <div class="invoice-edit-field-wrap__label">
                {{ $t("invoiceType") }}
              </div>
              <v-select
                v-model="invoice.pattern"
                :items="invoiceTypeSelectItems"
                solo
                flat
                hide-details
                dense
                height="40"
                item-value="value"
                item-text="text"
                append-icon="mdi-chevron-down"
                class="invoice-edit-field"
              />
            </div>
            <div class="invoice-edit-field-wrap">
              <div class="invoice-edit-field-wrap__label">
                {{ $t("invoicePattern") }}
              </div>
              <v-select
                :value="String(invoice.invoicePattern)"
                :items="invoicePatternItems"
                solo
                flat
                hide-details
                dense
                height="40"
                item-value="value"
                item-text="text"
                append-icon="mdi-chevron-down"
                class="invoice-edit-field"
                @change="onInvoicePatternChange"
              />
            </div>
            <div
              v-if="String(invoice.invoicePattern) === '4'"
              class="invoice-edit-field-wrap"
            >
              <div class="invoice-edit-field-wrap__label">
                {{ $t("contractNumber") }}
              </div>
              <v-text-field
                v-model="invoice.contractNumber"
                solo
                flat
                hide-details
                clearable
                dense
                height="40"
                class="invoice-edit-field"
              />
            </div>
          </div>
        </div>
        <div class="invoice-edit-sheet__footer">
          <v-btn
            color="primary"
            block
            depressed
            class="invoice-edit-sheet__submit"
            height="48"
            @click="backToMetaSheet"
          >
            {{ $t("Save") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="documentsSheet" eager>
      <v-card class="invoice-edit-sheet invoice-documents-sheet">
        <div
          class="invoice-more-sheet__handle"
          role="button"
          :aria-label="$t('close')"
          @touchstart.passive="onSheetHandleTouchStart"
          @touchend="onSheetHandleTouchEnd"
        />
        <div class="invoice-edit-sheet__header" dir="rtl">
          <span class="invoice-edit-sheet__header-spacer" />
          <div class="invoice-edit-sheet__title invoice-edit-sheet__title--inline">
            {{ $t("attachments") }} {{ $t("documents") }}
          </div>
          <button
            type="button"
            class="invoice-mobile-header__icon-btn"
            :aria-label="$t('back')"
            @click="backToMetaSheet"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div class="invoice-edit-sheet__body invoice-documents-sheet__body" dir="rtl">
          <div class="invoice-documents-sheet__hint">
            {{ $t("invoice-mobile-documents-hint") }}
          </div>
          <form-attachment-doc
            ref="refFilePicker"
            v-model="invoice.attachments"
            type-prop="invoice"
            :show-header="false"
            :val="invoice.id && invoice.attachments ? invoice.attachments : null"
          />
        </div>
        <div class="invoice-edit-sheet__footer">
          <v-btn
            color="primary"
            block
            depressed
            class="invoice-edit-sheet__submit"
            height="48"
            @click="backToMetaSheet"
          >
            {{ $t("Save") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="commissionSheet">
      <v-card class="invoice-edit-sheet">
        <div
          class="invoice-more-sheet__handle"
          role="button"
          :aria-label="$t('close')"
          @touchstart.passive="onSheetHandleTouchStart"
          @touchend="onSheetHandleTouchEnd"
        />
        <div class="invoice-edit-sheet__header" dir="rtl">
          <span class="invoice-edit-sheet__header-spacer" />
          <div class="invoice-edit-sheet__title invoice-edit-sheet__title--inline">
            {{ $t("commission") }}
          </div>
          <button
            type="button"
            class="invoice-mobile-header__icon-btn"
            :aria-label="$t('back')"
            @click="backToMetaSheet"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div class="invoice-edit-sheet__body" dir="rtl">
          <div class="invoice-edit-sheet__fields">
            <div class="invoice-edit-field-wrap">
              <div class="invoice-edit-field-wrap__label">{{ $t("visitor") }}</div>
              <button
                type="button"
                class="invoice-edit-picker-field"
                @click="
                  openEntityPicker(
                    'person',
                    $t('visitor'),
                    'seller',
                    JSON.stringify({ type: 'visitor' })
                  )
                "
              >
                <span class="invoice-edit-picker-field__text">
                  {{ entityLabel(invoice.seller) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
            </div>

            <div class="invoice-edit-field-wrap">
              <div class="invoice-edit-field-wrap__label">
                {{ $t("commissionAmount") }}
              </div>
              <money
                v-model="invoice.commissionAmount"
                :dense="true"
                :solo="true"
                :flat="true"
                :outlined="false"
                :clearable="true"
                :hide-details="true"
                value-when-is-empty=""
                :options="invoiceMoneyOptions"
                :disabled="!(invoice.total > 0)"
                class="invoice-row-detail__money"
                @input="onCommissionAmountInput"
              />
            </div>

            <div class="invoice-edit-field-wrap">
              <div class="invoice-edit-field-wrap__label">
                {{ $t("commissionPercent") }}
              </div>
              <button
                type="button"
                class="invoice-commission-percent"
                :disabled="!(invoice.total > 0)"
                @click="openGenericNumpad('commissionPercent')"
              >
                <span class="invoice-commission-percent__input">
                  {{ formatNumpadMoney(invoice.commissionPercent) }}
                </span>
                <span class="invoice-commission-percent__suffix">%</span>
              </button>
            </div>
          </div>
        </div>
        <div class="invoice-edit-sheet__footer">
          <v-btn
            color="primary"
            block
            depressed
            class="invoice-edit-sheet__submit"
            height="48"
            @click="backToMetaSheet"
          >
            {{ $t("Save") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-dialog
      v-model="entityPickerOpen"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
      content-class="invoice-entity-picker-dialog"
    >
      <v-card class="invoice-entity-picker">
        <header class="invoice-entity-picker__header" dir="ltr">
          <button
            type="button"
            class="invoice-entity-picker__icon-btn"
            :aria-label="$t('back')"
            @click="closeEntityPicker"
          >
            <v-icon size="26" color="#37474F">mdi-arrow-left</v-icon>
          </button>
          <h2 class="invoice-entity-picker__title">{{ entityPickerTitle }}</h2>
          <div class="invoice-entity-picker__actions">
            <button
              v-if="entityPickerType === 'person' && entityPickerAssign !== 'seller'"
              type="button"
              class="invoice-entity-picker__icon-btn"
              :aria-label="$t('newCustomer')"
              @click="openNewCustomerFromPicker"
            >
              <v-icon size="22" color="#607D8B">mdi-account-plus-outline</v-icon>
            </button>
            <button
              v-if="isProductPicker && canCreateProduct"
              type="button"
              class="invoice-entity-picker__icon-btn"
              :aria-label="$t('product')"
              @click="openNewProductDialog"
            >
              <v-icon size="22" color="#607D8B">mdi-plus</v-icon>
            </button>
          </div>
        </header>

        <div class="invoice-entity-picker__search" dir="rtl">
          <div class="invoice-entity-picker__search-box">
            <v-icon size="22" color="#78909C" class="invoice-entity-picker__search-icon">
              mdi-magnify
            </v-icon>
            <input
              v-model="entityPickerSearch"
              type="search"
              class="invoice-entity-picker__search-input"
              :placeholder="$t('search')"
              autocomplete="off"
              @keydown.enter.prevent
            />
            <button
              v-if="entityPickerSearch"
              type="button"
              class="invoice-entity-picker__search-clear"
              :aria-label="$t('clear')"
              @click="entityPickerSearch = ''"
            >
              <v-icon size="18" color="#78909C">mdi-close-circle</v-icon>
            </button>
          </div>
        </div>

        <div class="invoice-entity-picker__divider">
          <v-progress-linear
            v-if="entityPickerLoading"
            indeterminate
            color="#78909C"
            height="2"
            class="invoice-entity-picker__loader"
          />
        </div>

        <div
          ref="entityPickerBody"
          class="invoice-entity-picker__body"
          dir="rtl"
          @scroll.passive="onEntityPickerScroll"
        >
          <button
            v-for="(item, index) in entityPickerItems"
            :key="item.id != null ? item.id : index"
            type="button"
            class="invoice-entity-picker__item"
            :class="{
              'invoice-entity-picker__item--active': isEntityPickerSelected(item),
            }"
            @click="selectEntityPickerItem(item)"
          >
            <span class="invoice-entity-picker__index">{{ index + 1 }}</span>
            <span class="invoice-entity-picker__item-main">
              <span class="invoice-entity-picker__item-title">{{ item.name }}</span>
              <span
                v-if="entityPickerType === 'person' && (item.phone || item.mobile)"
                class="invoice-entity-picker__item-sub"
              >
                <template v-if="item.phone">
                  {{ $t("phone") }}: {{ item.phone }}
                </template>
                <template v-if="item.mobile">
                  <template v-if="item.phone"> · </template>
                  {{ $t("mobile") }}: {{ item.mobile }}
                </template>
              </span>
              <span
                v-if="isProductPicker && item.price != null"
                class="invoice-entity-picker__item-sub"
              >
                {{ formatMoney(item.price) }}
              </span>
            </span>
            <v-icon v-if="isEntityPickerSelected(item)" size="20" color="#607D8B">
              mdi-check
            </v-icon>
          </button>

          <div
            v-show="entityPickerHasMore"
            v-observe-visibility="{
              callback: onEntityPickerVisible,
              throttle: 200,
            }"
            class="invoice-entity-picker__more"
          >
            <v-progress-circular
              v-if="entityPickerLoading"
              :size="20"
              :width="2"
              color="#78909C"
              indeterminate
            />
          </div>

          <div
            v-if="!entityPickerLoading && !entityPickerItems.length"
            class="invoice-entity-picker__empty"
          >
            {{ $t("no-data") }}
          </div>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="newCustomerDialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <v-card>
        <v-col class="d-flex pt-2 pb-0">
          <v-btn icon @click="newCustomerDialog = false">
            <v-icon color="grey" large>mdi-close</v-icon>
          </v-btn>
          <p class="mx-auto font-weight-medium mt-2">
            {{ $t("newCustomer") }}
          </p>
        </v-col>
        <v-card-text class="pa-2">
          <customer
            v-if="newCustomerDialog"
            v-model="newCustomer"
            :return-object="true"
            @close="newCustomerDialog = false"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-bottom-sheet v-model="rowDetailSheet">
      <v-card v-if="rowDetailItem && rowDetailItem.product" class="invoice-row-detail">
        <div
          class="invoice-more-sheet__handle"
          role="button"
          :aria-label="$t('close')"
          @touchstart.passive="onSheetHandleTouchStart"
          @touchend="onSheetHandleTouchEnd"
        />
        <div class="invoice-row-detail__title">
          {{ $t("invoice-mobile-product-detail-title") }}
        </div>

        <div class="invoice-row-detail__product">
          <div class="invoice-row-detail__media">
            <img
              v-if="productImageUrl(rowDetailItem)"
              :src="productImageUrl(rowDetailItem)"
              alt=""
              class="invoice-row-detail__img"
            />
            <v-icon v-else size="28" color="#B0BEC5">mdi-image-outline</v-icon>
          </div>
          <div class="invoice-row-detail__name">
            {{ rowDetailItem.product.name }}
          </div>
          <button
            v-if="!cashierMode"
            type="button"
            class="invoice-row-detail__change"
            @click="changeRowProductFromDetail"
          >
            <v-icon size="18" color="#607D8B">mdi-swap-horizontal</v-icon>
          </button>
        </div>

        <div class="invoice-row-detail__body" dir="rtl">
          <div class="invoice-edit-field-wrap">
            <div class="invoice-edit-field-wrap__label">{{ $t("discount") }}</div>
            <div class="invoice-row-detail__discount-head">
              <div class="invoice-row-detail__segment">
                <button
                  type="button"
                  class="invoice-row-detail__segment-btn"
                  :class="{
                    'is-active': rowDetailDiscountMode === 'percent',
                  }"
                  @click="setRowDiscountMode('percent')"
                >
                  {{ $t("percent") }}
                </button>
                <button
                  type="button"
                  class="invoice-row-detail__segment-btn"
                  :class="{
                    'is-active': rowDetailDiscountMode === 'amount',
                  }"
                  @click="setRowDiscountMode('amount')"
                >
                  {{ currencyLabel || $t("rial") }}
                </button>
              </div>
            </div>
            <button
              v-if="rowDetailDiscountMode === 'percent'"
              type="button"
              class="invoice-row-detail__input-row"
              @click="openRowNumpad('discountPercent')"
            >
              <span class="invoice-row-detail__affix">%</span>
              <span class="invoice-row-detail__input invoice-row-detail__input--static">
                {{ rowDetailItem.discountPercent || 0 }}
              </span>
            </button>
            <button
              v-else
              type="button"
              class="invoice-row-detail__input-row"
              @click="openRowNumpad('discount')"
            >
              <span class="invoice-row-detail__input invoice-row-detail__input--static">
                {{ formatNumpadMoney(rowDetailItem.discount) }}
              </span>
              <span class="invoice-row-detail__affix">
                {{ currencyLabel || $t("rial") }}
              </span>
            </button>
          </div>

          <div class="invoice-edit-field-wrap">
            <div class="invoice-edit-field-wrap__label">{{ $t("tax") }}</div>
            <button
              type="button"
              class="invoice-row-detail__input-row"
              @click="openRowNumpad('tax')"
            >
              <span class="invoice-row-detail__affix">%</span>
              <span class="invoice-row-detail__input invoice-row-detail__input--static">
                {{ rowDetailTaxPercent || 0 }}
              </span>
            </button>
          </div>

          <div class="invoice-edit-field-wrap">
            <div class="invoice-edit-field-wrap__label">{{ $t("fee") }}</div>
            <button
              type="button"
              class="invoice-row-detail__input-row"
              @click="openRowNumpad('fee')"
            >
              <span class="invoice-row-detail__input invoice-row-detail__input--static">
                {{ formatNumpadMoney(rowDetailItem.fee) }}
              </span>
              <span class="invoice-row-detail__affix">
                {{ currencyLabel || $t("rial") }}
              </span>
            </button>
          </div>

          <div v-if="rowHasSerial(rowDetailItem)" class="invoice-edit-field-wrap">
            <div class="invoice-edit-field-wrap__label">{{ $t("serial") }}</div>
            <button
              type="button"
              class="invoice-edit-picker-field"
              @click="openRowSerialDialog"
            >
              <span class="invoice-edit-picker-field__text">
                {{ rowSerialLabel(rowDetailItem) }}
              </span>
              <v-icon size="20" color="#78909C">mdi-barcode</v-icon>
            </button>
          </div>

          <div class="invoice-edit-field-wrap">
            <div class="invoice-edit-field-wrap__label">
              {{ $t("count") }} / {{ $t("weight") }}
            </div>
            <button
              type="button"
              class="invoice-row-detail__input-row"
              @click="openRowNumpad('count')"
            >
              <span class="invoice-row-detail__input invoice-row-detail__input--static">
                {{ rowDetailItem.count || 0 }}
              </span>
              <span class="invoice-row-detail__affix">
                {{ rowUnitLabel(rowDetailItem) }}
              </span>
            </button>
            <div
              v-if="rowUnitConversionText(rowDetailItem)"
              class="invoice-row-detail__unit-hint"
            >
              {{ rowUnitConversionText(rowDetailItem) }}
            </div>
          </div>

          <div class="invoice-edit-field-wrap">
            <div class="invoice-edit-field-wrap__label">{{ $t("unit") }}</div>
            <v-menu
              v-model="rowUnitMenuOpen"
              offset-y
              left
              :disabled="unitItems(rowDetailItem).length <= 1"
            >
              <template v-slot:activator="{ on, attrs }">
                <button
                  type="button"
                  class="invoice-edit-picker-field"
                  v-bind="attrs"
                  v-on="on"
                >
                  <span class="invoice-edit-picker-field__text">
                    {{ rowUnitLabel(rowDetailItem) }}
                  </span>
                  <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
                </button>
              </template>
              <v-list dense>
                <v-list-item
                  v-for="(unit, uIndex) in unitItems(rowDetailItem)"
                  :key="unit.id || uIndex"
                  @click="selectRowUnit(unit)"
                >
                  <v-list-item-title>{{ unit.fame || unit.name }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>

          <div class="invoice-edit-field-wrap">
            <div class="invoice-edit-field-wrap__label">{{ $t("entrepot") }}</div>
            <button
              type="button"
              class="invoice-edit-picker-field"
              @click="openRowEntrepotPicker"
            >
              <span class="invoice-edit-picker-field__text">
                {{
                  entityLabel(rowDetailItem.article_entrepot) ||
                  entityLabel(invoice.entrepot) ||
                  "—"
                }}
              </span>
              <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
            </button>
          </div>

          <div class="invoice-edit-field-wrap">
            <div class="invoice-edit-field-wrap__label">
              {{ $t("description") }}
            </div>
            <textarea
              v-model="rowDetailItem.content"
              class="invoice-row-detail__textarea"
              rows="4"
              :placeholder="$t('description')"
            />
          </div>
        </div>

        <div class="invoice-row-detail__summary" dir="rtl">
          <span>
            {{ rowDetailItem.count || 0 }}
            {{ rowUnitLabel(rowDetailItem) }}
          </span>
          <span class="invoice-row-detail__summary-sep">|</span>
          <span>{{ formatMoney(rowDetailItem.fee) }}</span>
        </div>

        <div class="invoice-row-detail__footer">
          <v-btn
            color="primary"
            block
            depressed
            height="48"
            class="invoice-edit-sheet__submit"
            @click="saveRowDetail"
          >
            {{ $t("Save") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="rowSerialSheet" persistent>
      <v-card class="invoice-edit-sheet invoice-row-serial-sheet">
        <div
          class="invoice-more-sheet__handle"
          role="button"
          :aria-label="$t('close')"
          @touchstart.passive="onSheetHandleTouchStart"
          @touchend="onSheetHandleTouchEnd"
        />
        <div class="invoice-edit-sheet__header" dir="rtl">
          <span class="invoice-edit-sheet__header-spacer" />
          <div class="invoice-edit-sheet__title invoice-edit-sheet__title--inline">
            {{ $t("serial-management") }}
          </div>
          <button
            type="button"
            class="invoice-mobile-header__icon-btn"
            :aria-label="$t('back')"
            @click="closeRowSerialSheet"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="invoice-edit-sheet__body invoice-row-serial-sheet__body" dir="rtl">
          <div v-if="rowSerialIsInput" class="invoice-row-serial-sheet__add">
            <div class="invoice-row-serial-sheet__add-row">
              <input
                ref="rowSerialInput"
                v-model="rowSerialInput"
                type="text"
                class="invoice-row-serial-sheet__input"
                :placeholder="$t('enter-or-scan-serial')"
                @keydown.enter.prevent="addRowSerial"
              />
              <button
                type="button"
                class="invoice-row-serial-sheet__add-btn"
                @click="addRowSerial"
              >
                <v-icon size="18" color="#fff">mdi-plus</v-icon>
                <span>{{ $t("add") }}</span>
              </button>
            </div>
            <p v-if="rowSerialDuplicateHint" class="invoice-row-serial-sheet__hint">
              {{ $t("invoice-mobile-serial-duplicate") }}
            </p>
          </div>

          <div class="invoice-row-serial-sheet__toolbar">
            <div class="invoice-row-serial-sheet__search">
              <v-icon size="18" color="#90A4AE">mdi-magnify</v-icon>
              <input
                v-model="rowSerialSearch"
                type="search"
                class="invoice-row-serial-sheet__search-input"
                :placeholder="
                  rowSerialIsOutput
                    ? $t('invoice-mobile-serial-search-output-placeholder')
                    : $t('search-in-serials')
                "
              />
            </div>
            <div
              v-if="rowSerialIsOutput && rowSerialVisibleList.length"
              class="invoice-row-serial-sheet__tools"
            >
              <button
                type="button"
                class="invoice-row-serial-sheet__link-btn"
                @click="toggleSelectAllRowSerials"
              >
                {{
                  rowSerialAllVisibleSelected
                    ? $t("invoice-mobile-deselect-all")
                    : $t("invoice-mobile-select-all")
                }}
              </button>
              <span class="invoice-row-serial-sheet__count">
                {{ rowSerialEffectiveCount }} / {{ rowSerialAvailableCount }}
              </span>
              <button
                type="button"
                class="invoice-row-serial-sheet__link-btn"
                :class="{ 'is-muted': !rowSerialEffectiveCount }"
                :disabled="!rowSerialEffectiveCount"
                @click="clearRowSerialSelection"
              >
                {{ $t("clear") }}
              </button>
            </div>
          </div>

          <div class="invoice-row-serial-sheet__list">
            <div
              v-if="rowSerialLoading && !rowSerialVisibleList.length"
              class="invoice-row-serial-sheet__empty"
            >
              <v-progress-circular
                indeterminate
                size="26"
                width="2"
                color="#1976D2"
                class="mb-2"
              />
              <span>{{ $t("invoice-mobile-loading-serials") }}</span>
            </div>
            <div
              v-else-if="!rowSerialVisibleList.length"
              class="invoice-row-serial-sheet__empty"
            >
              <strong>{{ rowSerialEmptyTitle }}</strong>
              <span>{{ rowSerialEmptyHint }}</span>
            </div>

            <div
              v-for="(item, index) in rowSerialVisibleList"
              :key="item.uid || item.id || index"
              class="invoice-row-serial-sheet__item"
              :class="{
                'is-selected': rowSerialIsOutput && item.selected,
                'is-pickable': rowSerialIsOutput,
                'is-input-row': rowSerialIsInput,
              }"
              role="button"
              tabindex="0"
              @click="onRowSerialItemClick(item)"
              @keydown.enter.prevent="onRowSerialItemClick(item)"
              @keydown.space.prevent="onRowSerialItemClick(item)"
            >
              <template v-if="rowSerialIsOutput">
                <span class="invoice-row-serial-sheet__side" aria-hidden="true">
                  <span class="invoice-row-serial-sheet__mark">
                    <span class="invoice-row-serial-sheet__mark-dot" />
                  </span>
                </span>
                <span class="invoice-row-serial-sheet__item-main">
                  <strong>{{ item.value }}</strong>
                  <small v-if="item.created_at">{{ item.created_at }}</small>
                </span>
                <span class="invoice-row-serial-sheet__side" aria-hidden="true" />
              </template>
              <template v-else>
                <span class="invoice-row-serial-sheet__item-index">
                  {{ index + 1 }}
                </span>
                <span
                  class="invoice-row-serial-sheet__item-main invoice-row-serial-sheet__item-main--start"
                >
                  <strong>{{ item.value }}</strong>
                  <small v-if="item.created_at">{{ item.created_at }}</small>
                </span>
                <button
                  type="button"
                  class="invoice-row-serial-sheet__remove"
                  :aria-label="$t('remove')"
                  @click.stop="removeRowSerial(item)"
                >
                  <v-icon size="18" color="#E91E63">mdi-trash-can-outline</v-icon>
                </button>
              </template>
            </div>

            <button
              v-if="rowSerialIsOutput && rowSerialHasMore"
              type="button"
              class="invoice-row-serial-sheet__more"
              :disabled="rowSerialLoading"
              @click="loadMoreRowSerials"
            >
              <v-progress-circular
                v-if="rowSerialLoading"
                indeterminate
                size="16"
                width="2"
                color="#1976D2"
              />
              <span v-else>{{ $t("invoice-mobile-load-more") }}</span>
            </button>
          </div>
        </div>

        <div class="invoice-edit-sheet__footer">
          <v-btn
            color="primary"
            block
            depressed
            height="48"
            class="invoice-edit-sheet__submit"
            @click="confirmRowSerialSheet"
          >
            {{ rowSerialConfirmLabel }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-dialog
      v-model="barcodeDialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <v-card class="invoice-barcode-dialog">
        <v-col class="d-flex pt-2 pb-0 align-center">
          <v-btn icon @click="barcodeDialog = false">
            <v-icon color="grey" large>mdi-close</v-icon>
          </v-btn>
          <p class="mx-auto font-weight-medium mt-2 mb-2">
            {{ $t("barcode") }}
          </p>
          <div style="width: 48px" />
        </v-col>
        <v-card-text class="pa-2">
          <barcode-scanner v-if="barcodeDialog" @decode="onBarcodeDecode" />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="newProductDialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <v-card class="invoice-new-product-dialog">
        <v-col class="d-flex pt-2 pb-0">
          <v-btn icon @click="newProductDialog = false">
            <v-icon color="grey" large>mdi-close</v-icon>
          </v-btn>
          <p class="mx-auto font-weight-medium mt-2">
            {{ $t("product") }}
          </p>
        </v-col>
        <v-card-text class="pa-2 invoice-new-product-dialog__body">
          <product-form
            v-if="newProductDialog"
            v-model="newProduct"
            form-type="product"
            @addproduct="onCreatedProduct"
            @close="newProductDialog = false"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-bottom-sheet v-model="moreSheet" class="invoice-more-sheet">
      <v-card class="invoice-more-sheet__card">
        <div
          class="invoice-more-sheet__handle"
          role="button"
          :aria-label="$t('close')"
          @touchstart.passive="onSheetHandleTouchStart"
          @touchend="onSheetHandleTouchEnd"
        />
        <div class="invoice-more-sheet__title">{{ $t("more") }}</div>

        <button
          v-for="item in moreItems"
          :key="item.key"
          type="button"
          class="invoice-more-sheet__item"
          @click="runMoreAction(item)"
        >
          <v-icon class="invoice-more-sheet__icon" size="22" color="#5A6B85">
            {{ item.icon }}
          </v-icon>
          <span class="invoice-more-sheet__texts">
            <span class="invoice-more-sheet__item-title">{{ item.title }}</span>
            <span class="invoice-more-sheet__item-sub">{{ item.subtitle }}</span>
          </span>
        </button>

        <button
          type="button"
          class="invoice-more-sheet__item invoice-more-sheet__item--back"
          @click="goBack"
        >
          <v-icon class="invoice-more-sheet__icon" size="22" color="#5A6B85">
            mdi-arrow-left
          </v-icon>
          <span class="invoice-more-sheet__texts">
            <span class="invoice-more-sheet__item-title">{{ $t("back") }}</span>
            <span class="invoice-more-sheet__item-sub">
              {{ $t("invoice-mobile-back-to-page", { type: $t(invoice.type) }) }}
            </span>
          </span>
        </button>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="extrasSheet" class="invoice-more-sheet">
      <v-card class="invoice-more-sheet__card">
        <div
          class="invoice-more-sheet__handle"
          role="button"
          :aria-label="$t('close')"
          @touchstart.passive="onSheetHandleTouchStart"
          @touchend="onSheetHandleTouchEnd"
        />
        <div class="invoice-more-sheet__title">{{ $t("otherInputs") }}</div>

        <button
          v-for="item in extrasItems"
          :key="item.key"
          type="button"
          class="invoice-more-sheet__item"
          :disabled="item.disabled"
          @click="runExtrasAction(item)"
        >
          <v-icon class="invoice-more-sheet__icon" size="22" color="#5A6B85">
            {{ item.icon }}
          </v-icon>
          <span class="invoice-more-sheet__texts">
            <span class="invoice-more-sheet__item-title">{{ item.title }}</span>
            <span class="invoice-more-sheet__item-sub">{{ item.subtitle }}</span>
          </span>
        </button>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="otherInputsSheet">
      <v-card class="invoice-edit-sheet invoice-extras-form">
        <div
          class="invoice-more-sheet__handle"
          role="button"
          :aria-label="$t('close')"
          @touchstart.passive="onSheetHandleTouchStart"
          @touchend="onSheetHandleTouchEnd"
        />
        <div class="invoice-edit-sheet__header" dir="rtl">
          <span class="invoice-edit-sheet__header-spacer" />
          <div class="invoice-edit-sheet__title invoice-edit-sheet__title--inline">
            {{ $t("otherInputs") }}
          </div>
          <button
            type="button"
            class="invoice-mobile-header__icon-btn"
            :aria-label="$t('back')"
            @click="backToExtrasSheet"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="invoice-edit-sheet__body" dir="rtl">
          <div class="invoice-edit-sheet__fields">
            <div class="invoice-edit-field-wrap">
              <div class="invoice-edit-field-wrap__label">{{ $t("tax-calc") }}</div>
              <div class="invoice-tax-toggle">
                <button
                  v-for="item in invoice.taxOption"
                  :key="item.value"
                  type="button"
                  class="invoice-tax-toggle__btn"
                  :class="{
                    'is-active': invoice.selectedTaxOption == item.value,
                  }"
                  @click="onSelectTaxOption(item)"
                >
                  {{ item.label }}
                </button>
              </div>
            </div>

            <div class="invoice-edit-field-wrap">
              <div class="invoice-edit-field-wrap__label">
                {{ $t("documentType") }}
              </div>
              <v-select
                v-model="invoice.type"
                :items="invoice.typeItems || []"
                :disabled="documentTypeDisabled"
                solo
                flat
                hide-details
                dense
                height="40"
                item-value="value"
                item-text="text"
                item-disabled="disable"
                append-icon="mdi-chevron-down"
                class="invoice-edit-field"
                @input="onDocumentTypeChange"
              />
            </div>

            <div class="invoice-edit-field-wrap">
              <div class="invoice-edit-field-wrap__label">
                {{ $t("accountGiven") }}
              </div>
              <button
                type="button"
                class="invoice-edit-picker-field"
                @click="openEntityPicker('given', $t('accountGiven'))"
              >
                <span class="invoice-edit-picker-field__text">
                  {{ entityLabel(invoice.given) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
            </div>

            <div class="invoice-edit-field-wrap">
              <div class="invoice-edit-field-wrap__label">{{ $t("status") }}</div>
              <status
                v-model="invoice.status"
                :hideconfig="true"
                :type="invoice.type"
                :value="invoice.status"
                class="invoice-edit-field invoice-extras-status"
              />
            </div>

            <div class="invoice-edit-field-wrap">
              <div class="invoice-edit-field-wrap__label">{{ $t("tag") }}</div>
              <tag
                v-model="invoice.tag"
                :solo="true"
                :input="invoice.tag"
                :height="40"
                class="invoice-edit-field invoice-extras-tag"
              />
            </div>

            <div v-if="invoice.showCurrency" class="invoice-edit-field-wrap">
              <div class="invoice-edit-field-wrap__label">{{ $t("currency") }}</div>
              <button
                type="button"
                class="invoice-edit-picker-field"
                @click="openEntityPicker('currency', $t('currency'))"
              >
                <span class="invoice-edit-picker-field__text">
                  {{ entityLabel(invoice.currency) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
            </div>

            <div v-if="invoice.showCurrency" class="invoice-edit-field-wrap">
              <div class="invoice-edit-field-wrap__label">
                {{ $t("currency-rate") }}
              </div>
              <money
                v-model="invoice.currency_amount"
                :dense="true"
                :solo="true"
                :flat="true"
                :outlined="false"
                :clearable="true"
                :hide-details="true"
                value-when-is-empty=""
                :options="invoiceMoneyOptions"
                class="invoice-edit-field invoice-extras-money"
              />
            </div>

            <div class="invoice-edit-field-wrap">
              <div class="invoice-edit-field-wrap__label">{{ $t("dueDate") }}</div>
              <button
                type="button"
                class="invoice-edit-picker-field"
                @click="openDueDateSheet"
              >
                <span class="invoice-edit-picker-field__text">
                  {{ invoice.dueDate || "—" }}
                </span>
                <v-icon size="20" color="#78909C">mdi-calendar</v-icon>
              </button>
            </div>

            <div
              v-if="invoice.type === 'returnSell' || invoice.type === 'purchase'"
              class="invoice-edit-field-wrap"
            >
              <div class="invoice-edit-field-wrap__label">
                {{
                  $t(
                    invoice.type === "returnSell"
                      ? "refSellInvoice"
                      : "refPurchaseInvoice"
                  )
                }}
              </div>
              <button
                type="button"
                class="invoice-edit-picker-field"
                @click="
                  openEntityPicker(
                    'invoices',
                    $t(
                      invoice.type === 'returnSell'
                        ? 'refSellInvoice'
                        : 'refPurchaseInvoice'
                    ),
                    'refInvoice',
                    refInvoiceData
                  )
                "
              >
                <span class="invoice-edit-picker-field__text">
                  {{ entityLabel(invoice.refInvoice) || "—" }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
            </div>

            <div
              v-if="invoice.type === 'sell' || invoice.type === 'presale'"
              class="invoice-edit-field-wrap"
            >
              <div class="invoice-edit-field-wrap__label">
                {{ $t("priceTypeCalculate") }}
              </div>
              <v-select
                v-model="invoice.priceType"
                :items="invoice.priceTypeItems || []"
                solo
                flat
                hide-details
                dense
                height="40"
                item-value="value"
                item-text="text"
                item-disabled="disable"
                append-icon="mdi-chevron-down"
                class="invoice-edit-field"
              />
            </div>
          </div>
        </div>

        <div class="invoice-edit-sheet__footer">
          <v-btn
            color="primary"
            block
            depressed
            class="invoice-edit-sheet__submit"
            height="48"
            @click="backToExtrasSheet"
          >
            {{ $t("Save") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="dueDateSheet">
      <v-card class="invoice-edit-sheet">
        <div
          class="invoice-more-sheet__handle"
          role="button"
          :aria-label="$t('close')"
          @touchstart.passive="onSheetHandleTouchStart"
          @touchend="onSheetHandleTouchEnd"
        />
        <div class="invoice-edit-sheet__header" dir="rtl">
          <span class="invoice-edit-sheet__header-spacer" />
          <div class="invoice-edit-sheet__title invoice-edit-sheet__title--inline">
            {{ $t("dueDate") }}
          </div>
          <button
            type="button"
            class="invoice-mobile-header__icon-btn"
            :aria-label="$t('back')"
            @click="backToOtherInputsFromDueDate"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div class="invoice-edit-sheet__body" dir="rtl">
          <div class="invoice-mobile-date">
            <div class="invoice-mobile-date__selected">
              <span class="invoice-mobile-date__label">{{ $t("dueDate") }}</span>
              <strong class="invoice-mobile-date__value">
                {{ invoice.dueDate || "—" }}
              </strong>
              <button
                v-if="invoice.dueDate"
                type="button"
                class="invoice-mobile-date__clear"
                @click="invoice.dueDate = null"
              >
                {{ $t("clear") }}
              </button>
            </div>
            <date-picker
              v-model="invoice.dueDate"
              inline
              simple
              auto-submit
              :locale="$store.state.date_locale"
              :format="$store.state.date_format"
              :min="invoice.date"
              :max="$store.state.endDate"
              class="invoice-mobile-date__picker"
              color="#7B8BA3"
            />
          </div>
        </div>
        <div class="invoice-edit-sheet__footer">
          <v-btn
            color="primary"
            block
            depressed
            class="invoice-edit-sheet__submit"
            height="48"
            @click="backToOtherInputsFromDueDate"
          >
            {{ $t("Save") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="notificationSheet">
      <v-card class="invoice-edit-sheet invoice-notification-sheet">
        <div
          class="invoice-more-sheet__handle"
          role="button"
          :aria-label="$t('close')"
          @touchstart.passive="onSheetHandleTouchStart"
          @touchend="onSheetHandleTouchEnd"
        />
        <div class="invoice-edit-sheet__header" dir="rtl">
          <span class="invoice-edit-sheet__header-spacer" />
          <div class="invoice-edit-sheet__title invoice-edit-sheet__title--inline">
            {{ $t("notification") }}
          </div>
          <button
            type="button"
            class="invoice-mobile-header__icon-btn"
            :aria-label="$t('back')"
            @click="backToExtrasSheet"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div class="invoice-edit-sheet__body invoice-notification-sheet__body" dir="rtl">
          <div class="invoice-notification-sheet__hint">
            {{ $t("invoice-mobile-notification-sheet-hint") }}
          </div>

          <button
            type="button"
            class="invoice-notification-sheet__add"
            @click="addMobileNotification"
          >
            <v-icon size="18" color="#5A6B85">mdi-plus</v-icon>
            <span>{{ $t("addNotification") }}</span>
          </button>

          <div class="invoice-notification-sheet__list">
            <div
              v-if="!mobileNotificationItems.length"
              class="invoice-notification-sheet__empty"
            >
              {{ $t("invoice-mobile-notification-empty") }}
            </div>

            <div
              v-for="(item, index) in mobileNotificationItems"
              :key="'notif-' + index"
              class="invoice-notif-card"
            >
              <div class="invoice-notif-card__type">
                <div class="invoice-edit-field-wrap invoice-notif-card__type-field">
                  <div class="invoice-edit-field-wrap__label">{{ $t("type") }}</div>
                  <v-select
                    v-model="item.notificationType"
                    :items="notificationTypeItems"
                    solo
                    flat
                    dense
                    hide-details
                    height="40"
                    item-value="value"
                    item-text="text"
                    return-object
                    append-icon="mdi-chevron-down"
                    class="invoice-edit-field"
                    @change="syncMobileNotifications"
                  />
                </div>
                <button
                  type="button"
                  class="invoice-notif-card__remove"
                  :aria-label="$t('remove')"
                  @click="removeMobileNotification(index)"
                >
                  <v-icon size="20" color="#E91E63">mdi-close</v-icon>
                </button>
              </div>

              <div class="invoice-notif-card__row">
                <div class="invoice-edit-field-wrap">
                  <div class="invoice-edit-field-wrap__label">{{ $t("value") }}</div>
                  <button
                    type="button"
                    class="invoice-notif-card__input invoice-notif-card__input--numpad"
                    @click="openGenericNumpad('notificationValue', item)"
                  >
                    {{ formatNumpadMoney(item.value) }}
                  </button>
                </div>
                <div class="invoice-edit-field-wrap">
                  <div class="invoice-edit-field-wrap__label">
                    {{ $t("invoice-mobile-time-unit-label") }}
                  </div>
                  <v-select
                    v-model="item.timeType"
                    :items="notificationTimeItems"
                    solo
                    flat
                    dense
                    hide-details
                    height="40"
                    item-value="value"
                    item-text="text"
                    return-object
                    append-icon="mdi-chevron-down"
                    class="invoice-edit-field"
                    @change="syncMobileNotifications"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="invoice-edit-sheet__footer">
          <v-btn
            color="primary"
            block
            depressed
            class="invoice-edit-sheet__submit"
            height="48"
            @click="backToExtrasSheet"
          >
            {{ $t("Save") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="customFieldsSheet">
      <v-card
        class="invoice-edit-sheet invoice-notification-sheet invoice-custom-fields-sheet"
      >
        <div
          class="invoice-more-sheet__handle"
          role="button"
          :aria-label="$t('close')"
          @touchstart.passive="onSheetHandleTouchStart"
          @touchend="onSheetHandleTouchEnd"
        />
        <div class="invoice-edit-sheet__header" dir="rtl">
          <span class="invoice-edit-sheet__header-spacer" />
          <div class="invoice-edit-sheet__title invoice-edit-sheet__title--inline">
            {{ $t("customFields") }}
          </div>
          <button
            type="button"
            class="invoice-mobile-header__icon-btn"
            :aria-label="$t('back')"
            @click="backToExtrasSheet"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div class="invoice-edit-sheet__body invoice-notification-sheet__body" dir="rtl">
          <div class="invoice-notification-sheet__hint">
            {{ $t("invoice-mobile-custom-fields-hint") }}
          </div>

          <button
            type="button"
            class="invoice-notification-sheet__add"
            @click="addMobileCustomField"
          >
            <v-icon size="18" color="#5A6B85">mdi-plus</v-icon>
            <span>{{ $t("addCustomeField") }}</span>
          </button>

          <div class="invoice-notification-sheet__list">
            <div
              v-if="!mobileCustomFieldItems.length"
              class="invoice-notification-sheet__empty"
            >
              {{ $t("invoice-mobile-custom-fields-empty") }}
            </div>

            <div
              v-for="(item, index) in mobileCustomFieldItems"
              :key="'custom-field-' + index"
              class="invoice-notif-card"
            >
              <div class="invoice-notif-card__type">
                <div class="invoice-edit-field-wrap invoice-notif-card__type-field">
                  <div class="invoice-edit-field-wrap__label">
                    {{ $t("additionalFields") }}
                  </div>
                  <v-combobox
                    v-model="item.key"
                    :items="customFieldKeyItems"
                    :search-input.sync="customFieldSearch"
                    solo
                    flat
                    dense
                    hide-details
                    hide-selected
                    height="40"
                    append-icon="mdi-chevron-down"
                    class="invoice-edit-field"
                    @change="syncMobileCustomFields"
                  >
                    <template v-slot:no-data>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title>
                            {{ $t("toCreate") }}: "<strong>{{ customFieldSearch }}</strong
                            >".
                            {{ $t("doEnter") }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-combobox>
                </div>
                <button
                  type="button"
                  class="invoice-notif-card__remove"
                  :aria-label="$t('remove')"
                  @click="removeMobileCustomField(index)"
                >
                  <v-icon size="20" color="#E91E63">mdi-close</v-icon>
                </button>
              </div>

              <div class="invoice-edit-field-wrap">
                <div class="invoice-edit-field-wrap__label">{{ $t("value") }}</div>
                <input
                  v-model="item.value"
                  type="text"
                  class="invoice-notif-card__input invoice-notif-card__input--text"
                  @input="syncMobileCustomFields"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="invoice-edit-sheet__footer">
          <v-btn
            color="primary"
            block
            depressed
            class="invoice-edit-sheet__submit"
            height="48"
            @click="backToExtrasSheet"
          >
            {{ $t("Save") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="contentSheet">
      <v-card class="invoice-edit-sheet">
        <div
          class="invoice-more-sheet__handle"
          role="button"
          :aria-label="$t('close')"
          @touchstart.passive="onSheetHandleTouchStart"
          @touchend="onSheetHandleTouchEnd"
        />
        <div class="invoice-edit-sheet__header" dir="rtl">
          <span class="invoice-edit-sheet__header-spacer" />
          <div class="invoice-edit-sheet__title invoice-edit-sheet__title--inline">
            {{ $t("invoiceContent") }}
          </div>
          <button
            type="button"
            class="invoice-mobile-header__icon-btn"
            :aria-label="$t('back')"
            @click="backToExtrasSheet"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div class="invoice-edit-sheet__body" dir="rtl">
          <div class="invoice-edit-field-wrap">
            <div class="invoice-edit-field-wrap__label">
              {{ $t("description") }}
            </div>
            <textarea
              v-model="invoice.content"
              class="invoice-row-detail__textarea"
              rows="5"
              :placeholder="$t('invoiceContent')"
            />
          </div>
        </div>
        <div class="invoice-edit-sheet__footer">
          <v-btn
            color="primary"
            block
            depressed
            class="invoice-edit-sheet__submit"
            height="48"
            @click="backToExtrasSheet"
          >
            {{ $t("Save") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="clubCreditSheet">
      <v-card class="invoice-edit-sheet">
        <div
          class="invoice-more-sheet__handle"
          role="button"
          :aria-label="$t('close')"
          @touchstart.passive="onSheetHandleTouchStart"
          @touchend="onSheetHandleTouchEnd"
        />
        <div class="invoice-edit-sheet__header" dir="rtl">
          <span class="invoice-edit-sheet__header-spacer" />
          <div class="invoice-edit-sheet__title invoice-edit-sheet__title--inline">
            {{ $t("club-credit") }}
          </div>
          <button
            type="button"
            class="invoice-mobile-header__icon-btn"
            :aria-label="$t('back')"
            @click="backToExtrasSheet"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div class="invoice-edit-sheet__body" dir="rtl">
          <label class="invoice-extras-check">
            <input
              v-model="invoice.credit_status"
              type="checkbox"
              :disabled="!invoice.account"
            />
            <span>{{ $t("invoice-mobile-club-credit-checkbox") }}</span>
          </label>
          <div class="invoice-extras-credit-row">
            <div>
              <div class="invoice-edit-field-wrap__label">
                {{ $t("invoice-mobile-usable-credit-label") }}
              </div>
              <div class="invoice-extras-credit-value">
                {{ formatMoney(invoice.userCredit || 0) }}
              </div>
            </div>
            <button
              type="button"
              class="invoice-mobile-header__icon-btn"
              :aria-label="$t('refresh')"
              :disabled="!invoice.account"
              @click="refreshClubCredit"
            >
              <v-icon size="22" color="#5A6B85">mdi-refresh</v-icon>
            </button>
          </div>
          <div class="invoice-extras-credit-row">
            <div>
              <div class="invoice-edit-field-wrap__label">
                {{ $t("remainAmountCustomer") }}
              </div>
              <div class="invoice-extras-credit-value">
                {{ customerRemainText }}
              </div>
            </div>
          </div>
        </div>
        <div class="invoice-edit-sheet__footer">
          <v-btn
            color="primary"
            block
            depressed
            class="invoice-edit-sheet__submit"
            height="48"
            @click="backToExtrasSheet"
          >
            {{ $t("Save") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="invoiceDiscountSheet">
      <v-card class="invoice-edit-sheet">
        <div
          class="invoice-more-sheet__handle"
          role="button"
          :aria-label="$t('close')"
          @touchstart.passive="onSheetHandleTouchStart"
          @touchend="onSheetHandleTouchEnd"
        />
        <div class="invoice-edit-sheet__header" dir="rtl">
          <span class="invoice-edit-sheet__header-spacer" />
          <div class="invoice-edit-sheet__title invoice-edit-sheet__title--inline">
            {{ $t("total-discount") }}
          </div>
          <button
            type="button"
            class="invoice-mobile-header__icon-btn"
            :aria-label="$t('back')"
            @click="backToExtrasSheet"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div class="invoice-edit-sheet__body" dir="rtl">
          <div class="invoice-edit-field-wrap">
            <div class="invoice-edit-field-wrap__label">{{ $t("discount") }}</div>
            <div class="invoice-row-detail__discount-head">
              <div class="invoice-row-detail__segment">
                <button
                  type="button"
                  class="invoice-row-detail__segment-btn"
                  :class="{ 'is-active': !invoice.percentDiscountStatus }"
                  @click="setInvoiceDiscountMode(false)"
                >
                  {{ currencyLabel || $t("rial") }}
                </button>
                <button
                  type="button"
                  class="invoice-row-detail__segment-btn"
                  :class="{ 'is-active': invoice.percentDiscountStatus }"
                  @click="setInvoiceDiscountMode(true)"
                >
                  {{ $t("percent") }}
                </button>
              </div>
            </div>
            <button
              v-if="invoice.percentDiscountStatus"
              type="button"
              class="invoice-row-detail__input-row"
              @click="openInvoiceDiscountNumpad('percent')"
            >
              <span class="invoice-row-detail__affix">%</span>
              <span class="invoice-row-detail__input invoice-row-detail__input--static">
                {{ invoice.discountPercent || 0 }}
              </span>
            </button>
            <button
              v-else
              type="button"
              class="invoice-row-detail__input-row"
              @click="openInvoiceDiscountNumpad('amount')"
            >
              <span class="invoice-row-detail__input invoice-row-detail__input--static">
                {{ formatNumpadMoney(invoice.discount) }}
              </span>
              <span class="invoice-row-detail__affix">
                {{ currencyLabel || $t("rial") }}
              </span>
            </button>
          </div>
        </div>
        <div class="invoice-edit-sheet__footer">
          <v-btn
            color="primary"
            block
            depressed
            class="invoice-edit-sheet__submit"
            height="48"
            @click="backToExtrasSheet"
          >
            {{ $t("Save") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="productListSheet" class="invoice-product-sheet">
      <v-card class="invoice-product-sheet__card">
        <div
          class="invoice-more-sheet__handle"
          role="button"
          :aria-label="$t('close')"
          @touchstart.passive="onSheetHandleTouchStart"
          @touchend="onSheetHandleTouchEnd"
        />
        <div class="invoice-product-sheet__header">
          <div class="invoice-more-sheet__title invoice-product-sheet__title">
            {{ $t("productList") }}
          </div>
          <button
            type="button"
            class="invoice-mobile-header__icon-btn"
            :aria-label="$t('close')"
            @click="productListSheet = false"
          >
            <v-icon size="22" color="#37474F">mdi-close</v-icon>
          </button>
        </div>
        <div class="invoice-product-sheet__body">
          <products
            v-if="productListSheet"
            v-model="invoice.items"
            :data="productListData"
            @append="onProductAppend"
          />
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet
      v-model="rowNumpadOpen"
      class="invoice-row-numpad-sheet"
      @click:outside="cancelRowNumpad"
    >
      <v-card class="invoice-row-numpad" dir="rtl">
        <div
          class="invoice-more-sheet__handle"
          role="button"
          :aria-label="$t('close')"
          @touchstart.passive="onSheetHandleTouchStart"
          @touchend="onSheetHandleTouchEnd"
        />
        <div class="invoice-row-numpad__header">
          <span class="invoice-row-numpad__header-spacer" />
          <div class="invoice-row-numpad__title">{{ rowNumpadLabel }}</div>
          <button
            type="button"
            class="invoice-mobile-header__icon-btn invoice-row-numpad__back"
            :aria-label="$t('back')"
            @click="cancelRowNumpad"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="invoice-row-numpad__display">
          <div class="invoice-row-numpad__display-label">
            {{ rowNumpadLabel }}
          </div>
          <div class="invoice-row-numpad__display-value">
            {{ rowNumpadDisplay }}
          </div>
        </div>

        <div class="invoice-row-numpad__pad" dir="ltr">
          <div class="invoice-row-numpad__digits">
            <button
              v-for="n in [7, 8, 9, 4, 5, 6, 1, 2, 3]"
              :key="'d-' + n"
              type="button"
              class="invoice-row-numpad__key"
              @click="rowNumpadDigit(n)"
            >
              {{ n }}
            </button>
            <button
              type="button"
              class="invoice-row-numpad__key invoice-row-numpad__key--multi"
              @click="rowNumpadAppend('00')"
            >
              00
            </button>
            <button
              type="button"
              class="invoice-row-numpad__key"
              @click="rowNumpadDigit(0)"
            >
              0
            </button>
            <button
              type="button"
              class="invoice-row-numpad__key invoice-row-numpad__key--multi"
              @click="rowNumpadAppend('000')"
            >
              000
            </button>
          </div>
          <div class="invoice-row-numpad__actions">
            <button
              type="button"
              class="invoice-row-numpad__key invoice-row-numpad__key--action"
              @click="rowNumpadStep(1)"
            >
              +
            </button>
            <button
              type="button"
              class="invoice-row-numpad__key invoice-row-numpad__key--action"
              @click="rowNumpadStep(-1)"
            >
              −
            </button>
            <button
              type="button"
              class="invoice-row-numpad__key invoice-row-numpad__key--action"
              @click="rowNumpadClear"
            >
              C
            </button>
            <button
              type="button"
              class="invoice-row-numpad__key invoice-row-numpad__key--action"
              @click="rowNumpadDot"
            >
              .
            </button>
            <button
              type="button"
              class="invoice-row-numpad__key invoice-row-numpad__key--action"
              @click="rowNumpadBack"
            >
              <v-icon size="20" color="#5A6B85">mdi-backspace-outline</v-icon>
            </button>
          </div>
        </div>

        <div class="invoice-row-numpad__footer">
          <button
            type="button"
            class="invoice-row-numpad__confirm"
            @click="confirmRowNumpad"
          >
            {{ $t("confirm") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="receiveBlockedSheet">
      <v-card class="invoice-edit-sheet invoice-receive-blocked-sheet">
        <div
          class="invoice-more-sheet__handle"
          role="button"
          :aria-label="$t('close')"
          @touchstart.passive="onSheetHandleTouchStart"
          @touchend="onSheetHandleTouchEnd"
        />
        <div class="invoice-edit-sheet__header" dir="rtl">
          <span class="invoice-edit-sheet__header-spacer" />
          <div class="invoice-edit-sheet__title invoice-edit-sheet__title--inline">
            {{ $t("receiveCash") }}
          </div>
          <button
            type="button"
            class="invoice-mobile-header__icon-btn"
            :aria-label="$t('back')"
            @click="receiveBlockedSheet = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div
          class="invoice-edit-sheet__body invoice-receive-blocked-sheet__body"
          dir="rtl"
        >
          <component
            :is="receiveBlockedReason === 'empty' ? 'button' : 'div'"
            type="button"
            class="invoice-receive-blocked-sheet__card"
            :class="{ 'is-action': receiveBlockedReason === 'empty' }"
            @click="onReceiveBlockedCardClick"
          >
            <v-icon
              class="invoice-receive-blocked-sheet__icon"
              size="22"
              :color="receiveBlockedReason === 'empty' ? '#607D8B' : '#1976D2'"
            >
              {{ receiveBlockedReason === "empty" ? "mdi-plus" : "mdi-cash-remove" }}
            </v-icon>
            <div class="invoice-receive-blocked-sheet__texts">
              <div class="invoice-receive-blocked-sheet__card-sub">
                {{ receiveBlockedMessage }}
              </div>
            </div>
            <v-icon
              v-if="receiveBlockedReason === 'empty'"
              class="invoice-receive-blocked-sheet__chevron"
              size="20"
              color="#90A4AE"
            >
              mdi-chevron-left
            </v-icon>
          </component>
        </div>
        <div class="invoice-edit-sheet__footer invoice-receive-blocked-sheet__footer">
          <v-btn
            color="primary"
            block
            depressed
            class="invoice-edit-sheet__submit"
            height="48"
            @click="onReceiveBlockedContinueAnyway"
          >
            {{ $t("notImportant") }}
          </v-btn>
          <v-btn
            block
            outlined
            color="#546E7A"
            class="invoice-edit-sheet__submit invoice-receive-blocked-sheet__dismiss"
            height="48"
            @click="receiveBlockedSheet = false"
          >
            {{ $t("invoice-mobile-got-it") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-snackbar
      :value="invoice.snakbar"
      :color="invoice.snakbarColor ? invoice.snakbarColor : 'primary'"
      top
      @input="invoice.snakbar = $event"
    >
      {{ invoice.snakbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="invoice.snakbar = false">
          {{ $t("close") }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  name: "InvoiceMobile",
  inject: ["invoice"],
  components: {
    Products: () => import("../../../../components/general/Products/Products"),
    autocomplete: () => import("../../../../components/general/patial/autocomplete"),
    customer: () => import("../../../account/customer"),
    ProductForm: () => import("../../../product/product"),
    BarcodeScanner: () => import("../../../../components/general/barcode/barcode"),
    Composite: () => import("../../partial/Composite"),
    Money: () => import("../../../../components/general/Money/index"),
    Status: () => import("../../../../components/general/Status/status"),
    Tag: () => import("../../../../components/general/Tag/tag"),
    Aicomplete: () => import("../../../../components/general/AIcomplete/index.vue"),
    FormAttachmentDoc: () =>
      import("../../../../components/general/FormAttachmentDoc.vue"),
  },
  data() {
    return {
      moreSheet: false,
      extrasSheet: false,
      otherInputsSheet: false,
      dueDateSheet: false,
      notificationSheet: false,
      customFieldsSheet: false,
      contentSheet: false,
      clubCreditSheet: false,
      invoiceDiscountSheet: false,
      documentsSheet: false,
      commissionSheet: false,
      metaLocationSheet: false,
      metaDateSheet: false,
      metaOfficialSheet: false,
      metaTaxSheet: false,
      reviewSheet: false,
      reviewCompositeListSheet: false,
      reviewProductListSheet: false,
      reviewValidationSheet: false,
      reviewValidationErrors: [],
      receiveBlockedSheet: false,
      receiveBlockedReason: "empty",
      mobileNotificationItems: [],
      mobileCustomFieldItems: [],
      customFieldKeyItems: [],
      customFieldSearch: null,
      customFieldKeyTimer: null,
      productListSheet: false,
      cashierMode: false,
      shopSearch: "",
      shopSort: "default",
      shopCategoryId: null,
      shopCategories: [],
      shopProducts: [],
      shopLoading: false,
      shopPage: 1,
      shopLastPage: 1,
      shopSearchTimer: null,
      shopCancelSource: null,
      shopCategoriesLoaded: false,
      metaSheet: false,
      newCustomerDialog: false,
      newCustomer: null,
      newProductDialog: false,
      newProduct: null,
      barcodeDialog: false,
      barcodeLoading: false,
      productPickerRowIndex: null,
      rowDetailSheet: false,
      rowDetailItem: null,
      rowDetailDiscountMode: "percent",
      rowUnitMenuOpen: false,
      rowSerialSheet: false,
      rowSerialInput: "",
      rowSerialSearch: "",
      rowSerialSearchTimer: null,
      rowSerialLocal: [],
      rowSerialSelectedById: {},
      rowSerialSelectionState: {},
      rowSerialDuplicateHint: false,
      sheetHandleTouchY: null,
      rowNumpadOpen: false,
      rowNumpadField: null,
      rowNumpadItem: null,
      rowNumpadDraft: "0",
      rowNumpadPrevious: 0,
      rowNumpadLabel: "",
      rowNumpadReplaceNext: true,
      entityPickerAssign: "invoice",
      entityPickerOpen: false,
      entityPickerType: null,
      entityPickerTitle: "",
      entityPickerSearch: "",
      entityPickerData: null,
      entityPickerItems: [],
      entityPickerLoading: false,
      entityPickerPage: 1,
      entityPickerLastPage: 1,
      entityPickerTimer: null,
      entityPickerCancelSource: null,
    };
  },
  watch: {
    cashierMode(val) {
      if (val) {
        this.ensureShopCategories();
        this.reloadShopProducts();
      }
    },
    shopSearch() {
      if (!this.cashierMode) return;
      clearTimeout(this.shopSearchTimer);
      this.shopSearchTimer = setTimeout(() => {
        this.reloadShopProducts();
      }, 280);
    },
    newCustomer(val) {
      if (val) {
        this.invoice.account = val;
        this.newCustomerDialog = false;
        this.newCustomer = null;
        this.entityPickerOpen = false;
      }
    },
    newProduct(val) {
      if (val) {
        this.onCreatedProduct(val);
      }
    },
    entityPickerSearch() {
      if (!this.entityPickerOpen || !this.entityPickerType) return;
      this.fetchEntityPickerItems(true);
    },
    rowNumpadOpen(val) {
      if (!val && this.rowNumpadField) {
        this.cancelRowNumpad();
      }
    },
    notificationSheet(val) {
      if (val) {
        this.loadMobileNotifications();
      }
    },
    customFieldsSheet(val) {
      if (val) {
        this.loadMobileCustomFields();
        this.loadCustomFieldKeys();
      }
    },
    customFieldSearch() {
      this.loadCustomFieldKeys();
    },
    rowSerialSearch(val) {
      if (!this.rowSerialSheet || !this.rowSerialIsOutput) return;
      clearTimeout(this.rowSerialSearchTimer);
      this.rowSerialSearchTimer = setTimeout(() => {
        if (typeof this.invoice.onSerialSearch === "function") {
          this.invoice.onSerialSearch((val || "").toString().trim());
        }
      }, 400);
    },
    rowSerialOptionsTick() {
      if (!this.rowSerialSheet || !this.rowSerialIsOutput) return;
      this.syncRowSerialLocalFromInvoice();
    },
  },
  mounted() {
    this.ensureInvoiceDate();
    this.lockPageScroll(true);
  },
  // InvoiceIndex is kept alive, so leaving the form deactivates instead of
  // destroying: the page scroll lock must follow the same lifecycle,
  // otherwise html/body/.v-main stay overflow:hidden on the next page.
  activated() {
    this.lockPageScroll(true);
  },
  deactivated() {
    this.lockPageScroll(false);
  },
  beforeDestroy() {
    clearTimeout(this.rowSerialSearchTimer);
    this.lockPageScroll(false);
  },
  computed: {
    entryMode() {
      return this.cashierMode ? "cashier" : "rows";
    },
    shopSortLabel() {
      if (this.shopSort === "priceAsc")
        return this.$t("invoice-mobile-shop-sort-cheapest");
      if (this.shopSort === "priceDesc")
        return this.$t("invoice-mobile-shop-sort-priciest");
      return this.$t("invoice-mobile-shop-sort-price");
    },
    shopProductsDisplay() {
      const rows = Array.isArray(this.shopProducts) ? this.shopProducts.slice() : [];
      if (this.shopSort === "priceAsc") {
        rows.sort((a, b) => this.shopProductPrice(a) - this.shopProductPrice(b));
      } else if (this.shopSort === "priceDesc") {
        rows.sort((a, b) => this.shopProductPrice(b) - this.shopProductPrice(a));
      }
      return rows;
    },
    customerLabel() {
      const type = this.invoice.type;
      if (type === "purchase" || type === "returnPurchase") {
        return this.$t("detailsInvoice");
      }
      return this.$t("customer");
    },
    customerName() {
      const account = this.invoice.account;
      return (account && account.name) || "";
    },
    filledInvoiceItems() {
      return (this.invoice.items || []).filter((item) => item && item.product);
    },
    rowNumpadDisplay() {
      const raw = String(this.rowNumpadDraft == null ? "0" : this.rowNumpadDraft);
      if (raw.endsWith(".")) {
        const base = raw.slice(0, -1) || "0";
        const formatted = this.invoice.$numberFormat
          ? this.invoice.$numberFormat(base, "plain", 0)
          : base;
        return `${formatted}.`;
      }
      if (this.invoice.$numberFormat) {
        return this.invoice.$numberFormat(raw, "plain", 0);
      }
      return raw;
    },
    canCreateProduct() {
      const perms = this.$store.state.permissions || [];
      return perms.includes("Product") || perms.includes("product");
    },
    isProductPicker() {
      return (
        this.entityPickerType === "productService" || this.entityPickerType === "product"
      );
    },
    entityPickerHasMore() {
      return (
        !!this.entityPickerItems.length &&
        this.entityPickerPage < this.entityPickerLastPage
      );
    },
    rowDetailTaxPercent: {
      get() {
        const item = this.rowDetailItem;
        if (!item || !item.product) return 0;
        if (item.product.tax_percent != null && item.product.tax_percent !== "") {
          return item.product.tax_percent;
        }
        return item.tax_percent || 0;
      },
      set(val) {
        if (!this.rowDetailItem || !this.rowDetailItem.product) return;
        this.$set(this.rowDetailItem.product, "tax_percent", val);
        this.$set(this.rowDetailItem, "tax_percent", val);
      },
    },
    currencyLabel() {
      return (
        (this.invoice.currency &&
          (this.invoice.currency.symbol || this.invoice.currency.name)) ||
        this.$store.state.currencySymbol ||
        ""
      );
    },
    rowSerialIsOutput() {
      return this.invoice.serialFlow === "out";
    },
    rowSerialIsInput() {
      return this.invoice.serialFlow !== "out";
    },
    rowSerialLoading() {
      return !!this.invoice.serialsLoading;
    },
    rowSerialHasMore() {
      return !!this.invoice.serialHasMore;
    },
    rowSerialAvailableCount() {
      // Denominator = serials in this list (not invoice line qty).
      return Array.isArray(this.rowSerialLocal) ? this.rowSerialLocal.length : 0;
    },
    rowSerialEffectiveCount() {
      if (this.rowSerialIsOutput) {
        return Object.keys(this.rowSerialSelectedById).length;
      }
      return this.rowSerialLocal.length;
    },
    rowSerialVisibleList() {
      if (this.rowSerialIsOutput) return this.rowSerialLocal;
      const q = (this.rowSerialSearch || "").toString().trim().toLowerCase();
      if (!q) return this.rowSerialLocal;
      return this.rowSerialLocal.filter((item) =>
        String(item.value || "")
          .toLowerCase()
          .includes(q)
      );
    },
    rowSerialOptionsTick() {
      const item = this.invoice.serialItem;
      const opts = item && item.serialOptions;
      const len = Array.isArray(opts) ? opts.length : 0;
      const loading = this.invoice.serialsLoading ? 1 : 0;
      const page = this.invoice.serialPage || 0;
      return `${len}:${loading}:${page}:${item && item.id}`;
    },
    rowSerialAllVisibleSelected() {
      const list = this.rowSerialVisibleList;
      return (
        list.length > 0 && list.every((item) => item && item.id != null && item.selected)
      );
    },
    rowSerialEmptyTitle() {
      if (this.rowSerialIsOutput) {
        return this.rowSerialSearch
          ? this.$t("invoice-mobile-serial-empty-search-title")
          : this.$t("invoice-mobile-serial-empty-warehouse-title");
      }
      return this.$t("invoice-mobile-serial-empty-input-title");
    },
    rowSerialEmptyHint() {
      if (this.rowSerialIsOutput) {
        return this.rowSerialSearch
          ? this.$t("invoice-mobile-serial-empty-search-hint")
          : this.$t("invoice-mobile-serial-empty-warehouse-hint");
      }
      return this.$t("invoice-mobile-serial-empty-input-hint");
    },
    rowSerialConfirmLabel() {
      if (this.rowSerialIsOutput) {
        if (!this.rowSerialEffectiveCount)
          return this.$t("invoice-mobile-confirm-selection");
        return this.$t("invoice-mobile-confirm-selection-count", {
          selected: this.rowSerialEffectiveCount,
          total: this.rowSerialAvailableCount,
        });
      }
      if (!this.rowSerialEffectiveCount) return this.$t("confirm");
      return this.$t("invoice-mobile-confirm-serials-count", {
        count: this.rowSerialEffectiveCount,
      });
    },
    invoiceMetaText() {
      const parts = [
        this.entityLabel(this.invoice.entrepot),
        this.entityLabel(this.invoice.branch),
        this.invoice.date || null,
      ].filter(Boolean);
      return parts.length ? parts.join(" • ") : "";
    },
    footerItemCount() {
      const items = this.invoice.items || [];
      return items.reduce((sum, item) => {
        if (!item || !item.product) return sum;
        const count = Number(item.count);
        return sum + (Number.isFinite(count) ? count : 0);
      }, 0);
    },
    receiveBlockedMessage() {
      if (this.receiveBlockedReason === "empty") {
        return this.$t("first you should add some items");
      }
      return this.$t("invoice-mobile-no-remain-to-receive");
    },
    footerTotalText() {
      const total = this.invoice.total;
      const formatted =
        total != null && this.invoice.$numberFormat
          ? this.invoice.$numberFormat(total)
          : total != null
          ? total
          : "0";
      const currency =
        (this.invoice.currency &&
          (this.invoice.currency.symbol || this.invoice.currency.name)) ||
        this.invoice.$store.state.currencySymbol ||
        "";
      return currency ? `${formatted} ${currency}` : String(formatted);
    },
    mobileShellStyle() {
      return {
        height: "100%",
        maxHeight: "100%",
      };
    },
    showOfficialNumber() {
      const type = this.invoice.type;
      return type === "sell" || type === "presale" || type === "returnSell";
    },
    showTaxInvoiceFields() {
      // Same gate as desktop: taxPayerSystem (global mixin) && sell
      const taxActive =
        this.taxPayerSystem === true ||
        this.invoice.taxPayerSystem === true ||
        !!(
          this.$store.state.allConfig &&
          this.$store.state.allConfig.taxPayerSystem === "active"
        );
      return this.invoice.type === "sell" && taxActive;
    },
    invoicePatternItems() {
      return this.invoice.patternItems && this.invoice.patternItems.length
        ? this.invoice.patternItems
        : [
            { value: "1", text: this.$t("pattern1") },
            { value: "2", text: this.$t("pattern2") },
            { value: "3", text: this.$t("pattern3") },
            { value: "4", text: this.$t("pattern4") },
            { value: "5", text: this.$t("pattern5") },
            { value: "6", text: this.$t("pattern6") },
            { value: "7", text: this.$t("pattern7") },
          ];
    },
    invoiceTypeSelectItems() {
      return this.invoice.invoiceTypeItems && this.invoice.invoiceTypeItems.length
        ? this.invoice.invoiceTypeItems
        : [
            { value: 1, text: this.$t("firstType") },
            { value: 2, text: this.$t("secondType") },
            { value: 3, text: this.$t("thirdType") },
          ];
    },
    productListData() {
      const entrepot = this.invoice.entrepot;
      return JSON.stringify({ entrepot: entrepot ? entrepot.id : null });
    },
    moreItems() {
      const items = [
        {
          key: "productList",
          icon: "mdi-view-grid-outline",
          title: this.$t("productList"),
          subtitle: this.$t("invoice-mobile-view-product-list-subtitle"),
          action: "productList",
        },
        {
          key: "sellList",
          icon: "mdi-format-list-bulleted",
          title: this.$t("list") + " " + this.$t(this.invoice.type),
          subtitle: this.$t("invoice-mobile-view-type-list-subtitle", {
            type: this.$t(this.invoice.type),
          }),
          action: "sellList",
        },
        {
          key: "currency",
          icon: this.invoice.showCurrency ? "mdi-currency-usd" : "mdi-currency-usd-off",
          title: this.$t("showCurrency"),
          subtitle: this.invoice.showCurrency
            ? this.$t("invoice-mobile-currency-mode-on")
            : this.$t("invoice-mobile-currency-mode-off"),
          action: "currency",
        },
      ];
      // Cashier mode already adds products from the shop grid.
      if (!this.cashierMode) {
        items.push({
          key: "newRow",
          icon: "mdi-plus-box-outline",
          title: this.$t("newRow"),
          subtitle: this.$t("invoice-mobile-add-row-to-invoice-subtitle"),
          action: "newRow",
        });
      }
      return items;
    },
    showCommissionEntry() {
      return (
        !!this.invoice.commissionPermission &&
        (this.invoice.type === "sell" || this.invoice.type === "returnSell")
      );
    },
    hasActivePosDevice() {
      try {
        const raw = localStorage.getItem("posDevice");
        if (!raw) return false;
        const devices = JSON.parse(raw);
        if (!Array.isArray(devices) || !devices.length) return false;
        return devices.some((item) => item && item.status === "active");
      } catch (e) {
        return false;
      }
    },
    reviewSubmitLabel() {
      if (this.invoice.id) return this.$t("Save");
      return this.$t("invoice-mobile-final-payment");
    },
    reviewActiveCompositeItems() {
      const source = Array.isArray(this.invoice.compositeTableData)
        ? this.invoice.compositeTableData
        : [];
      return source.filter((item) => item && item.status !== "deactive");
    },
    reviewSummaryRows() {
      const rows = [];
      const money = (value) => this.formatMoney(value || 0);

      rows.push({
        label: this.customerLabel,
        value: this.entityLabel(this.invoice.account) || this.$t("haveNot"),
      });
      if (this.invoice.date) {
        rows.push({ label: this.$t("date"), value: this.invoice.date });
      }
      rows.push({
        label: this.$t("productCount"),
        value: String(this.footerItemCount),
      });

      const discount = Number(this.invoice.discount) || 0;
      if (discount > 0) {
        rows.push({ label: this.$t("total-discount"), value: money(discount) });
      }

      const tax = Number(this.invoice.totalTax) || 0;
      if (tax > 0) {
        rows.push({ label: this.$t("total-tax"), value: money(tax) });
      }

      if (this.invoice.credit_status && Number(this.invoice.credit) > 0) {
        rows.push({
          label: this.$t("club-credit"),
          value: money(this.invoice.credit),
        });
      } else if (this.invoice.credit_status && Number(this.invoice.userCredit) > 0) {
        rows.push({
          label: this.$t("club-credit"),
          value: money(this.invoice.userCredit),
        });
      }

      if (this.invoice.seller && this.entityLabel(this.invoice.seller)) {
        rows.push({
          label: this.$t("visitor"),
          value: this.entityLabel(this.invoice.seller),
        });
      }
      if (Number(this.invoice.commissionAmount) > 0) {
        rows.push({
          label: this.$t("commissionAmount"),
          value: money(this.invoice.commissionAmount),
        });
      }

      rows.push({
        label: this.$t("total"),
        value: money(this.invoice.total),
        emphasis: true,
      });

      return rows;
    },
    reviewCompositeListTitle() {
      return this.invoice.compositeType === "pay"
        ? this.$t("cashPayList")
        : this.$t("invoice-mobile-receipt-list-title");
    },
    reviewCompositeRows() {
      const items = this.reviewActiveCompositeItems;
      if (!items.length) return [];

      const money = (value) => this.formatMoney(value || 0);

      const totalPayment =
        this.invoice.totalPayment != null
          ? Number(this.invoice.totalPayment)
          : items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
      const remain =
        this.invoice.remainInvoice != null
          ? Number(this.invoice.remainInvoice)
          : Number(this.invoice.total || 0) -
            (Number(this.invoice.credit) || 0) -
            totalPayment;

      const typeCounts = items.reduce((acc, item) => {
        const key = item.compositeType || "other";
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});
      const typeSummary = Object.keys(typeCounts)
        .map((key) => `${this.$t(key)}: ${typeCounts[key]}`)
        .join(" · ");

      return [
        { label: this.$t("details"), value: typeSummary || "—" },
        {
          label:
            this.invoice.compositeType === "pay"
              ? this.$t("total-payments")
              : this.$t("total-recieve"),
          value: money(totalPayment),
        },
        {
          label: this.$t("invoice-remain"),
          value: money(remain),
          emphasis: true,
        },
      ];
    },
    attachmentCount() {
      const files = this.invoice.attachments;
      return Array.isArray(files) ? files.length : files ? 1 : 0;
    },
    documentsSubtitle() {
      const count = this.attachmentCount;
      return count
        ? this.$t("invoice-mobile-attached-files-count", { count })
        : this.$t("invoice-mobile-manage-attachments-subtitle");
    },
    metaLocationSubtitle() {
      const entrepot = this.entityLabel(this.invoice.entrepot);
      const branch = this.entityLabel(this.invoice.branch);
      if (entrepot && branch) return `${entrepot} · ${branch}`;
      if (entrepot || branch) return entrepot || branch;
      return this.$t("invoice-mobile-select-entrepot-branch-subtitle");
    },
    metaDateSubtitle() {
      return this.invoice.date || this.$t("invoice-mobile-issue-date-placeholder");
    },
    metaOfficialSubtitle() {
      return this.invoice.officialNumber
        ? String(this.invoice.officialNumber)
        : this.$t("invoice-mobile-official-number-placeholder");
    },
    commissionSubtitle() {
      if (this.invoice.seller && this.entityLabel(this.invoice.seller)) {
        return this.entityLabel(this.invoice.seller);
      }
      if (this.invoice.commissionAmount) {
        return this.formatMoney(this.invoice.commissionAmount);
      }
      return this.$t("invoice-mobile-commission-subtitle");
    },
    customerRemainText() {
      const info = this.invoice.personInfo || this.personInfo;
      const remain =
        info && info.remain && info.remain.remain != null ? info.remain.remain : 0;
      return this.formatMoney(remain);
    },
    extrasItems() {
      const compositeTitle =
        this.invoice.compositeType === "pay"
          ? this.$t("compositePayment")
          : this.$t("compositeReceipt");
      const items = [
        {
          key: "otherInputs",
          icon: "mdi-form-select",
          title: this.$t("otherInputs"),
          subtitle: this.$t("invoice-mobile-other-inputs-subtitle"),
          action: "otherInputs",
        },
        {
          key: "invoiceContent",
          icon: "mdi-text-box-outline",
          title: this.$t("invoiceContent"),
          subtitle: this.$t("invoice-mobile-invoice-content-subtitle"),
          action: "invoiceContent",
        },
        {
          key: "notification",
          icon: "mdi-bell-outline",
          title: this.$t("notification"),
          subtitle: this.$t("invoice-mobile-notification-schedule-subtitle"),
          action: "notification",
        },
        {
          key: "composite",
          icon: "mdi-cash-sync",
          title: compositeTitle,
          subtitle: this.$t("invoice-mobile-composite-extras-subtitle"),
          action: "composite",
        },
        {
          key: "invoiceDiscount",
          icon: "mdi-sale",
          title: this.$t("total-discount"),
          subtitle: this.$t("invoice-mobile-invoice-discount-subtitle"),
          action: "invoiceDiscount",
        },
      ];
      if (this.customFieldsPermission) {
        items.splice(3, 0, {
          key: "customFields",
          icon: "mdi-playlist-plus",
          title: this.$t("customFields"),
          subtitle: this.$t("invoice-mobile-custom-field-extras-subtitle"),
          action: "customFields",
        });
      }
      const clubEnabled =
        this.hasCustomerClub === true || this.invoice.hasCustomerClub === true;
      if (clubEnabled) {
        items.splice(2, 0, {
          key: "clubCredit",
          icon: "mdi-account-credit-card-outline",
          title: this.$t("club-credit"),
          subtitle: this.invoice.account
            ? this.$t("invoice-mobile-club-credit-apply-subtitle")
            : this.$t("invoice-mobile-select-customer-first-subtitle"),
          action: "clubCredit",
          disabled: !this.invoice.account,
        });
      }
      return items;
    },
    documentTypeDisabled() {
      return !(this.$route.name === "presaleEdit" || this.invoice.itemsProp);
    },
    refInvoiceData() {
      return JSON.stringify({
        type: [this.invoice.type === "returnSell" ? "sell" : "purchase"],
        account: this.invoice.account ? this.invoice.account.id : null,
      });
    },
    invoiceMoneyOptions() {
      if (this.invoice.options) return this.invoice.options;
      return {
        locale: "en-US",
        prefix: "",
        suffix: this.currencyLabel,
        precision:
          (this.invoice.currency && this.invoice.currency.decimal) ||
          this.$store.state.currency?.decimal ||
          0,
      };
    },
    notificationTypeItems() {
      return [
        { value: "notification", text: this.$t("notification") },
        { value: "sms", text: this.$t("sms") },
      ];
    },
    notificationTimeItems() {
      return [
        { value: "minutes", text: this.$t("minutes") },
        { value: "hours", text: this.$t("hours") },
        { value: "days", text: this.$t("days") },
        { value: "weeks", text: this.$t("weeks") },
      ];
    },
  },
  methods: {
    entityLabel(entity) {
      if (!entity) return null;
      return entity.name || entity.fame || entity.title || entity.abrName || null;
    },
    goBack() {
      this.moreSheet = false;
      this.invoice.$router.go(-1);
    },
    onBarcode() {
      this.barcodeDialog = true;
    },
    onBarcodeDecode(code) {
      const barcode = (code || "").toString().trim();
      if (!barcode || this.barcodeLoading) return;
      this.barcodeLoading = true;
      const entrepot = this.invoice.entrepot;
      this.$axios
        .get(this.$endpoint + "getdetail", {
          params: {
            select: "productService",
            search: barcode,
            page: 1,
            data: JSON.stringify({
              entrepot: entrepot ? entrepot.id : null,
            }),
            formType: this.invoice.type,
          },
        })
        .then((res) => {
          const rows = Array.from((res.data && res.data.data) || []);
          const exact =
            rows.find((item) => String(item.barcode) === barcode) ||
            rows.find((item) => item.barcode && String(item.barcode).includes(barcode)) ||
            rows[0];
          if (!exact) {
            if (this.invoice.snakbarText !== undefined) {
              this.invoice.snakbarText = this.$t("no-data");
              this.invoice.snakbarColor = "pink";
              this.invoice.snakbar = true;
            }
            return;
          }
          this.barcodeDialog = false;
          this.productPickerRowIndex = null;
          this.applySelectedProduct(exact);
        })
        .catch((err) => {
          if (
            !this.$axios.isCancel(err) &&
            typeof this.serverErrorHandeler === "function"
          ) {
            this.serverErrorHandeler(err);
          }
        })
        .finally(() => {
          this.barcodeLoading = false;
        });
    },
    onRefresh() {
      // Keep current entry mode (cashier vs rows); reset invoice + mobile UI to that mode's initial state.
      this.resetMobileUiForRefresh();
      if (typeof this.invoice.clearForm === "function") {
        this.invoice.clearForm();
      } else if (typeof this.invoice.getInvoiceNumber === "function") {
        this.invoice.getInvoiceNumber();
      }
      if (this.cashierMode) {
        this.ensureShopCategories();
        this.reloadShopProducts();
      }
    },
    resetMobileUiForRefresh() {
      if (this.rowNumpadOpen) {
        this.cancelRowNumpad();
      }
      if (this.rowSerialSheet) {
        this.closeRowSerialSheet();
      }

      this.moreSheet = false;
      this.extrasSheet = false;
      this.otherInputsSheet = false;
      this.dueDateSheet = false;
      this.notificationSheet = false;
      this.customFieldsSheet = false;
      this.contentSheet = false;
      this.clubCreditSheet = false;
      this.invoiceDiscountSheet = false;
      this.documentsSheet = false;
      this.commissionSheet = false;
      this.metaLocationSheet = false;
      this.metaDateSheet = false;
      this.metaOfficialSheet = false;
      this.metaTaxSheet = false;
      this.reviewSheet = false;
      this.reviewCompositeListSheet = false;
      this.reviewProductListSheet = false;
      this.reviewValidationSheet = false;
      this.reviewValidationErrors = [];
      this.receiveBlockedSheet = false;
      this.receiveBlockedReason = "empty";
      this.productListSheet = false;
      this.metaSheet = false;
      this.rowDetailSheet = false;
      this.rowDetailItem = null;
      this.rowDetailDiscountMode = "percent";
      this.rowUnitMenuOpen = false;
      this.newCustomerDialog = false;
      this.newCustomer = null;
      this.newProductDialog = false;
      this.newProduct = null;
      this.barcodeDialog = false;
      this.barcodeLoading = false;
      this.productPickerRowIndex = null;
      this.entityPickerOpen = false;
      this.entityPickerType = null;
      this.entityPickerTitle = "";
      this.entityPickerSearch = "";
      this.entityPickerData = null;
      this.entityPickerItems = [];
      this.entityPickerPage = 1;
      this.entityPickerLastPage = 1;
      this.customFieldSearch = null;
      this.mobileNotificationItems = [];
      this.mobileCustomFieldItems = [];
      this.customFieldKeyItems = [];
      this.sheetHandleTouchY = null;

      // Reset shop filters to defaults; keep cashierMode itself.
      this.shopSearch = "";
      this.shopSort = "default";
      this.shopCategoryId = null;
    },
    openNewCustomer() {
      this.newCustomer = null;
      this.newCustomerDialog = true;
    },
    openNewCustomerFromPicker() {
      this.openNewCustomer();
    },
    openEntityPicker(type, title, assign = "invoice", data = null) {
      this.entityPickerType = type;
      this.entityPickerTitle = title || "";
      this.entityPickerAssign = assign || "invoice";
      this.entityPickerData = data || null;
      this.entityPickerItems = [];
      this.entityPickerPage = 1;
      this.entityPickerLastPage = 1;
      this.entityPickerOpen = true;
      if (this.entityPickerSearch) {
        this.entityPickerSearch = "";
      } else {
        this.fetchEntityPickerItems(true);
      }
    },
    closeEntityPicker() {
      this.entityPickerOpen = false;
      this.productPickerRowIndex = null;
      this.entityPickerAssign = "invoice";
      this.entityPickerData = null;
      if (this.entityPickerCancelSource) {
        this.entityPickerCancelSource.cancel("picker closed");
        this.entityPickerCancelSource = null;
      }
      clearTimeout(this.entityPickerTimer);
    },
    currentEntityPickerValue() {
      if (this.entityPickerType === "entrepot") {
        if (this.entityPickerAssign === "rowEntrepot" && this.rowDetailItem) {
          return this.rowDetailItem.article_entrepot || this.invoice.entrepot;
        }
        return this.invoice.entrepot;
      }
      if (this.entityPickerType === "branch") return this.invoice.branch;
      if (this.entityPickerType === "person") {
        if (this.entityPickerAssign === "seller") return this.invoice.seller;
        return this.invoice.account;
      }
      if (this.entityPickerType === "given") return this.invoice.given;
      if (this.entityPickerType === "currency") return this.invoice.currency;
      if (this.entityPickerType === "invoices") return this.invoice.refInvoice;
      if (this.isProductPicker && this.productPickerRowIndex != null) {
        const item = this.invoice.items[this.productPickerRowIndex];
        return item && item.product;
      }
      return null;
    },
    isEntityPickerSelected(item) {
      const current = this.currentEntityPickerValue();
      if (!item || !current) return false;
      return current.id === item.id;
    },
    selectEntityPickerItem(item) {
      if (!item) return;
      if (this.entityPickerType === "entrepot") {
        if (this.entityPickerAssign === "rowEntrepot" && this.rowDetailItem) {
          this.$set(this.rowDetailItem, "article_entrepot", item);
          if (typeof this.invoice.changeEntrepotAtRow === "function") {
            this.invoice.changeEntrepotAtRow(
              this.rowDetailItem,
              this.itemIndex(this.rowDetailItem)
            );
          }
        } else {
          this.invoice.entrepot = item;
        }
      } else if (this.entityPickerType === "branch") {
        this.invoice.branch = item;
      } else if (this.entityPickerType === "person") {
        if (this.entityPickerAssign === "seller") {
          this.invoice.seller = item;
        } else {
          this.invoice.account = item;
        }
      } else if (this.entityPickerType === "given") {
        this.invoice.given = item;
      } else if (this.entityPickerType === "currency") {
        this.invoice.currency = item;
        this.onCurrencyChange();
      } else if (this.entityPickerType === "invoices") {
        this.invoice.refInvoice = item;
      } else if (this.isProductPicker) {
        this.applySelectedProduct(item);
        return;
      }
      this.closeEntityPicker();
    },
    closeAllMetaSubSheets() {
      this.metaLocationSheet = false;
      this.metaDateSheet = false;
      this.metaOfficialSheet = false;
      this.metaTaxSheet = false;
      this.documentsSheet = false;
      this.commissionSheet = false;
    },
    openMetaSubSheet(sheetKey) {
      this.metaSheet = false;
      this.$nextTick(() => {
        this[sheetKey] = true;
      });
    },
    openMetaDocumentsSheet() {
      this.openMetaSubSheet("documentsSheet");
    },
    openMetaCommissionSheet() {
      this.openMetaSubSheet("commissionSheet");
    },
    backToMetaSheet() {
      this.closeAllMetaSubSheets();
      this.$nextTick(() => {
        this.metaSheet = true;
      });
    },
    onCommissionAmountInput() {
      if (typeof this.invoice.calcCommissionPercent === "function") {
        this.invoice.calcCommissionPercent();
      }
    },
    onCommissionPercentInput() {
      if (typeof this.invoice.calcCommissionAmount === "function") {
        this.invoice.calcCommissionAmount();
      }
    },
    openRowDetail(item) {
      if (!item || !item.product) return;
      this.rowDetailItem = item;
      this.rowDetailDiscountMode = item.discountMode === "amount" ? "amount" : "percent";
      if (!item.article_entrepot && this.invoice.entrepot) {
        this.$set(item, "article_entrepot", this.invoice.entrepot);
      }
      // Ensure unit is set like desktop unitItems()
      this.unitItems(item);
      this.rowDetailSheet = true;
    },
    unitItems(item) {
      if (!item || !item.product) return [];
      const units = [];
      if (item.product.unit) {
        units.push(item.product.unit);
      }
      if (item.product.units && item.product.units.length > 0) {
        item.product.units.forEach((value) => {
          if (value && value.unit) {
            value.unit.value = value.value;
            units.push(value.unit);
          }
        });
      }
      if (!item.unit && units.length) {
        this.$set(item, "unit", units[0]);
      }
      return units;
    },
    rowUnitLabel(item) {
      if (item && item.unit && (item.unit.fame || item.unit.name)) {
        return item.unit.fame || item.unit.name;
      }
      if (item && item.product && item.product.unit) {
        return item.product.unit.fame || item.product.unit.name || this.$t("piece");
      }
      return this.$t("piece");
    },
    unitCalc(item) {
      if (typeof this.invoice.unitCalc === "function") {
        return this.invoice.unitCalc(item) || 1;
      }
      if (item && item.product && item.unit && item.product.units) {
        const i = item.product.units.findIndex((x) => x.unit_id === item.unit.id);
        if (i !== -1 && item.product.units[i].value) {
          return +item.product.units[i].value;
        }
      }
      return 1;
    },
    rowUnitConversionText(item) {
      if (!item || !item.product) return "";
      const units = this.unitItems(item);
      if (units.length <= 1) return "";
      const factor = this.unitCalc(item);
      if (!factor || factor === 1) return "";
      const baseLabel =
        (units[0] && (units[0].fame || units[0].name)) || this.$t("piece");
      const amount = (parseFloat(item.count) || 0) * factor;
      return `= ${amount} ${baseLabel}`;
    },
    onRowUnitChange() {
      if (typeof this.invoice.calcSum === "function") {
        this.invoice.calcSum();
      }
    },
    selectRowUnit(unit) {
      if (!this.rowDetailItem || !unit) return;
      this.$set(this.rowDetailItem, "unit", unit);
      this.rowUnitMenuOpen = false;
      this.onRowUnitChange();
    },
    saveRowDetail() {
      if (this.rowDetailItem && typeof this.invoice.calcSum === "function") {
        this.invoice.calcSum();
      }
      this.rowDetailSheet = false;
    },
    changeRowProductFromDetail() {
      if (!this.rowDetailItem) return;
      const idx = this.itemIndex(this.rowDetailItem);
      this.rowDetailSheet = false;
      this.openProductPicker(idx);
    },
    setRowDiscountMode(mode) {
      this.rowDetailDiscountMode = mode;
      if (!this.rowDetailItem) return;
      if (mode === "percent") {
        this.onRowDiscountPercentInput();
      } else {
        this.onRowDiscountAmountInput();
      }
    },
    onRowDiscountPercentInput() {
      if (!this.rowDetailItem) return;
      if (typeof this.invoice.checkDiscountPercent === "function") {
        this.invoice.checkDiscountPercent(this.rowDetailItem);
      }
    },
    onRowDiscountAmountInput() {
      if (!this.rowDetailItem) return;
      if (typeof this.invoice.checkDiscount === "function") {
        this.invoice.checkDiscount(this.rowDetailItem);
      }
    },
    onRowTaxPercentInput() {
      if (typeof this.invoice.calcSum === "function") {
        this.invoice.calcSum();
      }
    },
    onRowCountInput() {
      if (!this.rowDetailItem) return;
      if (typeof this.invoice.checkCount === "function") {
        this.invoice.checkCount(this.rowDetailItem);
      } else if (typeof this.invoice.calcSum === "function") {
        this.invoice.calcSum();
      }
    },
    onRowCountBlur() {
      if (!this.rowDetailItem) return;
      if (typeof this.invoice.zeroCountCheck === "function") {
        this.invoice.zeroCountCheck(this.rowDetailItem);
      }
    },
    onRowFeeInput() {
      if (typeof this.invoice.calcSum === "function") {
        this.invoice.calcSum();
      }
    },
    formatNumpadMoney(value) {
      const num = parseFloat(value);
      const safe = Number.isFinite(num) ? num : 0;
      if (this.invoice.$numberFormat) {
        return this.invoice.$numberFormat(safe);
      }
      return String(safe);
    },
    openRowNumpad(field, item = null) {
      const target = item || this.rowDetailItem;
      if (!target || !field) return;

      let value = 0;
      let label = "";
      if (field === "fee") {
        value = target.fee;
        label = this.$t("fee");
      } else if (field === "discount") {
        value = target.discount;
        label = this.$t("discount");
      } else if (field === "discountPercent") {
        value = target.discountPercent;
        label = this.$t("discount");
      } else if (field === "tax") {
        value =
          target.tax_percent != null
            ? target.tax_percent
            : target.product && target.product.tax_percent != null
            ? target.product.tax_percent
            : 0;
        label = this.$t("tax");
      } else if (field === "count") {
        value = target.count;
        label = `${this.$t("count")} / ${this.$t("weight")}`;
      } else if (field === "invoiceDiscount") {
        value = this.invoice.discount;
        label = this.$t("total-discount");
      } else if (field === "invoiceDiscountPercent") {
        value = this.invoice.discountPercent;
        label = `${this.$t("discount")} %`;
      } else {
        return;
      }

      const initial = value == null || value === "" ? 0 : value;
      this.rowNumpadField = field;
      this.rowNumpadItem = target;
      this.rowNumpadDraft = String(initial);
      this.rowNumpadPrevious = initial;
      this.rowNumpadLabel = label;
      this.rowNumpadReplaceNext = true;
      this.rowNumpadOpen = true;
    },
    parseNumpadNumber(data) {
      if (data === "" || data == null || data === ".") return 0;
      const num = parseFloat(data);
      return Number.isFinite(num) ? num : 0;
    },
    commitRowNumpadDraft() {
      this.applyRowNumpadValue(this.rowNumpadDraft);
    },
    applyRowNumpadValue(data) {
      const item = this.rowNumpadItem;
      const field = this.rowNumpadField;
      if (!item || !field) return;

      const value = this.parseNumpadNumber(data);

      if (field === "fee") {
        this.$set(item, "fee", value);
        if (typeof this.invoice.calcSum === "function") {
          this.invoice.calcSum();
        }
        return;
      }

      if (field === "discount") {
        this.$set(item, "discount", value);
        if (typeof this.invoice.checkDiscount === "function") {
          this.invoice.checkDiscount(item);
        } else if (typeof this.invoice.calcSum === "function") {
          this.invoice.calcSum();
        }
        return;
      }

      if (field === "discountPercent") {
        this.$set(item, "discountPercent", value);
        if (typeof this.invoice.checkDiscountPercent === "function") {
          this.invoice.checkDiscountPercent(item);
        } else if (typeof this.invoice.calcSum === "function") {
          this.invoice.calcSum();
        }
        return;
      }

      if (field === "tax") {
        if (item.product) {
          this.$set(item.product, "tax_percent", value);
        }
        this.$set(item, "tax_percent", value);
        if (typeof this.invoice.calcSum === "function") {
          this.invoice.calcSum();
        }
        return;
      }

      if (field === "count") {
        this.$set(item, "count", value);
        if (typeof this.invoice.checkCount === "function") {
          this.invoice.checkCount(item);
        } else if (typeof this.invoice.calcSum === "function") {
          this.invoice.calcSum();
        }
        return;
      }

      if (field === "invoiceDiscount") {
        this.invoice.discount = value;
        if (typeof this.invoice.checkTotalDiscount === "function") {
          this.invoice.checkTotalDiscount();
        } else if (typeof this.invoice.calcSum === "function") {
          this.invoice.calcSum();
        }
        return;
      }

      if (field === "invoiceDiscountPercent") {
        this.invoice.discountPercent = value;
        if (typeof this.invoice.discountCheck === "function") {
          this.invoice.discountCheck();
        } else if (typeof this.invoice.calcSum === "function") {
          this.invoice.calcSum();
        }
        return;
      }

      if (field === "commissionPercent") {
        this.invoice.commissionPercent = value;
        if (typeof this.onCommissionPercentInput === "function") {
          this.onCommissionPercentInput();
        }
        return;
      }

      if (field === "notificationValue") {
        this.$set(item, "value", value < 1 ? 1 : value);
        if (typeof this.syncMobileNotifications === "function") {
          this.syncMobileNotifications();
        }
      }
    },
    rowNumpadDigit(num) {
      this.rowNumpadAppend(String(num));
    },
    rowNumpadAppend(chunk) {
      const piece = String(chunk);
      if (!piece) return;
      if (String(this.rowNumpadDraft).length + piece.length > 16) return;
      if (this.rowNumpadReplaceNext) {
        this.rowNumpadDraft = piece;
        this.rowNumpadReplaceNext = false;
      } else if (this.rowNumpadDraft === "0" && /^\d+$/.test(piece)) {
        this.rowNumpadDraft = piece;
      } else {
        this.rowNumpadDraft = `${this.rowNumpadDraft}${piece}`;
      }
      this.commitRowNumpadDraft();
    },
    rowNumpadDot() {
      if (this.rowNumpadReplaceNext) {
        this.rowNumpadDraft = "0.";
        this.rowNumpadReplaceNext = false;
        this.commitRowNumpadDraft();
        return;
      }
      if (String(this.rowNumpadDraft).includes(".")) return;
      this.rowNumpadDraft = `${this.rowNumpadDraft}.`;
      this.commitRowNumpadDraft();
    },
    rowNumpadBack() {
      const current = String(this.rowNumpadDraft || "0");
      if (current.length <= 1) {
        this.rowNumpadDraft = "0";
        this.rowNumpadReplaceNext = true;
      } else {
        this.rowNumpadDraft = current.slice(0, -1);
        this.rowNumpadReplaceNext = false;
      }
      this.commitRowNumpadDraft();
    },
    rowNumpadClear() {
      this.rowNumpadDraft = "0";
      this.rowNumpadReplaceNext = true;
      this.commitRowNumpadDraft();
    },
    rowNumpadStep(delta) {
      const next = this.parseNumpadNumber(this.rowNumpadDraft) + delta;
      const safe = next < 0 ? 0 : next;
      this.rowNumpadDraft = String(safe);
      this.rowNumpadReplaceNext = true;
      this.commitRowNumpadDraft();
    },
    confirmRowNumpad() {
      this.commitRowNumpadDraft();
      if (this.rowNumpadField === "count" && this.rowNumpadItem) {
        if (typeof this.invoice.zeroCountCheck === "function") {
          this.invoice.zeroCountCheck(this.rowNumpadItem);
        }
      }
      this.rowNumpadField = null;
      this.rowNumpadItem = null;
      this.rowNumpadOpen = false;
    },
    cancelRowNumpad() {
      if (this.rowNumpadField && this.rowNumpadItem) {
        this.applyRowNumpadValue(this.rowNumpadPrevious);
      }
      this.rowNumpadField = null;
      this.rowNumpadItem = null;
      this.rowNumpadOpen = false;
    },
    openRowEntrepotPicker() {
      this.openEntityPicker("entrepot", this.$t("entrepot"), "rowEntrepot");
    },
    onSheetHandleTouchStart(e) {
      const touch = e.touches && e.touches[0];
      this.sheetHandleTouchY = touch ? touch.clientY : null;
    },
    onSheetHandleTouchEnd(e) {
      if (this.sheetHandleTouchY == null) return;
      const touch = e.changedTouches && e.changedTouches[0];
      const startY = this.sheetHandleTouchY;
      this.sheetHandleTouchY = null;
      if (!touch) return;
      // Swipe down on handle closes the topmost open sheet.
      if (touch.clientY - startY < 70) return;
      this.closeTopMobileSheetByHandle();
    },
    closeTopMobileSheetByHandle() {
      // Nested / overlay sheets first, then parent sheets.
      const stack = [
        "rowNumpadOpen",
        "rowSerialSheet",
        "receiveBlockedSheet",
        "reviewValidationSheet",
        "reviewProductListSheet",
        "reviewCompositeListSheet",
        "metaLocationSheet",
        "metaDateSheet",
        "metaOfficialSheet",
        "metaTaxSheet",
        "documentsSheet",
        "commissionSheet",
        "dueDateSheet",
        "notificationSheet",
        "customFieldsSheet",
        "contentSheet",
        "clubCreditSheet",
        "invoiceDiscountSheet",
        "otherInputsSheet",
        "extrasSheet",
        "productListSheet",
        "reviewSheet",
        "metaSheet",
        "moreSheet",
        "rowDetailSheet",
      ];
      for (let i = 0; i < stack.length; i++) {
        const key = stack[i];
        if (!this[key]) continue;
        if (key === "rowNumpadOpen") {
          this.cancelRowNumpad();
          return;
        }
        if (key === "rowSerialSheet") {
          this.closeRowSerialSheet();
          return;
        }
        this[key] = false;
        return;
      }
    },
    openRowSerialDialog() {
      if (!this.rowDetailItem) return;
      // Reuse invoice serial scenario (hydrate/fetch) without desktop dialog UI.
      if (typeof this.invoice.openSerialDialog === "function") {
        this.invoice.openSerialDialog(this.rowDetailItem);
      }
      // Keep desktop SerialManagementDialog closed; mobile uses its own sheet.
      if (this.invoice.serialDialog) {
        this.invoice.serialDialog = false;
      }
      this.resetRowSerialDraft();
      this.seedRowSerialSelectionFromItem(this.rowDetailItem);
      this.syncRowSerialLocalFromInvoice();
      this.rowSerialSheet = true;
      this.$nextTick(() => this.focusRowSerialInput());
    },
    closeRowSerialSheet() {
      this.rowSerialSheet = false;
      this.rowSerialInput = "";
      this.rowSerialSearch = "";
      this.rowSerialDuplicateHint = false;
      clearTimeout(this.rowSerialSearchTimer);
    },
    resetRowSerialDraft() {
      this.rowSerialInput = "";
      this.rowSerialSearch = "";
      this.rowSerialDuplicateHint = false;
      this.rowSerialLocal = [];
      this.rowSerialSelectedById = {};
      this.rowSerialSelectionState = {};
      clearTimeout(this.rowSerialSearchTimer);
    },
    formatRowSerialDate(date) {
      if (!date) return "";
      const str = String(date);
      if (/^\d{4}\/\d{2}\/\d{2}$/.test(str)) return str;
      if (!this.$moment) return str;
      const m = this.$moment(str);
      return m.isValid() ? m.format("jYYYY/jMM/jDD") : str;
    },
    seedRowSerialSelectionFromItem(item) {
      if (!this.rowSerialIsOutput || !item) return;
      const serials = Array.isArray(item.serials) ? item.serials : [];
      serials.forEach((serial) => {
        const id = serial && (serial.id || serial.serial_id);
        if (id == null) return;
        const row = {
          id,
          uid: serial.uid || id,
          value: String(serial.value || serial.serial || "").trim(),
          created_at: this.formatRowSerialDate(serial.created_at),
          selected: true,
          product_id: serial.product_id || null,
        };
        this.$set(this.rowSerialSelectedById, id, row);
        this.$set(this.rowSerialSelectionState, id, true);
      });
    },
    rememberRowSerialSelection(item) {
      if (!this.rowSerialIsOutput || !item || item.id == null) return;
      this.$set(this.rowSerialSelectionState, item.id, !!item.selected);
      if (item.selected) {
        this.$set(this.rowSerialSelectedById, item.id, {
          ...item,
          selected: true,
        });
      } else {
        this.$delete(this.rowSerialSelectedById, item.id);
      }
    },
    syncRowSerialLocalFromInvoice() {
      const item = this.invoice.serialItem || this.rowDetailItem;
      if (!item) {
        this.rowSerialLocal = [];
        return;
      }
      let source = [];
      if (this.rowSerialIsOutput) {
        source = Array.isArray(item.serialOptions)
          ? item.serialOptions
          : Array.isArray(this.invoice.serialDialogList)
          ? this.invoice.serialDialogList
          : [];
      } else {
        source = Array.isArray(item.serials) ? item.serials : this.rowSerialLocal;
        // Prefer local draft once user started editing input mode.
        if (this.rowSerialLocal.length && this.rowSerialSheet) {
          return;
        }
      }
      this.rowSerialLocal.forEach((row) => this.rememberRowSerialSelection(row));
      this.rowSerialLocal = (source || [])
        .map((serial, index) => {
          const id = serial.id || null;
          const remembered =
            this.rowSerialIsOutput &&
            id != null &&
            this.rowSerialSelectionState[id] !== undefined;
          return {
            id,
            uid: serial.uid || serial.id || `${serial.value || serial.serial}-${index}`,
            value: String(serial.value || serial.serial || "").trim(),
            created_at: this.formatRowSerialDate(serial.created_at),
            selected: remembered
              ? !!this.rowSerialSelectionState[id]
              : serial.selected !== undefined
              ? !!serial.selected
              : !this.rowSerialIsOutput,
            product_id: serial.product_id || null,
          };
        })
        .filter((row) => row.value);
      this.rowSerialLocal.forEach((row) => this.rememberRowSerialSelection(row));
    },
    focusRowSerialInput() {
      if (!this.rowSerialIsInput) return;
      const el = this.$refs.rowSerialInput;
      if (el && typeof el.focus === "function") el.focus();
    },
    addRowSerial() {
      if (!this.rowSerialIsInput) return;
      const value = (this.rowSerialInput || "").toString().trim();
      if (!value) return;
      const exists = this.rowSerialLocal.some(
        (row) => String(row.value).toLowerCase() === value.toLowerCase()
      );
      if (exists) {
        this.rowSerialDuplicateHint = true;
        this.rowSerialInput = "";
        this.$nextTick(() => this.focusRowSerialInput());
        return;
      }
      this.rowSerialDuplicateHint = false;
      this.rowSerialLocal.unshift({
        uid: `${Date.now()}-${value}`,
        id: null,
        value,
        created_at: this.$moment ? this.$moment().format("jYYYY/jMM/jDD") : "",
        selected: true,
        product_id:
          this.rowDetailItem &&
          this.rowDetailItem.product &&
          this.rowDetailItem.product.id
            ? this.rowDetailItem.product.id
            : null,
      });
      this.rowSerialInput = "";
      this.$nextTick(() => this.focusRowSerialInput());
    },
    removeRowSerial(item) {
      if (!this.rowSerialIsInput || !item) return;
      this.rowSerialLocal = this.rowSerialLocal.filter((row) => row.uid !== item.uid);
      this.$nextTick(() => this.focusRowSerialInput());
    },
    onRowSerialItemClick(item) {
      if (!this.rowSerialIsOutput) return;
      this.toggleRowSerialSelect(item);
    },
    toggleRowSerialSelect(item) {
      if (!this.rowSerialIsOutput || !item) return;
      // Chip remove may pass a selectedById clone; sync list row too.
      const listRow =
        item.id != null ? this.rowSerialLocal.find((row) => row.id === item.id) : null;
      const target = listRow || item;
      target.selected = !target.selected;
      this.rememberRowSerialSelection(target);
      if (listRow && item !== listRow) {
        item.selected = listRow.selected;
      }
    },
    toggleSelectAllRowSerials() {
      if (!this.rowSerialIsOutput) return;
      const selectAll = !this.rowSerialAllVisibleSelected;
      this.rowSerialVisibleList.forEach((item) => {
        if (!item || item.id == null) return;
        item.selected = selectAll;
        this.rememberRowSerialSelection(item);
      });
    },
    clearRowSerialSelection() {
      if (!this.rowSerialIsOutput) return;
      this.rowSerialLocal.forEach((item) => {
        if (!item) return;
        item.selected = false;
        this.rememberRowSerialSelection(item);
      });
      this.rowSerialSelectedById = {};
      this.rowSerialSelectionState = {};
    },
    loadMoreRowSerials() {
      if (typeof this.invoice.loadMoreSerials === "function") {
        this.invoice.loadMoreSerials();
      }
    },
    confirmRowSerialSheet() {
      this.rowSerialLocal.forEach((row) => this.rememberRowSerialSelection(row));
      const productId =
        this.rowDetailItem && this.rowDetailItem.product && this.rowDetailItem.product.id
          ? this.rowDetailItem.product.id
          : null;
      const rows = this.rowSerialIsOutput
        ? Object.keys(this.rowSerialSelectedById).map(
            (id) => this.rowSerialSelectedById[id]
          )
        : this.rowSerialLocal;
      const payload = rows.map((row) => ({
        id: row.id || null,
        product_id: productId,
        value: String(row.value),
        created_at: row.created_at,
        uid: row.uid,
        selected: true,
      }));
      if (typeof this.invoice.setSerials === "function") {
        this.invoice.setSerials(payload);
      } else if (this.rowDetailItem) {
        this.$set(this.rowDetailItem, "serials", payload);
      }
      this.closeRowSerialSheet();
    },
    rowHasSerial(item) {
      if (typeof this.invoice.rowHasSerial === "function") {
        return this.invoice.rowHasSerial(item);
      }
      return !!(item && item.has_serial === "active");
    },
    rowSerialLabel(item) {
      const serials = (item && item.serials) || [];
      if (!serials.length) return this.$t("invoice-mobile-serial-row-placeholder");
      if (serials.length === 1) {
        return (
          serials[0].value ||
          serials[0].serial ||
          serials[0].name ||
          serials[0] ||
          this.$t("invoice-mobile-one-serial")
        );
      }
      return this.$t("invoice-mobile-serial-count", { count: serials.length });
    },
    openProductPicker(rowIndex) {
      this.productPickerRowIndex =
        rowIndex === undefined || rowIndex === null ? null : rowIndex;
      this.openEntityPicker("productService", this.$t("product/service"));
    },
    openNewProductDialog() {
      this.newProduct = null;
      this.newProductDialog = true;
    },
    onCreatedProduct(product) {
      if (!product) return;
      this.newProductDialog = false;
      this.newProduct = null;
      this.applySelectedProduct(product, { fromCreate: true });
    },
    normalizeProductForRow(product) {
      if (!product) return product;
      const normalized = Object.assign({}, product);
      // create API sometimes returns fin_id as accounting id
      if (!normalized.id && normalized.fin_id) {
        normalized.id = normalized.fin_id;
      }
      if (!normalized.type) {
        normalized.type = "product";
      }
      if (!normalized.unit && normalized.unit_id) {
        normalized.unit = {
          id: normalized.unit_id,
          name:
            normalized.unit_name ||
            normalized.unit_fame ||
            this.$t("invoice-mobile-unit-fallback"),
          fame:
            normalized.unit_fame ||
            normalized.unit_name ||
            this.$t("invoice-mobile-unit-fallback"),
        };
      }
      if (normalized.unit && normalized.unit.name && !normalized.unit.fame) {
        normalized.unit.fame = normalized.unit.name;
      }
      // Brand-new products often have no stock yet; keep append/stock checks safe
      if (!normalized.stock) {
        normalized.stock = { count: 0 };
      }
      return normalized;
    },
    applySelectedProduct(product, options = {}) {
      if (!product) return;
      const fromCreate = !!options.fromCreate;
      product = this.normalizeProductForRow(product);
      const rowIndex = this.productPickerRowIndex;
      if (rowIndex != null) {
        const item = this.invoice.items[rowIndex];
        if (item) {
          this.$set(item, "product", product);
          item._boundProductId = null;
          if (typeof this.invoice.changeProduct === "function") {
            this.invoice.changeProduct(item, rowIndex);
          }
          this.unitItems(item);
          if (!item.unit && product.unit) {
            this.$set(item, "unit", product.unit);
          }
          if (typeof this.invoice.calcSum === "function") {
            this.invoice.calcSum();
          }
        }
      } else if (fromCreate || !this.canAppendProduct(product)) {
        // Desktop row path: addRow + changeProduct (append blocks new products with 0 stock)
        this.addProductAsRow(product);
      } else if (typeof this.invoice.append === "function") {
        const appended = this.invoice.append(product);
        if (appended === false) {
          this.addProductAsRow(product);
        } else {
          const last = (this.invoice.items || [])
            .slice()
            .reverse()
            .find((row) => row && row.product && row.product.id === product.id);
          if (last) {
            this.unitItems(last);
            if (!last.unit && product.unit) {
              this.$set(last, "unit", product.unit);
            }
          }
        }
      } else {
        this.addProductAsRow(product);
      }
      this.productPickerRowIndex = null;
      this.closeEntityPicker();
    },
    canAppendProduct(product) {
      if (!product || product.type !== "product") return true;
      const invoiceType = this.invoice.type;
      if (invoiceType !== "sell" && invoiceType !== "returnPurchase") return true;
      const stockCount =
        product.stock && product.stock.count != null
          ? parseFloat(product.stock.count)
          : 0;
      return Number.isFinite(stockCount) && stockCount > 0;
    },
    addProductAsRow(product) {
      if (!product || typeof this.invoice.addRow !== "function") return;
      const items = this.invoice.items || [];
      if (items.length > 0 && !items[0].product) {
        items.splice(0, 1);
      }
      const existingIdx = items.findIndex(
        (row) => row && row.product && row.product.id === product.id
      );
      if (existingIdx !== -1) {
        const row = items[existingIdx];
        row.count = (parseFloat(row.count) || 0) + 1;
        if (typeof this.invoice.checkCount === "function") {
          this.invoice.checkCount(row);
        } else if (typeof this.invoice.calcSum === "function") {
          this.invoice.calcSum();
        }
        return;
      }
      const price = product.price ? product.price : 0;
      this.invoice.addRow(product, 1, price);
      const idx = this.invoice.items.length - 1;
      const row = this.invoice.items[idx];
      if (!row) return;
      row._boundProductId = null;
      if (typeof this.invoice.changeProduct === "function") {
        this.invoice.changeProduct(row, idx);
      }
      this.unitItems(row);
      if (!row.unit && product.unit) {
        this.$set(row, "unit", product.unit);
      }
      if (typeof this.invoice.calcSum === "function") {
        this.invoice.calcSum();
      }
    },
    itemIndex(item) {
      return (this.invoice.items || []).indexOf(item);
    },
    productImageUrl(item) {
      const product = item && item.product;
      if (!product) return "";
      return (
        product.avatar ||
        product.image ||
        product.image_url ||
        (product.meta && product.meta.avatar) ||
        ""
      );
    },
    formatMoney(value) {
      const num = parseFloat(value);
      const safe = Number.isFinite(num) ? num : 0;
      const formatted = this.invoice.$numberFormat
        ? this.invoice.$numberFormat(safe)
        : String(safe);
      return this.currencyLabel ? `${formatted} ${this.currencyLabel}` : formatted;
    },
    rowFeeWithTax(item) {
      const fee = parseFloat(item.fee) || 0;
      const count = parseFloat(item.count) || 0;
      const tax = parseFloat(item.tax) || 0;
      if (!count) return fee;
      return fee + tax / count;
    },
    rowTotal(item) {
      const fee = parseFloat(item.fee) || 0;
      const count = parseFloat(item.count) || 0;
      const discount = parseFloat(item.discount) || 0;
      const tax = parseFloat(item.tax) || 0;
      return fee * count - discount + tax;
    },
    bumpCount(item, delta) {
      if (!item) return;
      const next = (parseFloat(item.count) || 0) + delta;
      item.count = next < 0 ? 0 : next;
      if (item.count <= 0) {
        this.onDeleteRow(item);
        return;
      }
      if (typeof this.invoice.checkCount === "function") {
        this.invoice.checkCount(item);
      } else if (typeof this.invoice.calcSum === "function") {
        this.invoice.calcSum();
      }
    },
    cycleShopSort() {
      if (this.shopSort === "default") this.shopSort = "priceAsc";
      else if (this.shopSort === "priceAsc") this.shopSort = "priceDesc";
      else this.shopSort = "default";
    },
    selectShopCategory(categoryId) {
      this.shopCategoryId = categoryId || null;
      this.reloadShopProducts();
    },
    ensureShopCategories() {
      if (this.shopCategoriesLoaded && this.shopCategories.length) return;
      this.loadShopCategories();
    },
    loadShopCategories() {
      const isDB = localStorage.getItem("dbCreated");
      if (isDB === "active") {
        try {
          const connection = new window.JsStore.Connection();
          connection.openDb("db_" + this.$store.state.userId);
          connection
            .select({ from: "productShortcut" })
            .then((data) => {
              if (data && data.length) {
                this.shopCategories = data;
                this.shopCategoriesLoaded = true;
                return;
              }
              this.fetchShopCategoriesApi();
            })
            .catch(() => this.fetchShopCategoriesApi());
          return;
        } catch (e) {
          // fall through to API
        }
      }
      this.fetchShopCategoriesApi();
    },
    fetchShopCategoriesApi() {
      this.$axios
        .get(this.$endpoint + "productpageshortcut", {
          params: { type: "page", page: 1 },
        })
        .then((res) => {
          const rows = Array.isArray(res.data)
            ? res.data
            : (res.data && res.data.data) || [];
          this.shopCategories = rows;
          this.shopCategoriesLoaded = true;
        })
        .catch((err) => {
          if (typeof this.serverErrorHandeler === "function") {
            this.serverErrorHandeler(err);
          }
        });
    },
    reloadShopProducts() {
      this.shopPage = 1;
      this.shopLastPage = 1;
      this.shopProducts = [];
      this.fetchShopProducts(true);
    },
    onShopScroll(e) {
      const el = e && e.target;
      if (!el || this.shopLoading) return;
      if (this.shopCategoryId) return;
      if (this.shopPage >= this.shopLastPage) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) {
        this.shopPage += 1;
        this.fetchShopProducts(false);
      }
    },
    fetchShopProducts(reset) {
      if (this.shopCategoryId && !(this.shopSearch || "").trim()) {
        this.fetchShopCategoryProducts();
        return;
      }
      this.fetchShopCatalogProducts(reset);
    },
    fetchShopCategoryProducts() {
      const categoryId = this.shopCategoryId;
      if (!categoryId) return;
      if (this.shopCancelSource) {
        this.shopCancelSource.cancel("Canceled due to new request");
      }
      this.shopLoading = true;
      const isDB = localStorage.getItem("dbCreated");
      if (isDB === "active") {
        try {
          const connection = new window.JsStore.Connection();
          connection.openDb("db_" + this.$store.state.userId);
          connection
            .select({
              from: "productShortcutItems",
              where: { page_id: categoryId },
            })
            .then((data) => {
              this.shopProducts = Array.isArray(data) ? data : [];
              this.shopLastPage = 1;
              this.shopLoading = false;
              if (!this.shopProducts.length) {
                this.fetchShopCategoryProductsApi(categoryId);
              }
            })
            .catch(() => {
              this.fetchShopCategoryProductsApi(categoryId);
            });
          return;
        } catch (e) {
          // fall through
        }
      }
      this.fetchShopCategoryProductsApi(categoryId);
    },
    fetchShopCategoryProductsApi(categoryId) {
      this.shopLoading = true;
      this.$axios
        .get(this.$endpoint + "productpageshortcut", {
          params: {
            type: "item",
            page: categoryId,
          },
        })
        .then((res) => {
          const rows = Array.isArray(res.data)
            ? res.data
            : (res.data && res.data.data) || [];
          this.shopProducts = rows;
          this.shopLastPage = 1;
        })
        .catch((err) => {
          if (typeof this.serverErrorHandeler === "function") {
            this.serverErrorHandeler(err);
          }
        })
        .finally(() => {
          this.shopLoading = false;
        });
    },
    fetchShopCatalogProducts(reset) {
      if (this.shopCancelSource) {
        this.shopCancelSource.cancel("Canceled due to new request");
      }
      this.shopCancelSource = this.$axios.CancelToken.source();
      this.shopLoading = true;
      const page = reset ? 1 : this.shopPage;
      const search = (this.shopSearch || "").trim();
      const entrepot = this.invoice.entrepot;
      const params = {
        select: "productService",
        search,
        page,
        formType: this.invoice.type,
        data: JSON.stringify({
          entrepot: entrepot ? entrepot.id : null,
        }),
      };
      this.$axios
        .get(this.$endpoint + "getdetail", {
          params,
          cancelToken: this.shopCancelSource.token,
        })
        .then((res) => {
          const payload = res.data || {};
          const rows = Array.isArray(payload.data)
            ? Array.from(payload.data)
            : Array.isArray(payload)
            ? Array.from(payload)
            : [];
          let lastPage = Number(
            payload.last_page || (payload.meta && payload.meta.last_page) || 1
          );
          if (
            !payload.last_page &&
            !(payload.meta && payload.meta.last_page) &&
            rows.length >= 10
          ) {
            lastPage = Math.max(page + 1, 1);
          }
          this.shopLastPage = Number.isFinite(lastPage) && lastPage > 0 ? lastPage : 1;
          if (reset || page === 1) {
            this.shopProducts = rows;
            this.shopPage = 1;
          } else {
            const map = new Map();
            for (const it of this.shopProducts) {
              map.set(it && it.id != null ? it.id : JSON.stringify(it), it);
            }
            for (const it of rows) {
              const key = it && it.id != null ? it.id : JSON.stringify(it);
              if (!map.has(key)) map.set(key, it);
            }
            this.shopProducts = Array.from(map.values());
          }
          if (!rows.length && page > 1) {
            this.shopLastPage = page - 1;
            this.shopPage = page - 1;
          }
        })
        .catch((err) => {
          if (
            !this.$axios.isCancel(err) &&
            typeof this.serverErrorHandeler === "function"
          ) {
            this.serverErrorHandeler(err);
          }
        })
        .finally(() => {
          this.shopLoading = false;
        });
    },
    shopProductId(product) {
      if (!product) return null;
      return product.id != null ? product.id : product.product_id;
    },
    shopProductPrice(product) {
      if (!product) return 0;
      const raw =
        product.price != null
          ? product.price
          : product.fee != null
          ? product.fee
          : product.sell_price != null
          ? product.sell_price
          : 0;
      const num = parseFloat(raw);
      return Number.isFinite(num) ? num : 0;
    },
    shopProductImage(product) {
      if (!product) return "";
      if (product.avatar && product.avatar.link) return product.avatar.link;
      return (
        product.avatar ||
        product.image ||
        product.image_url ||
        (product.meta && product.meta.avatar) ||
        ""
      );
    },
    findInvoiceItemByProduct(product) {
      const productId = this.shopProductId(product);
      if (productId == null) return null;
      return (this.invoice.items || []).find(
        (row) =>
          row &&
          row.product &&
          (row.product.id === productId || row.product.product_id === productId)
      );
    },
    shopProductCount(product) {
      const row = this.findInvoiceItemByProduct(product);
      return row ? parseFloat(row.count) || 0 : 0;
    },
    shopProductUnitLabel(product) {
      const row = this.findInvoiceItemByProduct(product);
      if (row) return this.rowUnitLabel(row);
      const unit = product && (product.unit || product.unit_name);
      if (!unit) return this.$t("piece");
      if (typeof unit === "string") return unit;
      return unit.fame || unit.name || this.$t("piece");
    },
    shopProductFeeWithTax(product) {
      const row = this.findInvoiceItemByProduct(product);
      if (row) return this.rowFeeWithTax(row);
      return this.shopProductPrice(product);
    },
    shopProductDiscount(product) {
      const row = this.findInvoiceItemByProduct(product);
      return row ? parseFloat(row.discount) || 0 : 0;
    },
    shopProductTotal(product) {
      const row = this.findInvoiceItemByProduct(product);
      if (row) return this.rowTotal(row);
      return 0;
    },
    ensureShopInvoiceRow(product) {
      let row = this.findInvoiceItemByProduct(product);
      if (row) return row;
      this.applySelectedProduct(this.normalizeProductForRow(product));
      return this.findInvoiceItemByProduct(product);
    },
    bumpShopProduct(product, delta) {
      if (!product || !delta) return;
      const existing = this.findInvoiceItemByProduct(product);
      if (existing) {
        this.bumpCount(existing, delta);
        return;
      }
      if (delta < 0) return;
      this.applySelectedProduct(this.normalizeProductForRow(product));
    },
    removeShopProduct(product) {
      const row = this.findInvoiceItemByProduct(product);
      if (!row) return;
      this.onDeleteRow(row);
    },
    openShopProductDetail(product) {
      const row = this.ensureShopInvoiceRow(product);
      if (!row) return;
      this.openRowDetail(row);
    },
    openShopProductNumpad(product) {
      const row = this.ensureShopInvoiceRow(product);
      if (!row) return;
      this.openRowNumpad("count", row);
    },
    onDeleteRow(item) {
      if (!item) return;
      if (this.rowDetailItem === item) {
        this.rowDetailSheet = false;
        this.rowDetailItem = null;
      }
      // Same remove path as desktop confirm, but immediate on mobile
      this.invoice.deleteItem = item;
      if (typeof this.invoice.remove === "function") {
        this.invoice.remove(item);
        return;
      }
      const index = (this.invoice.items || []).indexOf(item);
      if (index >= 0) {
        this.invoice.items.splice(index, 1);
        if (typeof this.invoice.calcSum === "function") {
          this.invoice.calcSum();
        }
      }
    },
    onEntityPickerVisible(isVisible) {
      if (!isVisible) return;
      this.loadMoreEntityPicker();
    },
    onEntityPickerScroll(e) {
      const el = e && e.target;
      if (!el) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) {
        this.loadMoreEntityPicker();
      }
    },
    loadMoreEntityPicker() {
      if (!this.entityPickerOpen || this.entityPickerLoading) return;
      if (this.entityPickerPage >= this.entityPickerLastPage) return;
      this.entityPickerPage += 1;
      this.fetchEntityPickerItems(false);
    },
    maybeFillEntityPicker() {
      this.$nextTick(() => {
        if (!this.entityPickerOpen || this.entityPickerLoading) return;
        if (this.entityPickerPage >= this.entityPickerLastPage) return;
        const body = this.$refs.entityPickerBody;
        if (!body) return;
        // If first pages don't fill the screen, keep loading next pages
        if (body.scrollHeight <= body.clientHeight + 40) {
          this.loadMoreEntityPicker();
        }
      });
    },
    fetchEntityPickerItems(reset) {
      if (!this.entityPickerType) return;
      if (reset) {
        this.entityPickerPage = 1;
        this.entityPickerItems = [];
        this.entityPickerLastPage = 1;
      }
      if (this.entityPickerCancelSource) {
        this.entityPickerCancelSource.cancel("Canceled due to new request");
      }
      this.entityPickerCancelSource = this.$axios.CancelToken.source();
      this.entityPickerLoading = true;
      clearTimeout(this.entityPickerTimer);
      const page = this.entityPickerPage;
      const type = this.entityPickerType;
      const search = (this.entityPickerSearch || "").trim();
      this.entityPickerTimer = setTimeout(() => {
        const params = {
          select: type,
          search,
          page,
        };
        if (this.entityPickerData) {
          params.data = this.entityPickerData;
        }
        if (type === "productService" || type === "product") {
          const entrepot = this.invoice.entrepot;
          params.data = JSON.stringify({
            entrepot: entrepot ? entrepot.id : null,
          });
          params.formType = this.invoice.type;
        }
        this.$axios
          .get(this.$endpoint + "getdetail", {
            params,
            cancelToken: this.entityPickerCancelSource.token,
          })
          .then((res) => {
            const payload = res.data || {};
            const rows = Array.isArray(payload.data)
              ? Array.from(payload.data)
              : Array.isArray(payload)
              ? Array.from(payload)
              : [];
            const lastPage = Number(
              payload.last_page ||
                (payload.meta && payload.meta.last_page) ||
                (rows.length && page === 1 && rows.length < 10
                  ? 1
                  : page + (rows.length ? 1 : 0))
            );
            this.entityPickerLastPage =
              Number.isFinite(lastPage) && lastPage > 0 ? lastPage : 1;
            // If API sent a full page but omitted last_page, assume there may be more
            if (
              !payload.last_page &&
              !(payload.meta && payload.meta.last_page) &&
              rows.length >= 10
            ) {
              this.entityPickerLastPage = Math.max(this.entityPickerLastPage, page + 1);
            }
            if (reset || page === 1) {
              this.entityPickerItems = rows;
            } else {
              const map = new Map();
              for (const it of this.entityPickerItems) {
                map.set(it && it.id != null ? it.id : JSON.stringify(it), it);
              }
              for (const it of rows) {
                const key = it && it.id != null ? it.id : JSON.stringify(it);
                if (!map.has(key)) map.set(key, it);
              }
              this.entityPickerItems = Array.from(map.values());
            }
            if (!rows.length && page > 1) {
              this.entityPickerLastPage = page - 1;
              this.entityPickerPage = page - 1;
            }
          })
          .catch((err) => {
            if (
              !this.$axios.isCancel(err) &&
              typeof this.serverErrorHandeler === "function"
            ) {
              this.serverErrorHandeler(err);
            }
          })
          .finally(() => {
            this.entityPickerLoading = false;
            this.maybeFillEntityPicker();
          });
      }, 50);
    },
    onAddRow() {
      this.openProductPicker();
    },
    onOfficialNumberBlur() {
      if (typeof this.invoice.getOfficialInvoiceNumber === "function") {
        this.invoice.getOfficialInvoiceNumber();
      }
    },
    onInvoicePatternChange(value) {
      this.invoice.invoicePattern = value;
    },
    onReceivePayment() {
      // Mobile-only: open composite cash receive directly (same as cash-format path).
      // Do not block on missing customer — desktop/composite does not either.
      const hasItems =
        Array.isArray(this.filledInvoiceItems) && this.filledInvoiceItems.length > 0;
      const remain = Number(this.invoice.remainInvoice);
      if (!hasItems || !Number.isFinite(remain) || remain <= 0) {
        this.receiveBlockedReason = !hasItems ? "empty" : "noRemain";
        this.receiveBlockedSheet = true;
        return;
      }
      this.openMobileCashReceive();
    },
    openMobileCashReceive() {
      this.$nextTick(() => {
        const comp = this.$refs.invoiceComposite;
        if (comp && typeof comp.openOptionsDialog === "function") {
          comp.openOptionsDialog("cash-format");
          return;
        }
        if (typeof this.invoice.openOptionsDialog === "function") {
          this.invoice.openOptionsDialog("cash-format");
        }
      });
    },
    onReceiveBlockedContinueAnyway() {
      // Bypass block only for receive-cash path; does not open review/submit.
      this.receiveBlockedSheet = false;
      this.openMobileCashReceive();
    },
    onReceiveBlockedCardClick() {
      if (this.receiveBlockedReason !== "empty") return;
      this.receiveBlockedSheet = false;
      this.$nextTick(() => {
        if (this.cashierMode) return;
        this.openProductPicker();
      });
    },
    openReviewSheet() {
      this.reviewCompositeListSheet = false;
      this.reviewProductListSheet = false;
      this.reviewValidationSheet = false;
      this.reviewSheet = true;
    },
    openReviewCompositeListSheet() {
      this.reviewSheet = false;
      this.$nextTick(() => {
        this.reviewCompositeListSheet = true;
      });
    },
    openReviewProductListSheet() {
      this.reviewSheet = false;
      this.$nextTick(() => {
        this.reviewProductListSheet = true;
      });
    },
    backToReviewSheet() {
      this.reviewCompositeListSheet = false;
      this.reviewProductListSheet = false;
      this.$nextTick(() => {
        this.reviewSheet = true;
      });
    },
    reviewCompositeItemDate(item) {
      if (!item) return this.$t("haveNot");
      if (item.dueDate) return item.dueDate;
      if (item.date) return item.date;
      return this.$t("haveNot");
    },
    formatReviewCompositeAmount(item) {
      return this.formatMoney((item && item.amount) || 0);
    },
    reviewCompositeItemOtherLines(item) {
      if (!item) return [];
      const lines = [];
      const other = item.other || {};
      const bank =
        (item.bank && item.bank.name) ||
        (item.bankName && item.bankName.name) ||
        (other.bank && other.bank.name) ||
        (other.bankName && other.bankName.name);
      if (bank) {
        lines.push({ label: this.$t("bank"), value: bank });
      }
      const pos = item.paymentByPos || other.paymentByPos;
      if (pos) {
        lines.push({
          label: this.$t("pos"),
          value: typeof pos === "string" ? this.$t(pos) : pos,
        });
      }
      const fishermanNumber = item.fishermanNumber || other.fishermanNumber;
      if (fishermanNumber) {
        lines.push({
          label: this.$t("fishermanNumber"),
          value: fishermanNumber,
        });
      }
      const number = item.number || other.number;
      if (number) {
        lines.push({ label: this.$t("check-number"), value: number });
      }
      const transferOption = item.transferOption || other.transferOption;
      if (transferOption) {
        lines.push({
          label: this.$t("transfer-type"),
          value: this.$t(transferOption),
        });
      }
      return lines;
    },
    // Mirror desktop create() + v-form rules (feeValidate / required fields).
    isInvalidDesktopFee(fee) {
      if (fee === null || fee === undefined || fee === "") return true;
      const n = parseFloat(fee);
      return !Number.isFinite(n) || n <= 0;
    },
    isInvalidDesktopCount(count) {
      // Desktop rule: (v) => !!v || is-required (zero/empty fails)
      if (count === null || count === undefined || count === "") return true;
      const n = Number(count);
      if (Number.isFinite(n)) return n <= 0;
      return !count;
    },
    getMobileSubmitValidationErrors() {
      const errors = [];
      const items = Array.isArray(this.invoice.items) ? this.invoice.items : [];
      const productItems = items.filter((item) => item && item.product);

      // Same as create(): need at least one product row
      if (!productItems.length) {
        errors.push({
          key: "items",
          title: this.$t("invoice-mobile-validation-goods-title"),
          description: this.$t("invoice-mobile-validation-goods-description"),
          action: "items",
        });
      }

      // Same as create(): account required
      if (!this.invoice.account) {
        errors.push({
          key: "account",
          title: this.customerLabel,
          description: this.$t("invoice-mobile-validation-account-description", {
            label: this.customerLabel,
          }),
          action: "account",
        });
      }

      // Desktop v-form: number required
      if (
        this.invoice.number === null ||
        this.invoice.number === undefined ||
        this.invoice.number === ""
      ) {
        errors.push({
          key: "number",
          title: this.$t("number"),
          description: this.$t("invoice-mobile-validation-number-description"),
          action: "number",
        });
      }

      // Desktop v-form: date required
      if (!this.invoice.date) {
        errors.push({
          key: "date",
          title: this.$t("invoice-date"),
          description: this.$t("invoice-mobile-validation-date-description"),
          action: "date",
        });
      }

      // Desktop v-form: currency required when showCurrency
      if (this.invoice.showCurrency && !this.invoice.currency) {
        errors.push({
          key: "currency",
          title: this.$t("currency"),
          description: this.$t("invoice-mobile-validation-currency-description"),
          action: "currency",
        });
      }

      // Desktop row rules (only rows with product): count required
      const invalidCount = productItems.find((item) =>
        this.isInvalidDesktopCount(item.count)
      );
      if (invalidCount) {
        errors.push({
          key: "count",
          title: this.$t("count"),
          description: this.$t("invoice-mobile-validation-count-description"),
          action: "items",
        });
      }

      // Desktop feeValidate: empty or <= 0 invalid
      const invalidFee = productItems.find((item) => this.isInvalidDesktopFee(item.fee));
      if (invalidFee) {
        errors.push({
          key: "fee",
          title: this.$t("invoice-mobile-validation-fee-title"),
          description: this.$t("invoice-mobile-validation-fee-description"),
          action: "items",
        });
      }

      return errors;
    },
    confirmReviewSubmit() {
      this.reviewCompositeListSheet = false;
      const errors = this.getMobileSubmitValidationErrors();
      if (errors.length) {
        this.reviewValidationErrors = errors;
        this.reviewSheet = false;
        this.$nextTick(() => {
          this.reviewValidationSheet = true;
        });
        return;
      }
      this.reviewValidationSheet = false;
      this.reviewValidationErrors = [];
      this.reviewSheet = false;
      this.$nextTick(() => {
        this.onViewAndSubmit();
      });
    },
    dismissReviewValidation() {
      this.reviewValidationSheet = false;
      this.$nextTick(() => {
        this.reviewSheet = true;
      });
    },
    fixReviewValidationItem(item) {
      if (!item) return;
      this.reviewValidationSheet = false;
      this.reviewSheet = false;
      this.$nextTick(() => {
        switch (item.action) {
          case "account":
            this.openEntityPicker("person", this.customerLabel);
            break;
          case "date":
            this.openMetaSubSheet("metaDateSheet");
            break;
          case "currency":
            this.extrasSheet = true;
            this.$nextTick(() => {
              this.openEntityPicker("currency", this.$t("currency"));
            });
            break;
          case "number":
            if (typeof this.invoice.getInvoiceNumber === "function") {
              this.invoice.getInvoiceNumber();
            }
            break;
          case "items":
          default:
            break;
        }
      });
    },
    onViewAndSubmit() {
      if (this.invoice.id) {
        if (typeof this.invoice.update === "function") {
          this.invoice.update();
        }
        return;
      }
      if (typeof this.invoice.create === "function") {
        // Mobile already validated via reviewValidationSheet; do not rely on desktop v-form.
        this.invoice.create({ skipFormValidation: true });
      }
    },
    closeMore() {
      this.moreSheet = false;
    },
    closeExtras() {
      this.extrasSheet = false;
    },
    backToExtrasSheet() {
      this.otherInputsSheet = false;
      this.dueDateSheet = false;
      this.notificationSheet = false;
      this.customFieldsSheet = false;
      this.contentSheet = false;
      this.clubCreditSheet = false;
      this.invoiceDiscountSheet = false;
      this.$nextTick(() => {
        this.extrasSheet = true;
      });
    },
    openDueDateSheet() {
      this.otherInputsSheet = false;
      this.$nextTick(() => {
        this.dueDateSheet = true;
      });
    },
    backToOtherInputsFromDueDate() {
      this.dueDateSheet = false;
      this.$nextTick(() => {
        this.otherInputsSheet = true;
      });
    },
    runExtrasAction(item) {
      if (!item || item.disabled) return;
      this.closeExtras();
      this.$nextTick(() => {
        switch (item.action) {
          case "otherInputs":
            this.otherInputsSheet = true;
            break;
          case "invoiceContent":
            this.contentSheet = true;
            break;
          case "notification":
            this.notificationSheet = true;
            break;
          case "customFields":
            this.customFieldsSheet = true;
            break;
          case "composite":
            this.openCompositeReceipt();
            break;
          case "invoiceDiscount":
            this.invoiceDiscountSheet = true;
            break;
          case "clubCredit":
            this.clubCreditSheet = true;
            break;
          default:
            break;
        }
      });
    },
    openCompositeReceipt() {
      this.extrasSheet = false;
      this.otherInputsSheet = false;
      this.$nextTick(() => {
        const comp = this.$refs.invoiceComposite;
        if (comp) {
          comp.compositeDialog = true;
        }
      });
    },
    loadMobileNotifications() {
      const source = Array.isArray(this.invoice.notifications)
        ? this.invoice.notifications
        : [];
      this.mobileNotificationItems = source.map((item) => {
        if (item && item.notificationType) {
          return {
            notificationType: item.notificationType,
            timeType: item.timeType,
            value: item.value != null ? item.value : 1,
          };
        }
        const typeValue = (item && item.type) || "notification";
        const timeValue = (item && item.time_type) || "days";
        return {
          notificationType: {
            value: typeValue,
            text: this.$t(typeValue),
          },
          timeType: {
            value: timeValue,
            text: this.$t(timeValue),
          },
          value: item && item.time_value != null ? item.time_value : 1,
        };
      });
    },
    syncMobileNotifications() {
      this.invoice.notifications = this.mobileNotificationItems.map((item) => ({
        notificationType: item.notificationType,
        timeType: item.timeType,
        value: item.value,
      }));
    },
    addMobileNotification() {
      this.mobileNotificationItems.push({
        notificationType: {
          value: "notification",
          text: this.$t("notification"),
        },
        timeType: {
          value: "days",
          text: this.$t("days"),
        },
        value: 1,
      });
      this.syncMobileNotifications();
    },
    removeMobileNotification(index) {
      this.mobileNotificationItems.splice(index, 1);
      this.syncMobileNotifications();
    },
    loadMobileCustomFields() {
      const source = Array.isArray(this.invoice.customFields)
        ? this.invoice.customFields
        : [];
      this.mobileCustomFieldItems = source.map((item) => ({
        key: item && item.key != null ? item.key : null,
        value: item && item.value != null ? item.value : null,
      }));
    },
    syncMobileCustomFields() {
      this.invoice.customFields = this.mobileCustomFieldItems.map((item) => ({
        key: item.key,
        value: item.value,
      }));
    },
    addMobileCustomField() {
      this.mobileCustomFieldItems.push({
        key: null,
        value: null,
      });
      this.syncMobileCustomFields();
    },
    removeMobileCustomField(index) {
      this.mobileCustomFieldItems.splice(index, 1);
      this.syncMobileCustomFields();
    },
    loadCustomFieldKeys() {
      clearTimeout(this.customFieldKeyTimer);
      this.customFieldKeyTimer = setTimeout(() => {
        this.$axios
          .get(this.$endpoint + "getdetail", {
            params: {
              select: "invoiceCustomFields",
              search: this.customFieldSearch,
            },
          })
          .then((res) => {
            this.customFieldKeyItems = res.data || [];
          })
          .catch((err) => {
            if (typeof this.serverErrorHandeler === "function") {
              this.serverErrorHandeler(err);
            } else if (typeof this.invoice.serverErrorHandeler === "function") {
              this.invoice.serverErrorHandeler(err);
            }
          });
      }, 400);
    },
    onSelectTaxOption(item) {
      if (typeof this.invoice.selectTaxOption === "function") {
        this.invoice.selectTaxOption(item);
      }
    },
    onDocumentTypeChange() {
      if (typeof this.invoice.checkProductsCount === "function") {
        this.invoice.checkProductsCount();
      }
    },
    onCurrencyChange() {
      if (typeof this.invoice.changeCurrency === "function") {
        this.invoice.changeCurrency();
      }
    },
    onCompositeUpdate(value) {
      if (typeof this.invoice.onTableUpdate === "function") {
        this.invoice.onTableUpdate(value);
      }
    },
    onCompositeTotalAmount(value) {
      if (typeof this.invoice.totalAmount === "function") {
        this.invoice.totalAmount(value);
      }
    },
    onCompositeInvoiceRemain(value) {
      if (typeof this.invoice.invoiceRemain === "function") {
        this.invoice.invoiceRemain(value);
      }
    },
    onCompositeSendToPos(payload) {
      if (typeof this.invoice.sendToPosProcess === "function") {
        this.invoice.sendToPosProcess(payload);
      }
    },
    refreshClubCredit() {
      if (typeof this.invoice.fetchUserCredit === "function") {
        this.invoice.fetchUserCredit();
      }
    },
    setInvoiceDiscountMode(isPercent) {
      if (!!this.invoice.percentDiscountStatus === !!isPercent) return;
      if (typeof this.invoice.discountChangeStatus === "function") {
        this.invoice.discountChangeStatus();
      } else {
        this.invoice.percentDiscountStatus = !!isPercent;
      }
    },
    openInvoiceDiscountNumpad(mode) {
      const field = mode === "percent" ? "invoiceDiscountPercent" : "invoiceDiscount";
      const value =
        mode === "percent" ? this.invoice.discountPercent : this.invoice.discount;
      this.rowNumpadField = field;
      this.rowNumpadItem = this.invoice;
      this.rowNumpadDraft = value == null || value === "" ? "0" : String(value);
      this.rowNumpadPrevious = value == null || value === "" ? 0 : value;
      this.rowNumpadLabel =
        mode === "percent" ? `${this.$t("discount")} %` : this.$t("total-discount");
      this.rowNumpadReplaceNext = true;
      this.rowNumpadOpen = true;
    },
    openGenericNumpad(field, item = null) {
      if (!field) return;
      let value = 0;
      let label = "";
      let target = item;

      if (field === "commissionPercent") {
        if (!(this.invoice.total > 0)) return;
        value = this.invoice.commissionPercent;
        label = this.$t("commissionPercent");
        target = this.invoice;
      } else if (field === "notificationValue") {
        if (!item) return;
        value = item.value;
        label = this.$t("value");
        target = item;
      } else {
        return;
      }

      const initial = value == null || value === "" ? 0 : value;
      this.rowNumpadField = field;
      this.rowNumpadItem = target;
      this.rowNumpadDraft = String(initial);
      this.rowNumpadPrevious = initial;
      this.rowNumpadLabel = label;
      this.rowNumpadReplaceNext = true;
      this.rowNumpadOpen = true;
    },
    ensureInvoiceDate() {
      if (!this.invoice.date && typeof this.invoice.setDate === "function") {
        this.invoice.setDate();
      }
    },
    lockPageScroll(lock) {
      const value = lock ? "hidden" : "";
      const overscroll = lock ? "none" : "";
      document.documentElement.style.overflow = value;
      document.documentElement.style.overscrollBehavior = overscroll;
      document.body.style.overflow = value;
      document.body.style.overscrollBehavior = overscroll;
      const main = document.querySelector(".v-main");
      const wrap = document.querySelector(".v-main__wrap");
      if (main) {
        main.style.overflow = value;
        main.style.overscrollBehavior = overscroll;
      }
      if (wrap) {
        wrap.style.overflow = value;
        wrap.style.overscrollBehavior = overscroll;
      }
    },
    onProductAppend(payload) {
      if (typeof this.invoice.append === "function") {
        this.invoice.append(payload);
      }
    },
    runMoreAction(item) {
      switch (item.action) {
        case "productList":
          // stay on invoice page — open in-page product picker (not product/list route)
          this.productListSheet = true;
          break;
        case "sellList":
          if (!this.invoice.formType && typeof this.invoice.goTo === "function") {
            this.invoice.goTo(this.invoice.type + "List");
          }
          break;
        case "currency":
          this.invoice.showCurrency = !this.invoice.showCurrency;
          break;
        case "newRow":
          this.openProductPicker();
          break;
        default:
          break;
      }
      this.closeMore();
    },
  },
};
</script>

<style scoped>
.invoice-mobile {
  --invoice-radius: 8px;
  --invoice-blue: #1565c0;
  --invoice-blue-soft: #e3f2fd;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  overscroll-behavior: none;
  background: #fff;
}

.invoice-mobile-header {
  flex: 0 0 auto;
  direction: ltr; /* match mockup: back left, actions right */
  display: grid;
  grid-template-columns: 40px 1fr auto;
  align-items: center;
  gap: 4px;
  min-height: 52px;
  padding: 6px 10px;
  background: #fff;
  border-bottom: 1px solid #eceff1;
}

.invoice-mobile-header__title {
  margin: 0;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: #263238;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.invoice-mobile-header__actions {
  display: flex;
  align-items: center;
  gap: 2px;
}

.invoice-mobile-header__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
}

.invoice-mobile-header__icon-btn:active {
  background: #f5f5f5;
}

.invoice-mobile-header__icon-btn--active {
  background: var(--invoice-blue-soft);
}

.invoice-mobile__body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding: 10px 10px 12px;
  background: #fff;
}

.invoice-mobile__body--shop {
  overflow: hidden;
}

.invoice-mobile__body--shop .invoice-summary-row {
  flex: 0 0 auto;
}

.invoice-mobile-init {
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

.invoice-summary-row {
  --invoice-radius: 8px;
  --invoice-border: #cfd8dc;
  --invoice-blue: #1976d2;
  display: flex;
  align-items: stretch;
  gap: 8px;
}

.invoice-summary-card {
  min-width: 0;
  min-height: 48px;
  padding: 8px 10px;
  border: 1px solid var(--invoice-border);
  border-radius: var(--invoice-radius);
  background: #fff;
  text-align: right;
  cursor: pointer;
}

.invoice-summary-card--customer {
  flex: 1.35 1 0;
  display: flex;
  align-items: stretch;
  gap: 6px;
  padding: 4px 4px 4px 8px;
  cursor: default;
}

.invoice-summary-card--meta {
  flex: 1.65 1 0;
}

.invoice-summary-card__main {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 1px;
  padding: 4px 4px 4px 0;
  border: 0;
  background: transparent;
  text-align: right;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.invoice-summary-card__main:active {
  opacity: 0.72;
}

.invoice-summary-card__create {
  flex: 0 0 40px;
  width: 40px;
  min-height: 40px;
  align-self: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.invoice-summary-card__create:active {
  background: #eceff1;
}

.invoice-summary-card__create:active .v-icon {
  color: #1565c0 !important;
}

.invoice-summary-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  margin-bottom: 2px;
}

.invoice-summary-card__label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #78909c;
  line-height: 1.3;
}

.invoice-summary-card__value {
  display: block;
  max-width: 100%;
  font-size: 12px;
  font-weight: 600;
  color: #37474f;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.invoice-summary-card__value.is-placeholder {
  color: #90a4ae;
  font-weight: 600;
}

.invoice-summary-add-btn {
  flex: 0 0 48px;
  width: 48px;
  min-height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid #cfd8dc;
  border-radius: var(--invoice-radius);
  background: var(--invoice-blue-soft);
  cursor: pointer;
}

.invoice-summary-add-btn:active {
  background: #d0e7fc;
}

.invoice-product-add-row {
  display: flex;
  align-items: stretch;
  gap: 8px;
  width: 100%;
  margin-top: 10px;
}

.invoice-add-row-btn {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 14px;
  border: 1px dashed #b0bec5;
  border-radius: 8px;
  background: #f3f5f7;
  cursor: pointer;
}

.invoice-add-row-btn:active {
  background: #eceff2;
}

.invoice-add-row-btn__text {
  font-size: 14px;
  font-weight: 700;
  color: #546e7a;
  letter-spacing: 0;
}

.invoice-shop {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  margin-top: 12px;
  gap: 10px;
}

.invoice-shop__toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}

.invoice-shop__search {
  flex: 1 1 auto;
  min-width: 0;
  height: 44px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border-radius: 10px;
  background: #f3f5f7;
  border: 1px solid #e0e4e8;
  box-sizing: border-box;
}

.invoice-shop__search-icon {
  flex: 0 0 auto;
}

.invoice-shop__search-input {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  border: 0;
  outline: none;
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  color: #263238;
  text-align: right;
}

.invoice-shop__search-input::placeholder {
  color: #90a4ae;
  font-weight: 500;
}

.invoice-shop__search-input::-webkit-search-cancel-button {
  display: none;
}

.invoice-shop__search-clear {
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
}

.invoice-shop__sort-btn {
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e0e4e8;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.invoice-shop__sort-btn--active {
  background: #e3f2fd;
  border-color: #90caf9;
}

.invoice-shop__sort-btn:active {
  background: #eceff1;
}

.invoice-shop__cats {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  padding-bottom: 2px;
}

.invoice-shop__cats::-webkit-scrollbar {
  display: none;
}

.invoice-shop__cat {
  flex: 0 0 auto;
  height: 34px;
  padding: 0 14px;
  border: 1px solid #cfd8dc;
  border-radius: 8px;
  background: #fff;
  color: #455a64;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.invoice-shop__cat--active {
  background: #1976d2;
  border-color: #1976d2;
  color: #fff;
}

.invoice-shop__cat:active:not(.invoice-shop__cat--active) {
  background: #eceff1;
}

.invoice-shop__list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 8px;
}

.invoice-shop__state,
.invoice-shop__more {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 72px;
  color: #90a4ae;
  font-size: 13px;
  font-weight: 600;
}

.invoice-shop__list .invoice-product-card {
  margin: 0;
}

.invoice-barcode-dialog {
  min-height: 100%;
}

.invoice-product-rows {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
  padding-bottom: 8px;
}

.invoice-product-card {
  border: 1px solid #e8eef3;
  border-radius: 10px;
  background: #fff;
  padding: 10px;
}

.invoice-product-card__top {
  display: flex;
  align-items: stretch;
  gap: 12px;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  text-align: right;
  cursor: pointer;
}

.invoice-row-detail {
  direction: rtl;
  border-radius: 16px 16px 0 0 !important;
  /* Match edit sheets: classic vh is taller than visible area on Xiaomi WebView */
  max-height: 78vh !important;
  max-height: 78dvh !important;
  max-height: 78svh !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.invoice-row-detail__title {
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  color: #212121;
  padding: 0 16px 12px;
  border-bottom: 1px solid #eeeeee;
}

.invoice-row-detail__product {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f2f5;
}

.invoice-row-detail__media {
  flex: 0 0 48px;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: #f0f2f5;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.invoice-row-detail__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.invoice-row-detail__name {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 14px;
  font-weight: 700;
  color: #263238;
  text-align: right;
}

.invoice-row-detail__change {
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e0e4e8;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
}

.invoice-row-detail__body {
  /* Keep header/summary/footer visible; only fields scroll */
  flex: 1 1 auto;
  min-height: 0;
  max-height: calc(78vh - 220px);
  max-height: calc(78dvh - 220px);
  max-height: calc(78svh - 220px);
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding: 12px 16px 8px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.invoice-row-detail__body .invoice-edit-field-wrap {
  margin: 0;
  flex-shrink: 0;
  overflow: visible;
  gap: 8px;
}

.invoice-row-detail__body .invoice-edit-field-wrap__label {
  margin: 0 2px;
  position: static;
}

.invoice-row-detail__discount-head {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 8px;
}

.invoice-row-detail__segment {
  display: inline-flex;
  border: 1px solid #dfe5ea;
  border-radius: 8px;
  overflow: hidden;
  background: #f7f8fa;
}

.invoice-row-detail__segment-btn {
  min-width: 64px;
  height: 32px;
  padding: 0 10px;
  border: 0;
  background: transparent;
  color: #607d8b;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.invoice-row-detail__segment-btn.is-active {
  background: #eceff2;
  color: #37474f;
}

.invoice-row-detail__input-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 44px;
  padding: 0 12px;
  border: 1px solid #cfd8dc;
  border-radius: 8px;
  background: #fff;
  font: inherit;
  color: inherit;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.invoice-row-detail__input-row:active {
  background: #f7f8fa;
}

.invoice-row-detail__affix {
  flex: 0 0 auto;
  color: #78909c;
  font-size: 13px;
  font-weight: 700;
}

.invoice-row-detail__textarea {
  display: block;
  width: 100%;
  min-height: 96px;
  padding: 10px 12px;
  border: 1px solid #cfd8dc;
  border-radius: 8px;
  outline: none;
  background: #fff;
  resize: vertical;
  text-align: right;
  font-size: 14px;
  font-weight: 600;
  color: #37474f;
  line-height: 1.5;
  box-sizing: border-box;
  font-family: inherit;
}

.invoice-row-detail__textarea:focus {
  border-color: #90caf9;
}

.invoice-row-detail__input {
  flex: 1 1 auto;
  min-width: 0;
  height: 42px;
  border: 0;
  outline: none;
  background: transparent;
  text-align: right;
  font-size: 14px;
  color: #263238;
}

.invoice-row-detail__input--static {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-weight: 700;
  pointer-events: none;
}

.invoice-row-detail__money {
  display: block;
  width: 100%;
  min-height: 44px;
  margin: 0 !important;
}

.invoice-row-detail__money ::v-deep .v-input {
  margin: 0 !important;
  padding: 0 !important;
}

.invoice-row-detail__money ::v-deep .v-input__control {
  min-height: 44px !important;
}

.invoice-row-detail__money ::v-deep .v-input__slot {
  min-height: 44px !important;
  height: 44px !important;
  margin: 0 !important;
  padding: 0 12px !important;
  border: 1px solid #cfd8dc !important;
  border-radius: 8px !important;
  background: #fff !important;
  box-shadow: none !important;
}

.invoice-row-detail__money ::v-deep .v-text-field__details,
.invoice-row-detail__money ::v-deep .v-messages {
  display: none !important;
}

.invoice-row-detail__money ::v-deep .v-input__slot::before,
.invoice-row-detail__money ::v-deep .v-input__slot::after {
  display: none !important;
}

.invoice-row-numpad {
  padding: 6px 12px calc(12px + env(safe-area-inset-bottom, 0px));
  border-radius: 16px 16px 0 0 !important;
  background: #f3f5f7 !important;
}

.invoice-row-numpad__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.invoice-row-numpad__title {
  flex: 1 1 auto;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #37474f;
}

.invoice-row-numpad__header-spacer {
  flex: 0 0 40px;
  width: 40px;
}

.invoice-row-numpad__back {
  flex: 0 0 40px;
}

.invoice-row-numpad__display {
  margin-bottom: 12px;
  padding: 12px 16px 14px;
  border-radius: 14px;
  border: 1px solid #d5dde8;
  background: linear-gradient(180deg, #f8f9fc 0%, #eef1f6 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.85);
}

.invoice-row-numpad__display-label {
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #8a96a8;
  text-align: right;
}

.invoice-row-numpad__display-value {
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-size: 32px;
  font-weight: 800;
  color: #5a6b85;
  direction: ltr;
  unicode-bidi: plaintext;
  letter-spacing: 0.03em;
  line-height: 1.1;
}

.invoice-row-numpad__pad {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 1fr);
  gap: 8px;
  margin-bottom: 10px;
}

.invoice-row-numpad__digits {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.invoice-row-numpad__actions {
  display: grid;
  grid-template-rows: repeat(5, minmax(0, 1fr));
  gap: 8px;
}

.invoice-row-numpad__key {
  min-height: 48px;
  border: 0;
  border-radius: 12px;
  background: #fff;
  color: #263238;
  font-size: 22px;
  font-weight: 800;
  box-shadow: 0 1px 0 rgba(55, 71, 79, 0.08);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.invoice-row-numpad__key:active {
  background: #eceff1;
}

.invoice-row-numpad__key--multi {
  font-size: 16px;
}

.invoice-row-numpad__key--action {
  background: #e8edf5;
  color: #5a6b85;
  font-size: 18px;
  border: 1px solid #d0d8e4;
}

.invoice-row-numpad__key--action:active {
  background: #d9e1ec;
}

.invoice-row-numpad__footer {
  margin-top: 2px;
}

.invoice-row-numpad__confirm {
  width: 100%;
  min-height: 48px;
  border: 1px solid #c5d0e0;
  border-radius: 12px;
  background: #e8edf5;
  color: #5a6b85;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.invoice-row-numpad__confirm:active {
  background: #d9e1ec;
}

.invoice-row-detail__summary {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 10px 16px;
  border-top: 1px solid #f0f2f5;
  font-size: 13px;
  font-weight: 700;
  color: #546e7a;
}

.invoice-row-detail__summary-sep {
  color: #cfd8dc;
}

.invoice-row-detail__footer {
  flex: 0 0 auto;
  /* Extra bottom space so Save stays above Xiaomi system/nav chrome */
  padding: 10px 16px calc(20px + env(safe-area-inset-bottom, 0px));
  background: #fff;
  border-top: 1px solid #eeeeee;
}

/* Higher specificity: base .invoice-edit-sheet__body sets display:block later */
.invoice-edit-sheet__body.invoice-row-serial-sheet__body {
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
  padding: 16px 16px 12px !important;
}

.invoice-row-serial-sheet__add,
.invoice-row-serial-sheet__toolbar {
  flex: 0 0 auto;
  width: 100%;
  margin: 0 0 16px;
  box-sizing: border-box;
}

.invoice-row-serial-sheet__list {
  flex: 1 1 auto;
  width: 100%;
  min-height: 0;
  margin: 0;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding-bottom: 4px;
}

.invoice-row-serial-sheet__link-btn {
  border: 0;
  background: transparent;
  color: #1976d2;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  padding: 0;
  min-width: 64px;
  text-align: center;
}

.invoice-row-serial-sheet__link-btn.is-muted,
.invoice-row-serial-sheet__link-btn:disabled {
  color: #b0bec5;
  cursor: default;
}

.invoice-row-serial-sheet__add {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.invoice-row-serial-sheet__add-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
}

.invoice-row-serial-sheet__input,
.invoice-row-serial-sheet__search-input {
  width: 100%;
  min-height: 44px;
  padding: 0 12px;
  border: 1px solid #d7dee6;
  border-radius: 12px;
  background: #fff;
  outline: none;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  color: #37474f;
  text-align: right;
  box-sizing: border-box;
}

.invoice-row-serial-sheet__input:focus,
.invoice-row-serial-sheet__search-input:focus {
  border-color: #64b5f6;
}

.invoice-row-serial-sheet__add-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 80px;
  min-height: 44px;
  padding: 0 12px;
  border: 0;
  border-radius: 12px;
  background: #1976d2;
  color: #fff;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
}

.invoice-row-serial-sheet__hint {
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  color: #e65100;
}

.invoice-row-serial-sheet__toolbar {
  display: block;
}

.invoice-row-serial-sheet__search {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 46px;
  margin: 0 0 16px;
  padding: 0 14px;
  border: 1px solid #d7dee6;
  border-radius: 12px;
  background: #fff;
}

.invoice-row-serial-sheet__search .invoice-row-serial-sheet__search-input {
  border: 0;
  min-height: 42px;
  padding: 0;
}

.invoice-row-serial-sheet__tools {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 10px 12px;
  border-radius: 12px;
  background: #f3f7fc;
}

.invoice-row-serial-sheet__tools .invoice-row-serial-sheet__link-btn:first-child {
  justify-self: start;
  text-align: right;
}

.invoice-row-serial-sheet__tools .invoice-row-serial-sheet__link-btn:last-child {
  justify-self: end;
  text-align: left;
}

.invoice-row-serial-sheet__count {
  justify-self: center;
  min-width: 48px;
  padding: 4px 10px;
  border-radius: 999px;
  background: #fff;
  color: #1976d2;
  font-size: 12px;
  font-weight: 800;
  text-align: center;
  box-shadow: 0 0 0 1px rgba(25, 118, 210, 0.12);
}

.invoice-row-serial-sheet__list > .invoice-row-serial-sheet__item,
.invoice-row-serial-sheet__list > .invoice-row-serial-sheet__empty,
.invoice-row-serial-sheet__list > .invoice-row-serial-sheet__more {
  margin: 0 0 16px;
}

.invoice-row-serial-sheet__list > :last-child {
  margin-bottom: 4px;
}

.invoice-row-serial-sheet__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 12px;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: #90a4ae;
}

.invoice-row-serial-sheet__empty strong {
  color: #607d8b;
  font-size: 13px;
}

.invoice-row-serial-sheet__empty span {
  max-width: 280px;
  line-height: 1.6;
}

.invoice-row-serial-sheet__item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0;
  min-height: 68px;
  padding: 14px 12px;
  border: 1px solid #e3eaf2;
  border-radius: 14px;
  background: #fff;
  cursor: default;
  overflow: hidden;
  transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
  -webkit-tap-highlight-color: transparent;
}

.invoice-row-serial-sheet__item.is-pickable {
  cursor: pointer;
}

.invoice-row-serial-sheet__item.is-pickable:active {
  background: #f7fafc;
}

.invoice-row-serial-sheet__item.is-selected {
  border-color: #90caf9;
  background: #eaf3fc;
  box-shadow: 0 0 0 1px rgba(25, 118, 210, 0.08);
}

.invoice-row-serial-sheet__side {
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.invoice-row-serial-sheet__mark {
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.invoice-row-serial-sheet__mark-dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid #cfd8dc;
  background: #fff;
  box-sizing: border-box;
  transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.invoice-row-serial-sheet__item.is-selected .invoice-row-serial-sheet__mark-dot {
  border-color: #1976d2;
  background: #1976d2;
  box-shadow: inset 0 0 0 3px #fff;
}

.invoice-row-serial-sheet__item-index {
  flex: 0 0 28px;
  width: 28px;
  color: #90a4ae;
  font-size: 12px;
  font-weight: 700;
  text-align: center;
}

.invoice-row-serial-sheet__item-main {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  text-align: center;
  padding: 0 4px;
}

.invoice-row-serial-sheet__item-main--start {
  align-items: flex-start;
  text-align: right;
}

.invoice-row-serial-sheet__item-main strong {
  font-size: 14px;
  font-weight: 800;
  color: #37474f;
  word-break: break-all;
  letter-spacing: 0.02em;
  line-height: 1.35;
}

.invoice-row-serial-sheet__item.is-selected .invoice-row-serial-sheet__item-main strong {
  color: #0d47a1;
}

.invoice-row-serial-sheet__item-main small {
  font-size: 11px;
  font-weight: 600;
  color: #90a4ae;
}

.invoice-row-serial-sheet__item.is-selected .invoice-row-serial-sheet__item-main small {
  color: #64b5f6;
}

.invoice-row-serial-sheet__remove {
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
}

.invoice-row-serial-sheet__more {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 44px;
  border: 1px dashed #c5d6ea;
  border-radius: 12px;
  background: #f5f9fd;
  color: #1976d2;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
}

.invoice-row-serial-sheet__more:disabled {
  opacity: 0.6;
  cursor: default;
}

.invoice-row-detail__footer .invoice-edit-sheet__submit {
  position: static;
  box-shadow: none;
  padding-top: 0;
  padding-bottom: 0;
  margin-top: 0 !important;
}

.invoice-product-card__media {
  flex: 0 0 88px;
  width: 88px;
  height: 88px;
  min-width: 88px;
  min-height: 88px;
  max-width: 88px;
  max-height: 88px;
  align-self: flex-start;
  border-radius: 10px;
  background: #f0f2f5;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.invoice-product-card__img {
  width: 88px;
  height: 88px;
  max-width: 100%;
  max-height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

.invoice-product-card__info {
  flex: 1 1 auto;
  min-width: 0;
  text-align: right;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
}

.invoice-product-card__title {
  font-size: 14px;
  font-weight: 700;
  color: #263238;
  line-height: 1.35;
  margin-bottom: 4px;
}

.invoice-product-card__line {
  font-size: 12px;
  color: #546e7a;
  line-height: 1.45;
}

.invoice-product-card__line--accent {
  color: #455a64;
}

.invoice-product-card__line--accent span {
  color: #607d8b;
  font-weight: 700;
}

.invoice-product-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid #f0f2f5;
}

.invoice-product-card__tools {
  display: flex;
  align-items: center;
  gap: 8px;
}

.invoice-product-card__tool {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 40px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;
  border: 1px solid #e0e4e8;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.invoice-product-card__tool:active {
  background: #eceff2;
}

.invoice-product-card__count {
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  height: 40px;
  min-height: 40px;
  gap: 4px;
  border: 1px solid #e0e4e8;
  border-radius: 8px;
  background: #f7f8fa;
  padding: 0 2px;
}

.invoice-product-card__count-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
}

.invoice-product-card__count-value {
  min-width: 36px;
  padding: 0;
  border: 0;
  background: transparent;
  text-align: center;
  font-size: 14px;
  font-weight: 700;
  color: #37474f;
  line-height: 1.15;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.invoice-product-card__count-value small {
  display: block;
  font-size: 10px;
  font-weight: 600;
  color: #78909c;
}

.invoice-row-detail__unit-hint {
  margin-top: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
  text-align: right;
}

.invoice-new-product-dialog {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.invoice-new-product-dialog__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.invoice-add-row-btn__text {
  font-size: 13px;
  font-weight: 700;
  color: var(--invoice-blue);
  line-height: 1;
}

.invoice-edit-sheet__body.invoice-receive-blocked-sheet__body {
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
  padding-top: 12px !important;
  padding-bottom: 8px !important;
}

.invoice-receive-blocked-sheet__card {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 14px 14px;
  border: 1px solid #e3eaf2;
  border-radius: 14px;
  background: #f5f8fb;
  text-align: right;
  box-sizing: border-box;
}

.invoice-receive-blocked-sheet__card.is-action {
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.invoice-receive-blocked-sheet__card.is-action:active {
  background: #eaf1f8;
  border-color: #bbdefb;
}

.invoice-receive-blocked-sheet__icon {
  flex: 0 0 auto;
  line-height: 1;
}

.invoice-receive-blocked-sheet__texts {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: right;
}

.invoice-receive-blocked-sheet__card-sub {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #546e7a;
  line-height: 1.5;
}

.invoice-receive-blocked-sheet__chevron {
  flex: 0 0 auto;
}

.invoice-receive-blocked-sheet__footer {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.invoice-receive-blocked-sheet__dismiss {
  border-color: #cfd8dc !important;
  background: #fff !important;
  color: #546e7a !important;
  font-weight: 700 !important;
  letter-spacing: 0 !important;
}

.invoice-receive-blocked-sheet__dismiss:active {
  background: #f5f7f9 !important;
}

.invoice-mobile-footer {
  flex: 0 0 auto;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));
  border: 0;
  border-top: 1px solid #eceff1;
  border-radius: 0;
  background: #fff;
  box-shadow: none;
}

.invoice-mobile-footer__summary {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #37474f;
  line-height: 1.35;
  white-space: nowrap;
}

.invoice-mobile-footer__sep {
  color: #90a4ae;
  margin: 0 2px;
}

.invoice-mobile-footer__amount {
  color: var(--invoice-blue);
  font-weight: 700;
}

.invoice-mobile-footer__btn {
  flex: 0 0 auto;
  min-height: 36px;
  padding: 7px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
}

.invoice-mobile-footer__btn--solid {
  border: 0;
  background: var(--invoice-blue-soft);
  color: var(--invoice-blue);
}

.invoice-mobile-footer__btn--outline {
  border: 1px solid var(--invoice-blue);
  background: #fff;
  color: var(--invoice-blue);
}

.invoice-edit-sheet__body.invoice-review-sheet__body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 18px 18px 12px;
}

.invoice-review-sheet__hint {
  margin: 0 2px 2px;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
  line-height: 1.7;
}

.invoice-review-sheet__card {
  padding: 16px 16px 14px;
  border: 1px solid #e6ebf0;
  border-radius: 14px;
  background: #f7f8fa;
}

.invoice-review-sheet__card--soft {
  background: #eef2f6;
}

.invoice-review-sheet__card-title {
  margin: 0 0 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e0e6ed;
  font-size: 13px;
  font-weight: 700;
  color: #5a6b85;
  line-height: 1.5;
}

.invoice-review-sheet__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 2px;
  font-size: 13px;
  color: #78909c;
  line-height: 1.45;
}

.invoice-review-sheet__row strong {
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  text-align: left;
}

.invoice-review-sheet__row.is-emphasis {
  margin-top: 6px;
  padding-top: 14px;
  padding-bottom: 4px;
  border-top: 1px solid #dde3ea;
  color: #546e7a;
}

.invoice-review-sheet__row.is-emphasis strong {
  color: #263238;
  font-size: 14px;
}

.invoice-edit-sheet__body.invoice-review-validation-sheet__body {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 16px 12px;
}

.invoice-review-validation-sheet__hint {
  margin: 0 2px 6px;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
  line-height: 1.7;
}

.invoice-review-validation-sheet__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 12px;
  border: 1px solid #ffcdd2;
  border-radius: 12px;
  background: #fff5f5;
  text-align: right;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.invoice-review-validation-sheet__item:active {
  background: #ffebee;
}

.invoice-review-validation-sheet__icon {
  flex: 0 0 auto;
}

.invoice-review-validation-sheet__texts {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.invoice-review-validation-sheet__item-title {
  font-size: 14px;
  font-weight: 800;
  color: #c62828;
}

.invoice-review-validation-sheet__item-sub {
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
  line-height: 1.55;
}

.invoice-review-product-list__row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #e0e6ed;
  border-radius: 12px;
  background: #fff;
}

.invoice-review-product-list__media {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
  background: #f5f7f9;
  border: 1px solid #e8edf3;
}

.invoice-review-product-list__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.invoice-review-product-list__info {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: right;
}

.invoice-review-product-list__info strong {
  font-size: 13px;
  font-weight: 800;
  color: #37474f;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.invoice-review-product-list__info span {
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
  line-height: 1.3;
}

.invoice-review-product-list__amount {
  flex: 0 0 auto;
  max-width: 42%;
  font-size: 12px;
  font-weight: 800;
  color: #263238;
  direction: ltr;
  text-align: left;
  line-height: 1.3;
}

.invoice-review-sheet__list-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 48px;
  padding: 0 14px;
  border: 1px solid #c5d0e0;
  border-radius: 12px;
  background: #e8edf5;
  color: #5a6b85;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.invoice-review-sheet__list-btn span {
  flex: 1 1 auto;
  text-align: right;
}

.invoice-review-sheet__list-btn:active {
  background: #d9e1ec;
}

.invoice-edit-sheet__body.invoice-review-list-sheet__body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 18px 12px;
}

.invoice-review-list-sheet__empty {
  padding: 28px 12px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: #90a4ae;
}

.invoice-review-list-sheet__card {
  padding: 14px 16px 10px;
  border: 1px solid #e0e6ed;
  border-radius: 14px;
  background: #f7f8fa;
}

.invoice-review-list-sheet__head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e6ebf0;
}

.invoice-review-list-sheet__index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 8px;
  background: #e8edf5;
  color: #5a6b85;
  font-size: 12px;
  font-weight: 800;
}

.invoice-review-list-sheet__type {
  font-size: 14px;
  font-weight: 800;
  color: #37474f;
}

.invoice-review-sheet__pay-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 48px;
  margin: 0;
  padding: 0 14px;
  border: 1.5px solid #00664d;
  border-radius: 12px;
  background: rgba(0, 102, 77, 0.1);
  color: #00664d;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.invoice-review-sheet__pay-btn:active {
  background: rgba(0, 102, 77, 0.16);
}

.invoice-review-sheet__pay-btn:disabled {
  opacity: 0.65;
  cursor: default;
}

.invoice-mobile-footer__btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.invoice-mobile-footer__more {
  flex: 0 0 28px;
  width: 28px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: -4px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
}

.invoice-edit-sheet {
  direction: rtl;
  border-radius: 16px 16px 0 0 !important;
  /* svh/dvh: avoid Xiaomi WebView where classic vh is taller than the visible area */
  max-height: 78vh !important;
  max-height: 78dvh !important;
  max-height: 78svh !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
}

.invoice-edit-sheet__title {
  flex: 0 0 auto;
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  color: #212121;
  padding: 4px 16px 14px;
  border-bottom: 1px solid #eeeeee;
}

.invoice-edit-sheet__header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 8px 8px;
  border-bottom: 1px solid #eeeeee;
}

.invoice-edit-sheet__title--inline {
  flex: 1 1 auto;
  margin: 0;
  padding: 0;
  border-bottom: 0;
}

.invoice-edit-sheet__header-spacer {
  flex: 0 0 40px;
  width: 40px;
}

.invoice-edit-sheet__body {
  /* Reserve chrome so footer stays inside the sheet on Xiaomi/WebView */
  flex: 1 1 auto;
  min-height: 0;
  max-height: calc(78vh - 180px);
  max-height: calc(78dvh - 180px);
  max-height: calc(78svh - 180px);
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding: 16px;
  display: block;
}

.invoice-edit-sheet__fields {
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 0 0 auto;
  margin-bottom: 0;
}

.invoice-edit-field-wrap {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 0;
  overflow: visible;
}

.invoice-edit-field-wrap__label {
  display: block;
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 600;
  color: #455a64;
  text-align: right;
  line-height: 1.4;
  margin: 0 2px 8px;
}

.invoice-edit-field {
  margin: 0 !important;
  padding: 0 !important;
  width: 100%;
  flex: 0 0 40px;
  max-height: 40px;
  overflow: hidden;
}

.invoice-edit-field.v-input,
.invoice-edit-field ::v-deep .v-input {
  margin: 0 !important;
  padding: 0 !important;
  font-size: 14px;
}

.invoice-edit-field ::v-deep .v-input__control,
.invoice-edit-field.v-input ::v-deep .v-input__control {
  min-height: 40px !important;
  height: 40px !important;
}

.invoice-edit-field ::v-deep .v-input__slot,
.invoice-edit-field.v-input ::v-deep .v-input__slot {
  min-height: 40px !important;
  height: 40px !important;
  margin: 0 !important;
  padding: 0 12px !important;
  border: 1px solid #cfd8dc !important;
  border-radius: 8px !important;
  background: #fff !important;
  box-shadow: none !important;
}

.invoice-edit-field ::v-deep .v-input__slot::before,
.invoice-edit-field ::v-deep .v-input__slot::after,
.invoice-edit-field.v-input ::v-deep .v-input__slot::before,
.invoice-edit-field.v-input ::v-deep .v-input__slot::after {
  display: none !important;
}

.invoice-edit-field ::v-deep .v-input__append-inner,
.invoice-edit-field ::v-deep .v-input__prepend-inner,
.invoice-edit-field.v-input ::v-deep .v-input__append-inner,
.invoice-edit-field.v-input ::v-deep .v-input__prepend-inner {
  margin-top: 0 !important;
  align-self: center !important;
}

.invoice-edit-field ::v-deep .v-label,
.invoice-edit-field.v-input ::v-deep .v-label {
  display: none !important;
}

.invoice-edit-field ::v-deep input,
.invoice-edit-field ::v-deep .v-select__selection,
.invoice-edit-field.v-input ::v-deep input,
.invoice-edit-field.v-input ::v-deep .v-select__selection {
  margin: 0 !important;
  font-size: 14px;
  color: #263238;
}

.invoice-edit-field ::v-deep .v-icon,
.invoice-edit-field.v-input ::v-deep .v-icon {
  color: #78909c !important;
  font-size: 20px !important;
}

.invoice-edit-datepicker {
  display: none !important;
  width: 0 !important;
  height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  overflow: hidden !important;
}

.invoice-mobile-date {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.invoice-mobile-date__selected {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 14px;
  border: 1px solid #e0e6ed;
  border-radius: 12px;
  background: #f7f9fc;
}

.invoice-mobile-date__label {
  width: 100%;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}

.invoice-mobile-date__value {
  font-size: 18px;
  font-weight: 800;
  color: #263238;
  letter-spacing: 0.02em;
}

.invoice-mobile-date__clear {
  border: 0;
  background: transparent;
  color: #e91e63;
  font-size: 13px;
  font-weight: 700;
  padding: 4px 0;
  cursor: pointer;
}

.invoice-mobile-date__picker {
  width: 100%;
}

.invoice-mobile-date__picker ::v-deep .vpd-main,
.invoice-mobile-date__picker ::v-deep .vpd-wrapper,
.invoice-mobile-date__picker ::v-deep .vpd-container,
.invoice-mobile-date__picker ::v-deep .vpd-content {
  width: 100% !important;
  max-width: 100% !important;
  box-shadow: none !important;
  border: 1px solid #e0e6ed !important;
  border-radius: 12px !important;
  background: #fff !important;
  overflow: hidden;
}

.invoice-mobile-date__picker ::v-deep .vpd-header,
.invoice-mobile-date__picker ::v-deep .vpd-addon-list {
  background: #c5d0e0 !important;
  color: #37474f !important;
}

.invoice-mobile-date__picker ::v-deep .vpd-header *,
.invoice-mobile-date__picker ::v-deep .vpd-addon-list * {
  color: #37474f !important;
}

.invoice-mobile-date__picker ::v-deep .vpd-day.selected span,
.invoice-mobile-date__picker ::v-deep .vpd-day.selected,
.invoice-mobile-date__picker ::v-deep .selected {
  background: #c5d0e0 !important;
  color: #37474f !important;
  box-shadow: none !important;
}

.invoice-mobile-date__picker ::v-deep .vpd-input-group,
.invoice-mobile-date__picker ::v-deep input {
  display: none !important;
}

.invoice-edit-sheet__footer {
  flex: 0 0 auto;
  flex-shrink: 0;
  position: relative;
  z-index: 3;
  margin-top: auto;
  padding: 10px 16px calc(20px + env(safe-area-inset-bottom, 0px));
  background: #fff;
  border-top: 1px solid #eeeeee;
}

.invoice-edit-sheet__submit {
  flex: 0 0 auto;
  margin: 0 !important;
  border-radius: 8px !important;
  font-weight: 700;
  letter-spacing: 0;
  width: 100%;
  min-height: 48px;
}

.invoice-product-sheet__card {
  direction: rtl;
  border-radius: 16px 16px 0 0 !important;
  padding: 8px 0 0;
  height: 85vh;
  display: flex;
  flex-direction: column;
}

.invoice-product-sheet__header {
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px;
  border-bottom: 1px solid #eeeeee;
}

.invoice-product-sheet__title {
  flex: 1;
  border-bottom: 0;
  padding-bottom: 8px;
}

.invoice-product-sheet__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  padding: 8px;
}

.invoice-more-sheet__card {
  direction: rtl;
  border-radius: 16px 16px 0 0 !important;
  padding: 8px 0 12px;
  max-height: 85vh;
  overflow-y: auto;
}

.invoice-more-sheet__handle {
  position: relative;
  width: 40px;
  height: 4px;
  margin: 8px auto 12px;
  border-radius: 999px;
  background: #cfd8dc;
  /* Larger invisible hit area for swipe-to-dismiss */
  cursor: grab;
  -webkit-tap-highlight-color: transparent;
  touch-action: none;
}

.invoice-more-sheet__handle::before {
  content: "";
  position: absolute;
  top: -14px;
  bottom: -14px;
  left: -40px;
  right: -40px;
}

.invoice-more-sheet__title {
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  color: #212121;
  padding: 4px 16px 12px;
  border-bottom: 1px solid #eeeeee;
}

.invoice-more-sheet__item {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  gap: 12px;
  padding: 12px 16px;
  border: 0;
  border-bottom: 1px solid #eeeeee;
  background: #fff;
  text-align: right;
  cursor: pointer;
}

.invoice-more-sheet__item:active {
  background: #f7f9fc;
}

.invoice-more-sheet__item:disabled {
  opacity: 0.55;
  cursor: default;
}

.invoice-more-sheet__item--back {
  margin-top: 4px;
  border-bottom: 0;
}

.invoice-mobile-composite-host {
  position: absolute;
  left: 0;
  top: 0;
  width: 0;
  height: 0;
  overflow: visible;
  z-index: 0;
}

.invoice-mobile-composite-host ::v-deep > .v-btn,
.invoice-mobile-composite-host ::v-deep > .v-container > .v-btn,
.invoice-mobile-composite-host ::v-deep > .container > .v-btn {
  position: absolute !important;
  width: 0 !important;
  height: 0 !important;
  min-width: 0 !important;
  min-height: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
  opacity: 0 !important;
  pointer-events: none !important;
  overflow: hidden !important;
}

.invoice-extras-check {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  font-size: 14px;
  font-weight: 700;
  color: #37474f;
  cursor: pointer;
}

.invoice-extras-check input {
  width: 18px;
  height: 18px;
}

.invoice-extras-credit-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 14px;
  padding: 12px;
  border: 1px solid #d5dde8;
  border-radius: 10px;
  background: #f8f9fc;
}

.invoice-extras-credit-value {
  margin-top: 4px;
  font-size: 16px;
  font-weight: 800;
  color: #5a6b85;
}

.invoice-extras-form .invoice-edit-sheet__body {
  padding: 14px 16px;
}

.invoice-extras-form .invoice-edit-sheet__fields {
  gap: 14px;
}

.invoice-extras-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 44px;
  border: 1px solid #cfd8dc;
  border-radius: 8px;
  background: #e8edf5;
  color: #5a6b85;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

.invoice-tax-toggle {
  display: flex;
  width: 100%;
  border: 1px solid #cfd8dc;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.invoice-tax-toggle__btn {
  flex: 1 1 50%;
  min-height: 40px;
  border: 0;
  background: transparent;
  color: #607d8b;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.invoice-tax-toggle__btn.is-active {
  background: #5a6b85;
  color: #fff;
}

.invoice-extras-autocomplete,
.invoice-extras-status,
.invoice-extras-tag,
.invoice-extras-money {
  width: 100%;
}

/* Force status / tag / money / aicomplete to match picker fields */
.invoice-extras-form .invoice-extras-status ::v-deep .v-input,
.invoice-extras-form .invoice-extras-tag ::v-deep .v-input,
.invoice-extras-form .invoice-extras-money ::v-deep .v-input,
.invoice-extras-form .invoice-extras-autocomplete ::v-deep .v-input {
  margin: 0 !important;
  padding: 0 !important;
}

.invoice-extras-form .invoice-extras-status ::v-deep .v-input__control,
.invoice-extras-form .invoice-extras-tag ::v-deep .v-input__control,
.invoice-extras-form .invoice-extras-money ::v-deep .v-input__control,
.invoice-extras-form .invoice-extras-autocomplete ::v-deep .v-input__control {
  min-height: 40px !important;
}

.invoice-extras-form .invoice-extras-status ::v-deep .v-input__slot,
.invoice-extras-form .invoice-extras-tag ::v-deep .v-input__slot,
.invoice-extras-form .invoice-extras-money ::v-deep .v-input__slot,
.invoice-extras-form .invoice-extras-autocomplete ::v-deep .v-input__slot {
  min-height: 40px !important;
  height: 40px !important;
  margin: 0 !important;
  padding: 0 12px !important;
  border: 1px solid #cfd8dc !important;
  border-radius: 8px !important;
  background: #fff !important;
  box-shadow: none !important;
}

.invoice-extras-form .invoice-extras-status ::v-deep fieldset,
.invoice-extras-form .invoice-extras-tag ::v-deep fieldset,
.invoice-extras-form .invoice-extras-money ::v-deep fieldset,
.invoice-extras-form .invoice-extras-autocomplete ::v-deep fieldset {
  border: 0 !important;
}

.invoice-extras-form .invoice-extras-status ::v-deep .v-input__slot::before,
.invoice-extras-form .invoice-extras-status ::v-deep .v-input__slot::after,
.invoice-extras-form .invoice-extras-tag ::v-deep .v-input__slot::before,
.invoice-extras-form .invoice-extras-tag ::v-deep .v-input__slot::after,
.invoice-extras-form .invoice-extras-money ::v-deep .v-input__slot::before,
.invoice-extras-form .invoice-extras-money ::v-deep .v-input__slot::after,
.invoice-extras-form .invoice-extras-autocomplete ::v-deep .v-input__slot::before,
.invoice-extras-form .invoice-extras-autocomplete ::v-deep .v-input__slot::after {
  display: none !important;
}

.invoice-extras-form .invoice-extras-status ::v-deep .v-label,
.invoice-extras-form .invoice-extras-tag ::v-deep .v-label,
.invoice-extras-form .invoice-extras-money ::v-deep .v-label,
.invoice-extras-form .invoice-extras-autocomplete ::v-deep .v-label {
  display: none !important;
}

.invoice-extras-form .invoice-extras-status ::v-deep .v-text-field__details,
.invoice-extras-form .invoice-extras-tag ::v-deep .v-text-field__details,
.invoice-extras-form .invoice-extras-money ::v-deep .v-text-field__details,
.invoice-extras-form .invoice-extras-autocomplete ::v-deep .v-text-field__details,
.invoice-extras-form .invoice-extras-status ::v-deep .v-messages,
.invoice-extras-form .invoice-extras-tag ::v-deep .v-messages,
.invoice-extras-form .invoice-extras-money ::v-deep .v-messages,
.invoice-extras-form .invoice-extras-autocomplete ::v-deep .v-messages {
  display: none !important;
  min-height: 0 !important;
}

.invoice-extras-form .invoice-extras-status ::v-deep .v-icon,
.invoice-extras-form .invoice-extras-tag ::v-deep .v-icon,
.invoice-extras-form .invoice-extras-money ::v-deep .v-icon,
.invoice-extras-form .invoice-extras-autocomplete ::v-deep .v-icon {
  color: #78909c !important;
  font-size: 20px !important;
}

.invoice-extras-form .invoice-edit-field ::v-deep .v-input__slot,
.invoice-extras-form .invoice-edit-picker-field {
  border-radius: 8px !important;
}

.invoice-notification-sheet__body {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.invoice-notification-sheet__hint {
  flex: 0 0 auto;
  margin-bottom: 12px;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
  line-height: 1.5;
}

.invoice-notification-sheet__add {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  min-height: 44px;
  margin-bottom: 12px;
  border: 1px solid #c5d0e0;
  border-radius: 10px;
  background: #e8edf5;
  color: #5a6b85;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

.invoice-notification-sheet__list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 0;
  padding-bottom: 2px;
}

.invoice-notification-sheet__empty {
  margin-bottom: 0;
  padding: 16px;
  border: 1px dashed #cfd8dc;
  border-radius: 12px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: #90a4ae;
}

.invoice-notif-card {
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  gap: 10px;
  margin-bottom: 12px;
  padding: 12px;
  border: 1px solid #e0e4e8;
  border-radius: 12px;
  background: #fff;
  overflow: visible;
}

.invoice-notif-card:last-child {
  margin-bottom: 0;
}

.invoice-notif-card__type {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}

.invoice-notif-card__type-field {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
}

.invoice-notif-card__row {
  display: grid;
  grid-template-columns: minmax(72px, 0.85fr) minmax(0, 1.35fr);
  gap: 10px;
  align-items: start;
}

.invoice-notif-card__row .invoice-edit-field-wrap {
  margin: 0;
  min-width: 0;
}

.invoice-notif-card .invoice-edit-field-wrap__label {
  position: static;
  margin: 0 2px 6px;
}

.invoice-notif-card .invoice-edit-field {
  flex: none !important;
  width: 100%;
  height: 40px !important;
  max-height: 40px !important;
  margin: 0 !important;
  overflow: hidden !important;
}

.invoice-notif-card .invoice-edit-field.v-input {
  margin: 0 !important;
  min-height: 40px !important;
  height: 40px !important;
  max-height: 40px !important;
}

.invoice-notif-card .invoice-edit-field ::v-deep .v-input__control,
.invoice-notif-card .invoice-edit-field ::v-deep .v-input__slot {
  margin: 0 !important;
  min-height: 40px !important;
  height: 40px !important;
  max-height: 40px !important;
}

.invoice-notif-card .invoice-edit-field ::v-deep .v-select__selections {
  flex-wrap: nowrap;
  overflow: hidden;
  min-height: 0 !important;
  padding: 0 !important;
}

.invoice-notif-card .invoice-edit-field ::v-deep .v-select__selection {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin: 0 !important;
}

.invoice-notif-card__remove {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  margin-bottom: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #f8bbd0;
  border-radius: 10px;
  background: #fce4ec;
  cursor: pointer;
}

.invoice-notif-card__input {
  width: 100%;
  height: 40px;
  padding: 0 10px;
  border: 1px solid #cfd8dc;
  border-radius: 8px;
  outline: none;
  background: #fff;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #37474f;
  box-sizing: border-box;
}

.invoice-notif-card__input--numpad {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.invoice-notif-card__input--numpad:active {
  background: #e3f2fd;
  border-color: #90caf9;
}

.invoice-notif-card__input--text {
  text-align: right;
  font-weight: 600;
}

.invoice-custom-fields-sheet .invoice-notif-card .invoice-edit-field-wrap {
  margin: 0;
  min-width: 0;
}

.invoice-more-sheet__icon {
  flex: 0 0 auto;
  margin-left: 0;
  margin-right: 0;
}

.invoice-more-sheet__texts {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 1 auto;
  min-width: 0;
  text-align: right;
}

.invoice-more-sheet__item-title {
  display: block;
  width: 100%;
  font-size: 14px;
  font-weight: 700;
  color: #212121;
  line-height: 1.35;
  text-align: right;
}

.invoice-more-sheet__item-sub {
  display: block;
  width: 100%;
  margin-top: 2px;
  font-size: 12px;
  font-weight: 400;
  color: #90a4ae;
  line-height: 1.35;
  text-align: right;
}

.invoice-meta-sheet__body {
  padding-top: 4px;
  padding-bottom: 4px;
}

.invoice-meta-sheet__nav-list {
  flex: 0 0 auto;
  margin: 0 -16px;
}

.invoice-meta-sheet__nav-list .invoice-more-sheet__item:last-child {
  border-bottom: 0;
}

.invoice-commission-percent {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #cfd8dc;
  border-radius: 8px;
  background: #fff;
  box-sizing: border-box;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.invoice-commission-percent:active:not(:disabled) {
  background: #e3f2fd;
  border-color: #90caf9;
}

.invoice-commission-percent:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.invoice-commission-percent__input {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  border: 0;
  outline: none;
  background: transparent;
  text-align: right;
  font-size: 14px;
  font-weight: 600;
  color: #263238;
  direction: ltr;
  unicode-bidi: plaintext;
}

.invoice-commission-percent__suffix {
  flex: 0 0 auto;
  color: #78909c;
  font-size: 13px;
  font-weight: 700;
}

/* Higher specificity: base .invoice-edit-sheet__body uses display:block */
.invoice-edit-sheet__body.invoice-documents-sheet__body {
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
}

.invoice-documents-sheet__hint {
  flex: 0 0 auto;
  margin-bottom: 12px;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
  line-height: 1.5;
}

/* class lands on file-picker root v-card */
.invoice-documents-sheet__picker {
  flex: 1 1 auto !important;
  min-height: 0 !important;
  width: 100% !important;
  margin: 0 0 12px !important;
  overflow: hidden !important;
  background: transparent !important;
  display: flex !important;
  flex-direction: column !important;
  box-shadow: none !important;
}

.invoice-documents-sheet__picker ::v-deep > .v-form {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.invoice-documents-sheet__picker ::v-deep > .v-form > .v-btn {
  flex: 0 0 auto;
  min-height: 48px !important;
  height: 48px !important;
  margin: 0 0 12px !important;
  border-radius: 12px !important;
  border: 1px dashed #90a4ae !important;
  background: #f5f7f9 !important;
  color: #546e7a !important;
  font-weight: 700 !important;
  letter-spacing: 0 !important;
}

.invoice-documents-sheet__picker ::v-deep .v-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  background: transparent !important;
  padding: 0 !important;
}

.invoice-documents-sheet__picker ::v-deep .v-list-item {
  min-height: 64px !important;
  margin: 0 0 10px !important;
  padding: 10px 12px !important;
  border: 1px solid #e0e4e8 !important;
  border-radius: 12px !important;
  background: #fff !important;
}

.invoice-documents-sheet__picker ::v-deep .v-list-item__avatar {
  margin: 0 0 0 12px !important;
  border-radius: 10px !important;
  overflow: hidden;
}

.invoice-documents-sheet__picker ::v-deep .v-list-item__title {
  font-size: 14px !important;
  font-weight: 700 !important;
  color: #263238 !important;
  text-align: right;
}

.invoice-documents-sheet__picker ::v-deep .v-list-item__subtitle {
  font-size: 12px !important;
  color: #90a4ae !important;
  text-align: right;
}

.invoice-documents-sheet__picker ::v-deep .v-list-item__icon .v-icon {
  color: #e91e63 !important;
}

.invoice-edit-picker-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  min-height: 40px;
  margin: 0;
  padding: 0 12px;
  border: 1px solid #cfd8dc;
  border-radius: 8px;
  background: #fff;
  box-sizing: border-box;
  cursor: pointer;
  text-align: right;
}

.invoice-edit-picker-field:active {
  background: #e3f2fd;
  border-color: #90caf9;
}

.invoice-edit-picker-field__text {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 14px;
  color: #263238;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.invoice-entity-picker {
  --picker-nude: #eceff2;
  --picker-nude-border: #c5ced6;
  --picker-nude-strong: #607d8b;
  --picker-nude-soft: #f3f5f7;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 100vh;
  background: var(--picker-nude-soft) !important;
  border-radius: 0 !important;
}

.invoice-entity-picker__header {
  flex: 0 0 auto;
  display: grid;
  grid-template-columns: 44px 1fr auto;
  align-items: center;
  gap: 4px;
  min-height: 56px;
  padding: 8px 10px;
  background: #fff;
  border-bottom: 1px solid #eceff1;
}

.invoice-entity-picker__title {
  margin: 0;
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  color: #263238;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.invoice-entity-picker__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 44px;
}

.invoice-entity-picker__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
}

.invoice-entity-picker__icon-btn:active {
  background: var(--picker-nude);
}

.invoice-entity-picker__search {
  flex: 0 0 auto;
  padding: 12px;
  background: #fff;
}

.invoice-entity-picker__search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  min-height: 48px;
  padding: 0 12px;
  border-radius: 10px;
  background: var(--picker-nude);
  border: 1px solid #e0e4e8;
  box-sizing: border-box;
}

.invoice-entity-picker__search-icon {
  flex: 0 0 auto;
}

.invoice-entity-picker__search-input {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  border: 0;
  outline: none;
  background: transparent;
  font-size: 15px;
  font-weight: 500;
  color: #263238;
  text-align: right;
  line-height: 1.4;
}

.invoice-entity-picker__search-input::placeholder {
  color: #90a4ae;
  font-weight: 400;
}

.invoice-entity-picker__search-input::-webkit-search-cancel-button {
  display: none;
}

.invoice-entity-picker__search-clear {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
}

.invoice-entity-picker__divider {
  flex: 0 0 auto;
  position: relative;
  height: 1px;
  background: #eceff1;
}

.invoice-entity-picker__loader {
  position: absolute !important;
  left: 0 !important;
  right: 0 !important;
  top: 0 !important;
  margin: 0 !important;
}

.invoice-entity-picker__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 10px 12px 20px;
}

.invoice-entity-picker__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 52px;
  margin: 0 0 8px;
  padding: 10px 12px;
  border: 1px solid #e8eef3;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  text-align: right;
}

.invoice-entity-picker__item--active {
  border-color: var(--picker-nude-border);
  background: var(--picker-nude);
}

.invoice-entity-picker__index {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  border-radius: 6px;
  background: #e8ecef;
  color: #607d8b;
  font-size: 12px;
  font-weight: 700;
}

.invoice-entity-picker__item--active .invoice-entity-picker__index {
  background: #d5dce2;
  color: var(--picker-nude-strong);
}

.invoice-entity-picker__item-main {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.invoice-entity-picker__item-title {
  font-size: 14px;
  font-weight: 600;
  color: #263238;
}

.invoice-entity-picker__item-sub {
  font-size: 12px;
  color: #78909c;
}

.invoice-entity-picker__more {
  display: flex;
  justify-content: center;
  padding: 12px 0;
  min-height: 40px;
}

.invoice-entity-picker__empty {
  text-align: center;
  font-size: 13px;
  color: #90a4ae;
  padding: 28px 0;
}
</style>
